import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { WO_TYPE_COLORS, WO_TYPE_LABELS } from '../utils/constants';
import { FiShield, FiPlus, FiUsers, FiBox, FiRepeat, FiToggleLeft, FiToggleRight, FiAlertTriangle, FiCheck, FiFileText, FiSettings, FiMail, FiKey, FiEdit2, FiTrash2, FiSearch, FiX, FiMapPin, FiTruck, FiUserCheck, FiChevronDown, FiChevronRight, FiImage, FiUpload, FiDownload, FiArrowLeft, FiArrowRight, FiArrowUp, FiArrowDown, FiEye, FiDollarSign, FiClock, FiCalendar, FiLink2, FiSliders, FiMenu, FiRefreshCw, FiTool, FiLock, FiUnlock } from 'react-icons/fi';
import { useSite } from '../context/SiteContext';
import titleCase from '../utils/titleCase';
import capitalizeFirst from '../utils/capitalizeFirst';
import { playClick, playSuccess } from '../utils/sounds';
import ManageUsers from './ManageUsers';
import AssetCreateWizard from '../components/AssetCreateWizard';
import Reports from './Reports';
import Licences from './Licences';
import TeamOrgChart from '../components/TeamOrgChart';

const BASE_TABS = [
  { key: 'overview', label: 'Overview', icon: FiShield },
  { key: 'users', label: 'Users', icon: FiUsers },
  { key: 'teams', label: 'Teams', icon: FiUserCheck },
  { key: 'kpt', label: 'KPT', icon: FiKey },
  { key: 'tasklists', label: 'PM02 Work Orders', icon: FiCheck },
  { key: 'assets', label: 'Assets', icon: FiBox },
  { key: 'licences', label: 'Licences', icon: FiKey },
  { key: 'costcodes', label: 'Cost Centres', icon: FiDollarSign },
  { key: 'flocations', label: 'Functional Locations', icon: FiMapPin },
  { key: 'reports', label: 'Reports', icon: FiFileText },
  { key: 'settings', label: 'Settings', icon: FiSettings },
];

// Convert a plan slug ('home_edition') to a display label ('Home Edition').
function formatPlanName(slug) {
  if (!slug) return '';
  return String(slug).split(/[_-]+/).filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
}

const VALID_FREQUENCIES = ['1 Daily', '1 Weekly', '2 Weekly', '1 Month', '3 Months', '6 Months', '1 Year', '2 Years', '3 Years', '5 Years', '10 Years'];
const TASK_ITEM_TYPES = [
  { value: 'checkbox', label: 'Checkbox' },
];

export default function AdminPanel() {
  const { authFetch, user } = useAuth();
  const siteCtx = useSite();
  const navigate = useNavigate();
  const TABS = BASE_TABS;
  const [tab, setTab] = useState('overview');

  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState(null);
  const [firebaseStatus, setFirebaseStatus] = useState(null); // { connected, project_id? }
  const [loading, setLoading] = useState(true);
  // SMTP is .env-managed; no editable form state. testEmailTo + emailMsg
  // are kept for the test-email control that still lives in Settings.
  const [emailMsg, setEmailMsg] = useState('');
  const [testEmailTo, setTestEmailTo] = useState('');
  const [scheduleSettings, setScheduleSettings] = useState({ daily_hour: 5, weekly_day: 1, weekly_hour: 8, adherence_day: 1, adherence_hour: 8 });
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleMsg, setScheduleMsg] = useState('');

  // Maintenance Mode
  const [maintenanceStatus, setMaintenanceStatus] = useState({ enabled: false, message: '', estimated_hours: '' });
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);

  // Capacity Check
  const [capacityData, setCapacityData] = useState(null);
  const [capacityLoading, setCapacityLoading] = useState(false);
  const [capacityWeeks, setCapacityWeeks] = useState(4);
  const [rescheduleOp, setRescheduleOp] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduling, setRescheduling] = useState(false);

  // Settings sections collapse state
  const [settingsOpen, setSettingsOpen] = useState({});
  const toggleSection = key => setSettingsOpen(s => ({ ...s, [key]: !s[key] }));

  // Toast
  const [toast, setToast] = useState(null);
  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  // Asset Management
  const [adminAssets, setAdminAssets] = useState([]);
  const [assetStats, setAssetStats] = useState(null);
  const [assetSearch, setAssetSearch] = useState('');
  const [showRedundantAssets, setShowRedundantAssets] = useState(false);
  const [assetsSubTab, setAssetsSubTab] = useState('list'); // 'list' | 'fields'
  const [assetStatusFilter, setAssetStatusFilter] = useState('');
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [showAssetWizard, setShowAssetWizard] = useState(false);
  const [editAsset, setEditAsset] = useState(null);
  const [assetForm, setAssetForm] = useState({ name: '', tag: '', status_id: '3', serial_number: '', notes: '', supplier_name: '', purchase_date: '', warranty_end: '', capacity: '', cost_code_id: '', functional_location_id: '' });
  const [statuses, setStatuses] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [showCfModal, setShowCfModal] = useState(false);
  const [editCf, setEditCf] = useState(null);
  const [cfForm, setCfForm] = useState({ name: '', field_type: 'text' });
  const [draggingCfId, setDraggingCfId] = useState(null);

  // Teams Management (uses app_groups table)
  const [teams, setTeams] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [editTeam, setEditTeam] = useState(null);
  const [teamForm, setTeamForm] = useState({ name: '', description: '', member_ids: [], supervised_by: '' });
  const [memberSearch, setMemberSearch] = useState('');
  const [expandedTeams, setExpandedTeams] = useState(new Set());

  // Cost Codes
  const [costCodes, setCostCodes] = useState([]);
  const [costCodeLoading, setCostCodeLoading] = useState(false);
  const [showCostCodeModal, setShowCostCodeModal] = useState(false);
  const [editCostCode, setEditCostCode] = useState(null);
  const [costCodeForm, setCostCodeForm] = useState({ description: '' });
  const [nextCostCode, setNextCostCode] = useState('');

  // Functional Locations (SAP-style hierarchy: 5061 → 5061.01 → 5061.01.01)
  const [flNodes, setFlNodes] = useState([]);
  const [flLoading, setFlLoading] = useState(false);
  const [flExpanded, setFlExpanded] = useState(() => new Set());
  const [flEditing, setFlEditing] = useState(null);        // { id, code, name } or null
  const [flAddingUnder, setFlAddingUnder] = useState(null); // parent_id (null = root) or undefined
  const [flAddForm, setFlAddForm] = useState({ code: '', name: '' });
  const [flSearch, setFlSearch] = useState('');
  // Read-only by default — every edit / drag / add / delete is gated on
  // this. Unlock toggles edit mode on; locking only asks for confirmation
  // when there were unsaved-style changes in the session (flDirty).
  // flDirty turns the header toggle into a green Save button so the user
  // has a clear 'I'm done' moment that re-locks the tree.
  const [flLocked, setFlLocked] = useState(true);
  const [flDirty, setFlDirty] = useState(false);
  // Snapshot of FL tree + attached assets taken at the moment of Unlock.
  // Used by the Cancel button to revert the session's changes via diff.
  // Stored shape: { fls: [{id, code, name, parent_id, parent_asset_id, sort_order}],
  //                 assets: [{id, tag, functional_location_id, parent_asset_id}] }.
  const [flSnapshot, setFlSnapshot] = useState(null);
  const [flRollup, setFlRollup] = useState(null);          // { node, labour_cost, ... }
  const [flRollupLoading, setFlRollupLoading] = useState(false);
  const [flAttachedAssets, setFlAttachedAssets] = useState([]); // [{id, tag, name, functional_location_id, ...}]
  // Inline "create asset under this FL" form. parent_id of the FL we're
  // adding under (or undefined when not adding).
  const [flAssetAddingUnder, setFlAssetAddingUnder] = useState(undefined);
  const [flAssetForm, setFlAssetForm] = useState({ tag: '', name: '' });
  // Unattached-asset bulk-assign modal
  const [showAssetMgr, setShowAssetMgr] = useState(false);
  const [unattachedAssets, setUnattachedAssets] = useState([]);
  const [unattachedLoading, setUnattachedLoading] = useState(false);
  const [unattachedSearch, setUnattachedSearch] = useState('');
  const [unattachedSelected, setUnattachedSelected] = useState(() => new Set());
  const [unattachedTargetFl, setUnattachedTargetFl] = useState('');
  const [unattachedSaving, setUnattachedSaving] = useState(false);
  // Quick-move popover on attached asset rows inside the FL tree.
  const [flAssetMovePopoverFor, setFlAssetMovePopoverFor] = useState(null);
  const [flAssetMoveSearch, setFlAssetMoveSearch] = useState('');
  const [flAssetMoveSaving, setFlAssetMoveSaving] = useState(false);
  // Move-FL-node popover — change a node's parent (whole subtree + assets
  // follow automatically via the parent_id chain on the server).
  const [flMovePopoverFor, setFlMovePopoverFor] = useState(null);
  const [flMoveSearch, setFlMoveSearch] = useState('');
  const [flMoveSaving, setFlMoveSaving] = useState(false);
  // Per-asset expand state — Set of asset ids that are currently expanded
  // (showing their sub-asset rows). Collapsed by default.
  const [flAssetExpanded, setFlAssetExpanded] = useState(() => new Set());

  // Task List Templates
  const [taskTemplates, setTaskTemplates] = useState([]);
  const [tplSearch, setTplSearch] = useState('');
  const [tplFilterNoAssets, setTplFilterNoAssets] = useState(false);
  const [showTaskTplModal, setShowTaskTplModal] = useState(false);
  const [editTaskTpl, setEditTaskTpl] = useState(null);
  const tplOpIdRef = useRef(1);
  const tplItemIdRef = useRef(1);
  const [taskTplForm, setTaskTplForm] = useState({ name: '', description: '', schedule_frequency: '', operations: [{ _id: 0, title: '', priority: 'Normal', sections: [{ name: 'General', items: [] }] }] });
  const [activeSectionIdx, setActiveSectionIdx] = useState(0);
  const [newSectionName, setNewSectionName] = useState(null);
  const [editingSectionIdx, setEditingSectionIdx] = useState(null);
  const [editingSectionName, setEditingSectionName] = useState('');
  const [taskAssetSearch, setTaskAssetSearch] = useState('');
  const [taskAssetResults, setTaskAssetResults] = useState([]);
  const [selectedAssetLabel, setSelectedAssetLabel] = useState('');
  const [taskTplStep, setTaskTplStep] = useState(1); // 1 = details, 2 = operations, 3 = task items
  const [newTaskItem, setNewTaskItem] = useState({ label: '', description: '', item_type: 'checkbox', required: true, require_photo: false });
  const [dragItem, setDragItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [dragSection, setDragSection] = useState(null);
  const [dragOverSection, setDragOverSection] = useState(null);
  const [newDropdownOption, setNewDropdownOption] = useState('');
  const [taskTplDetail, setTaskTplDetail] = useState(null);
  const [showTaskTplDetail, setShowTaskTplDetail] = useState(false);
  const [deletePreview, setDeletePreview] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditConfirm, setShowEditConfirm] = useState(false);
  const [pendingEditSave, setPendingEditSave] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, rows, onConfirm, confirmLabel, confirmColor, typeToConfirm }
  const [confirmTyped, setConfirmTyped] = useState('');
  const [expandedTplId, setExpandedTplId] = useState(null);
  const [tplAssets, setTplAssets] = useState([]);
  const [tplAssetsLoading, setTplAssetsLoading] = useState(false);
  const [showAddAssetModal, setShowAddAssetModal] = useState(false);
  const [addAssetTplId, setAddAssetTplId] = useState(null);
  const [addAssetForm, setAddAssetForm] = useState({ asset_id: '', schedule_frequency: '', planned_hours: 0, planned_minutes: 0, planned_resources: 1, start_date: '' });
  const [addAssetSearch, setAddAssetSearch] = useState('');
  const [addAssetResults, setAddAssetResults] = useState([]);
  const [addAssetLabel, setAddAssetLabel] = useState('');
  const [addAssetOps, setAddAssetOps] = useState([]);
  const [addAssetSuccess, setAddAssetSuccess] = useState(null);
  const [editAssetModal, setEditAssetModal] = useState(null); // { tplId, assetId, assetName, ops, op_hours }
  const [editAssetSaving, setEditAssetSaving] = useState(false);
  const [linkWoModal, setLinkWoModal] = useState(null); // { tplId, assetId, assetName }
  const [linkableWos, setLinkableWos] = useState([]);
  const [linkableWosLoading, setLinkableWosLoading] = useState(false);
  const [linkWoSearch, setLinkWoSearch] = useState('');
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [editAssetHistory, setEditAssetHistory] = useState(null); // array or null
  const [viewAssetHistory, setViewAssetHistory] = useState(null); // { assetName } or null

  // KPT (Key Permission Types) Management
  const [kpts, setKpts] = useState([]);
  const [showKptModal, setShowKptModal] = useState(false);
  const [editKpt, setEditKpt] = useState(null);
  const [kptForm, setKptForm] = useState({ name: '', permissions: [], color: '#2563eb' });
  const [kptNameExists, setKptNameExists] = useState(false);
  const [selectedKpt, setSelectedKpt] = useState(null);
  const [kptUsers, setKptUsers] = useState([]);
  const [kptUserSearch, setKptUserSearch] = useState('');
  const [kptUsersLoading, setKptUsersLoading] = useState(false);
  const [viewKpt, setViewKpt] = useState(null);

  // Adherence Target
  const [adherenceTarget, setAdherenceTarget] = useState(80);
  const [savingTarget, setSavingTarget] = useState(false);

  // Pay Settings
  const [paySettings, setPaySettings] = useState({ week_end_day: 'friday', pay_day: 'thursday', pay_cycle: 'weekly' });
  const [paySettingsLoaded, setPaySettingsLoaded] = useState(false);
  const [savingPaySettings, setSavingPaySettings] = useState(false);

  // Site Branding
  const [brandingForm, setBrandingForm] = useState({ site_name: '', default_theme: 'system', subscription_plan_name: '', otp_feature_enabled: false });
  // Plan name shown in the header badge — loaded on mount so it renders
  // before the Settings tab is opened (brandingForm is settings-tab gated).
  const [headerPlanName, setHeaderPlanName] = useState('');
  const [brandingLogo, setBrandingLogo] = useState(null);
  const [brandingFavicon, setBrandingFavicon] = useState(null);
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [brandingMsg, setBrandingMsg] = useState('');
  const [brandingLoaded, setBrandingLoaded] = useState(false);

  // (Licences tab uses embedded Licences component)

  useEffect(() => {
    authFetch('/api/admin/site-settings').then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setHeaderPlanName(d.subscription_plan_name || ''); })
      .catch(() => {});
  }, [authFetch]);

  useEffect(() => {
    Promise.all([
      authFetch('/api/issues/templates').then(r => r.json()),
      authFetch('/api/stats').then(r => r.json()),
      authFetch('/api/admin/settings/email-schedule').then(r => r.ok ? r.json() : null).catch(() => null),
      authFetch('/api/admin/maintenance').then(r => r.ok ? r.json() : null).catch(() => null),
      authFetch('/api/admin/firebase-status').then(r => r.ok ? r.json() : null).catch(() => null),
    ]).then(([tpl, st, schedule, maint, firebase]) => {
      setTemplates(tpl);
      setStats(st);
      if (schedule && schedule.daily_hour !== undefined) setScheduleSettings(schedule);
      if (maint) setMaintenanceStatus({ enabled: maint.maintenance, message: maint.message || '', estimated_hours: '' });
      setFirebaseStatus(firebase);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const safeFetch = (url, setter, fallback = [], options) => {
    authFetch(url, options)
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(d => setter(Array.isArray(fallback) ? (Array.isArray(d) ? d : fallback) : d))
      .catch(err => { console.error(`Admin fetch ${url}:`, err); setter(fallback); });
  };

  const loadAssetTab = () => {
    const url = `/api/admin/assets${showRedundantAssets ? '?include_redundant=1' : ''}`;
    safeFetch(url, setAdminAssets);
    safeFetch('/api/admin/assets/stats', setAssetStats, { total: 0, byStatus: [] });
    safeFetch('/api/assets/lookups', d => setStatuses(d.statuses || []), {});
    safeFetch('/api/admin/asset-custom-fields', setCustomFields);
  };

  // Re-fetch admin asset list when the Show redundant toggle changes.
  // tab/safeFetch intentionally omitted from deps — safeFetch is recreated
  // every render (would infinite-loop) and we only care about the toggle.
  useEffect(() => {
    if (tab === 'assets') {
      const url = `/api/admin/assets${showRedundantAssets ? '?include_redundant=1' : ''}`;
      authFetch(url).then(r => r.ok ? r.json() : []).then(d => setAdminAssets(Array.isArray(d) ? d : [])).catch(() => {});
    }
  }, [showRedundantAssets, tab, authFetch]);

  const loadLicenceTab = () => {}; // Licences tab uses embedded component

  const loadTeamsTab = () => {
    safeFetch('/api/admin/groups', setTeams);
    safeFetch('/api/users', setAllUsers);
  };

  const loadKptTab = () => {
    safeFetch('/api/admin/kpts', setKpts);
    if (allUsers.length === 0) safeFetch('/api/users', setAllUsers);
  };

  const loadKptUsers = (kptId) => {
    setKptUsersLoading(true);
    authFetch(`/api/admin/kpts/${kptId}/users`).then(r => r.ok ? r.json() : []).then(d => { setKptUsers(Array.isArray(d) ? d : []); setKptUsersLoading(false); }).catch(() => { setKptUsers([]); setKptUsersLoading(false); });
  };

  const toTitleCase = (str) => str.replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());

  const KPT_PERMISSION_SECTIONS = [
    { title: 'Work Orders', perms: [
      { code: 'approve_wo', label: 'Approve Work Orders' },
      { code: 'reschedule_wo', label: 'Reschedule Work Orders' },
      { code: 'teco_wo', label: 'Teco Work Orders' },
      { code: 'delete_work_order', label: 'Delete Work Order' },
    ]},
    { title: 'Notifications', perms: [
      { code: 'create_notif', label: 'Create Notification' },
      { code: 'approve_notif', label: 'Approve Notifications' },
      { code: 'time_confirm', label: 'Time Confirm' },
    ]},
    { title: 'Licences', perms: [
      { code: 'view_licences', label: 'View Licences' },
      { code: 'create_licences', label: 'Create Licences' },
      { code: 'manage_licences', label: 'Manage Licences' },
    ]},
    { title: 'Projects', perms: [
      { code: 'create_projects', label: 'Create Projects' },
      { code: 'manage_projects', label: 'Manage All Projects' },
    ]},
    { title: 'Administration', perms: [
      { code: 'manage_team', label: 'Manage Team' },
      { code: 'supervisor_panel', label: 'Supervisor Panel' },
      { code: 'admin_panel', label: 'Admin Panel' },
    ]},
  ];
  const KPT_PERMISSIONS = KPT_PERMISSION_SECTIONS.flatMap(s => s.perms);

  const saveKpt = async () => {
    if (!kptForm.name.trim()) { showToast('Name is required', 'error'); return; }
    if (kptNameExists) { showToast('A KPT with that name already exists', 'error'); return; }
    const method = editKpt ? 'PUT' : 'POST';
    const url = editKpt ? `/api/admin/kpts/${editKpt.id}` : '/api/admin/kpts';
    const res = await authFetch(url, { method, body: JSON.stringify(kptForm) });
    if (res.ok) { setShowKptModal(false); setEditKpt(null); loadKptTab(); showToast(editKpt ? 'KPT updated' : 'KPT created'); }
    else { const d = await res.json().catch(() => ({})); showToast(d.error || 'Error', 'error'); }
  };

  const deleteKpt = (kpt) => {
    setConfirmDialog({
      title: 'Delete KPT',
      rows: [{ label: 'KPT', value: kpt.name }],
      confirmLabel: 'Delete', confirmColor: '#dc2626',
      onConfirm: async () => {
        const res = await authFetch(`/api/admin/kpts/${kpt.id}`, { method: 'DELETE' });
        if (res.ok) { loadKptTab(); if (selectedKpt?.id === kpt.id) { setSelectedKpt(null); setKptUsers([]); } showToast('KPT deleted'); }
        setConfirmDialog(null);
      }
    });
  };

  const assignUserToKpt = async (userId) => {
    if (!selectedKpt) return;
    const res = await authFetch(`/api/admin/kpts/${selectedKpt.id}/users`, { method: 'POST', body: JSON.stringify({ user_id: userId }) });
    if (res.ok) { loadKptUsers(selectedKpt.id); loadKptTab(); setKptUserSearch(''); }
  };

  const removeUserFromKpt = async (userId) => {
    if (!selectedKpt) return;
    const res = await authFetch(`/api/admin/kpts/${selectedKpt.id}/users/${userId}`, { method: 'DELETE' });
    if (res.ok) { loadKptUsers(selectedKpt.id); loadKptTab(); }
  };

  const loadTaskListsTab = (silent) => {
    safeFetch('/api/issues/task-list-templates/all', setTaskTemplates, [], silent ? { silent: true } : undefined);
  };

  const searchTaskAssets = (query) => {
    setTaskAssetSearch(query);
    if (!query.trim()) { setTaskAssetResults([]); return; }
    authFetch(`/api/assets?search=${encodeURIComponent(query.trim())}&limit=10`)
      .then(r => r.ok ? r.json() : { data: [] })
      .then(res => { setTaskAssetResults(res.data || []); })
      .catch(() => { setTaskAssetResults([]); });
  };

  const saveTaskTemplate = async () => {
    if (!taskTplForm.name.trim()) { alert('Template name is required'); return; }
    // Flatten per-operation sections/items into a single items array
    const validOps = (taskTplForm.operations || []).filter(o => o.title.trim());
    const flatItems = [];
    validOps.forEach((op, idx) => {
      const opNum = String((idx + 1) * 10).padStart(4, '0');
      (op.sections || []).forEach(sec => {
        (sec.items || []).forEach(item => {
          flatItems.push({ ...item, section: sec.name || 'General', operation_number: opNum });
        });
      });
    });
    if (flatItems.length < 1) { alert('At least 1 task item is required'); return; }
    for (const item of flatItems) {
      if (!item.label || !item.label.trim()) { alert('All task items must have a label'); return; }
    }
    const opsForPayload = validOps.map(o => ({ title: o.title, priority: o.priority || 'Normal' }));
    const payload = { ...taskTplForm, items: flatItems, operations: opsForPayload, name: taskTplForm.name.trim() };

    if (editTaskTpl) {
      // Show edit confirmation for existing templates
      setPendingEditSave(true);
      setShowEditConfirm(true);
      return;
    }

    // Confirmation before creating
    setConfirmDialog({
      title: 'Create PM02 Template',
      rows: [
        { label: 'Template Name', value: taskTplForm.name },
        taskTplForm.description ? { label: 'Description', value: taskTplForm.description } : null,
        { label: 'Operations', value: `${validOps.length}` },
        { label: 'Total Items', value: `${flatItems.length}` },
      ].filter(Boolean),
      confirmLabel: 'Create Template',
      confirmColor: '#2563eb',
      onConfirm: async () => {
        const res = await authFetch('/api/issues/task-list-templates/create', { method: 'POST', body: JSON.stringify(payload) });
        if (res.ok) { setShowTaskTplModal(false); setEditTaskTpl(null); setConfirmDialog(null); loadTaskListsTab(); showToast('PM02 template created'); }
        else { const d = await res.json(); setConfirmDialog(null); alert(d.error || 'Error'); }
      }
    });
  };

  const confirmSaveTaskTemplate = async () => {
    const validOps = (taskTplForm.operations || []).filter(o => o.title.trim());
    const flatItems = [];
    validOps.forEach((op, idx) => {
      const opNum = String((idx + 1) * 10).padStart(4, '0');
      (op.sections || []).forEach(sec => {
        (sec.items || []).forEach(item => {
          flatItems.push({ ...item, section: sec.name || 'General', operation_number: opNum });
        });
      });
    });
    const opsForPayload = validOps.map(o => ({ title: o.title, priority: o.priority || 'Normal' }));
    const payload = { ...taskTplForm, items: flatItems, operations: opsForPayload, name: taskTplForm.name.trim() };
    const res = await authFetch(`/api/issues/task-list-templates/${editTaskTpl.id}`, { method: 'PUT', body: JSON.stringify(payload) });
    if (res.ok) {
      const newVersion = (editTaskTpl?.version || 1) + 1;
      setShowTaskTplModal(false); setShowEditConfirm(false); setPendingEditSave(false); loadTaskListsTab();
      setConfirmDialog({
        title: 'Template Updated',
        rows: [
          { label: 'Template', value: taskTplForm.name },
          { label: 'Version', value: `${editTaskTpl?.tskl_number || ''}/${String(newVersion + 9).padStart(4, '0')}` },
          { label: 'Changes', value: 'Future and unconfirmed work orders will use the updated template' },
        ],
        confirmLabel: 'Done',
        confirmColor: '#10b981',
        hideCancel: true,
        onConfirm: () => { setConfirmDialog(null); setEditTaskTpl(null); }
      });
    }
    else { const d = await res.json(); alert(d.error || 'Error'); setShowEditConfirm(false); setPendingEditSave(false); }
  };

  const initiateDeleteTaskTemplate = async (tpl) => {
    const res = await authFetch(`/api/issues/task-list-templates/${tpl.id}/delete-preview`);
    if (res.ok) {
      const data = await res.json();
      setDeletePreview({ ...data, tpl });
      setShowDeleteConfirm(true);
    }
  };

  const confirmDeleteTaskTemplate = async () => {
    if (!deletePreview) return;
    const res = await authFetch(`/api/issues/task-list-templates/${deletePreview.tpl.id}`, { method: 'DELETE' });
    if (res.ok) { loadTaskListsTab(); showToast('PM02 template deleted'); setShowDeleteConfirm(false); setDeletePreview(null); }
  };

  const loadTaskListDetail = async (tpl) => {
    const res = await authFetch(`/api/issues/task-list-templates/${tpl.id}`);
    if (res.ok) {
      const data = await res.json();
      setTaskTplDetail(data);
      setShowTaskTplDetail(true);
    }
  };

  const loadTplAssets = async (tplId) => {
    setTplAssetsLoading(true);
    try {
      const res = await authFetch(`/api/issues/task-list-templates/${tplId}/assets`);
      if (res.ok) setTplAssets(await res.json());
    } catch (e) {}
    setTplAssetsLoading(false);
  };

  const toggleExpandTpl = (tplId) => {
    setSelectedAssetId(null);
    if (expandedTplId === tplId) { setExpandedTplId(null); setTplAssets([]); }
    else { setExpandedTplId(tplId); loadTplAssets(tplId); }
  };

  const searchAddAssets = (query) => {
    setAddAssetSearch(query);
    if (!query.trim()) { setAddAssetResults([]); return; }
    const assignedIds = new Set(tplAssets.map(a => a.asset_id));
    authFetch(`/api/assets?search=${encodeURIComponent(query.trim())}&limit=20${addAssetTplId ? `&exclude_template=${addAssetTplId}` : ''}`)
      .then(r => r.ok ? r.json() : { data: [] })
      .then(res => {
        const filtered = (res.data || []).filter(a => {
          if (assignedIds.has(a.id)) return false;
          const statusName = (a.status_name || '').toLowerCase();
          if (statusName.includes('inactive') || statusName.includes('retired') || statusName.includes('disposed') || statusName.includes('decommission')) return false;
          return true;
        });
        setAddAssetResults(filtered);
      })
      .catch(() => { setAddAssetResults([]); });
  };

  const handleAddAsset = async () => {
    if (!addAssetForm.asset_id || !addAssetForm.schedule_frequency || !addAssetForm.start_date) return;
    // Compute total hours from first operation (primary planned hours for the WO)
    const opHours = addAssetForm.op_hours || {};
    const firstOp = addAssetOps.length > 0 ? opHours[addAssetOps[0].number] || {} : {};
    const totalHours = (parseFloat(firstOp.planned_hours) || 0) + ((parseInt(firstOp.planned_minutes) || 0) / 60);
    const totalResources = parseInt(firstOp.planned_resources) || 1;
    // Build per-operation hours with decimal values for server
    const opHoursForServer = {};
    addAssetOps.forEach(op => {
      const oh = opHours[op.number] || {};
      opHoursForServer[op.number] = { planned_hours: (parseFloat(oh.planned_hours) || 0) + ((parseInt(oh.planned_minutes) || 0) / 60), planned_resources: parseInt(oh.planned_resources) || 1 };
    });
    const res = await authFetch(`/api/issues/task-list-templates/${addAssetTplId}/assets`, {
      method: 'POST', body: JSON.stringify({ asset_id: addAssetForm.asset_id, schedule_frequency: addAssetForm.schedule_frequency, start_date: addAssetForm.start_date, planned_hours: totalHours, planned_resources: totalResources, op_hours: opHoursForServer })
    });
    if (res.ok) {
      setShowAddAssetModal(false);
      loadTplAssets(addAssetTplId);
      loadTaskListsTab();
      setAddAssetSuccess(addAssetLabel || 'Asset');
    } else {
      const d = await res.json().catch(() => ({}));
      showToast(d.error || 'Error adding asset', 'error');
    }
  };

  const openEditAssetModal = async (tplId, ta) => {
    const r = await authFetch(`/api/issues/task-list-templates/${tplId}`);
    if (!r.ok) return;
    const d = await r.json();
    const ops = (d.operations || []).map((o, i) => ({ number: o.operation_number || String((i + 1) * 10).padStart(4, '0'), title: o.title || `Operation ${i + 1}` }));
    // Parse existing op_hours_json - could be string or object from MySQL
    let existing = {};
    try {
      if (ta.op_hours_json) existing = typeof ta.op_hours_json === 'string' ? JSON.parse(ta.op_hours_json) : ta.op_hours_json;
    } catch (e) { existing = {}; }
    const perOp = {};
    const hasOpJson = Object.keys(existing).length > 0;
    ops.forEach(o => {
      const ex = existing[o.number] || {};
      if (hasOpJson && Object.keys(ex).length > 0) {
        // Use stored per-op data
        const totalH = parseFloat(ex.planned_hours) || 0;
        const wholeHours = Math.floor(totalH);
        const rawMins = Math.round((totalH - wholeHours) * 60);
        const snappedMins = [0, 15, 30, 45].reduce((prev, curr) => Math.abs(curr - rawMins) < Math.abs(prev - rawMins) ? curr : prev);
        perOp[o.number] = { planned_hours: wholeHours, planned_minutes: snappedMins, planned_resources: parseInt(ex.planned_resources) || 1 };
      } else if (!hasOpJson && (ta.planned_hours > 0 || ta.planned_resources > 1)) {
        // No op_hours_json stored — use planned_hours/planned_resources for all ops
        const totalH = parseFloat(ta.planned_hours) || 0;
        const wholeHours = Math.floor(totalH);
        const rawMins = Math.round((totalH - wholeHours) * 60);
        const snappedMins = [0, 15, 30, 45].reduce((prev, curr) => Math.abs(curr - rawMins) < Math.abs(prev - rawMins) ? curr : prev);
        perOp[o.number] = { planned_hours: wholeHours, planned_minutes: snappedMins, planned_resources: parseInt(ta.planned_resources) || 1 };
      } else {
        perOp[o.number] = { planned_hours: 0, planned_minutes: 0, planned_resources: 1 };
      }
    });
    const nextDue = ta.next_notif_due || ta.next_wo_due || null;
    const nextDueStatus = ta.next_notif_due ? ta.next_notif_status : (ta.next_wo_due ? 'Active WO' : null);
    setEditAssetModal({ tplId, assetId: ta.asset_id, assetName: `${ta.asset_tag || ''} ${ta.asset_name || 'Unnamed'}`.trim(), ops, op_hours: perOp, schedule_frequency: ta.schedule_frequency || '', nextDue, nextDueStatus, _initial: JSON.stringify({ op_hours: perOp, schedule_frequency: ta.schedule_frequency || '' }) });
  };

  const openAssetHistory = (tplId, ta) => {
    setEditAssetHistory(null);
    const nextDue = ta.next_notif_due || ta.next_wo_due || null;
    const nextDueLabel = ta.next_notif_due ? 'Next Notification' : (ta.next_wo_due ? 'Next Work Order' : null);
    setViewAssetHistory({ assetName: `${ta.asset_tag || ''} ${ta.asset_name || 'Unnamed'}`.trim(), nextDue, nextDueLabel, frequency: ta.schedule_frequency || '' });
    authFetch(`/api/issues/task-list-templates/${tplId}/assets/${ta.asset_id}/history`)
      .then(r2 => r2.ok ? r2.json() : [])
      .then(h => setEditAssetHistory(h))
      .catch(() => setEditAssetHistory([]));
  };

  const handleEditAssetSave = async () => {
    if (!editAssetModal) return;
    setEditAssetSaving(true);
    const opHoursForServer = {};
    editAssetModal.ops.forEach(op => {
      const oh = editAssetModal.op_hours[op.number] || {};
      opHoursForServer[op.number] = { planned_hours: (parseFloat(oh.planned_hours) || 0) + ((parseInt(oh.planned_minutes) || 0) / 60), planned_resources: parseInt(oh.planned_resources) || 1 };
    });
    const res = await authFetch(`/api/issues/task-list-templates/${editAssetModal.tplId}/assets/${editAssetModal.assetId}`, {
      method: 'PATCH', body: JSON.stringify({ op_hours: opHoursForServer, schedule_frequency: editAssetModal.schedule_frequency })
    });
    setEditAssetSaving(false);
    if (res.ok) {
      setEditAssetModal(null);
      loadTplAssets(editAssetModal.tplId);
      showToast('Asset schedule updated');
    } else {
      const d = await res.json().catch(() => ({}));
      showToast(d.error || 'Error updating hours', 'error');
    }
  };

  const handleRenewAsset = async (tplId, ta) => {
    const assetName = `${ta.asset_tag || ''} ${ta.asset_name || 'Unnamed'}`.trim();
    setConfirmDialog({
      title: 'Renew PM02 Notification',
      rows: [
        { label: 'Asset', value: assetName },
        { label: 'Frequency', value: ta.schedule_frequency || 'Not set' },
        { label: 'Last TECO', value: (() => {
          if (!ta.last_teco_date) return 'None';
          // Server may return YYYY-MM-DD or a full ISO string; take the date portion only
          const datePart = String(ta.last_teco_date).slice(0, 10);
          const d = new Date(datePart + 'T00:00:00');
          if (isNaN(d.getTime())) return 'None';
          return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
        })() },
      ],
      subtitle: 'This will create a new pending notification for supervisor approval. The due date will be calculated from the last TECO date plus the frequency.',
      confirmLabel: 'Renew',
      confirmColor: '#2563eb',
      onConfirm: async () => {
        const res = await authFetch(`/api/issues/task-list-templates/${tplId}/assets/${ta.asset_id}/renew`, { method: 'POST' });
        if (res.ok) {
          const data = await res.json();
          loadTplAssets(tplId);
          setConfirmDialog(null);
          showToast(`Notification ${data.notif_number} created — due ${new Date(data.due_date + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}`);
        } else {
          const d = await res.json().catch(() => ({}));
          setConfirmDialog(null);
          showToast(d.error || 'Error creating notification', 'error');
        }
      }
    });
  };

  const openLinkWoModal = async (tplId, ta) => {
    const assetName = `${ta.asset_tag || ''} ${ta.asset_name || 'Unnamed'}`.trim();
    setLinkWoModal({ tplId, assetId: ta.asset_id, assetName });
    setLinkableWos([]);
    setLinkableWosLoading(true);
    try {
      const res = await authFetch(`/api/issues/task-list-templates/${tplId}/assets/${ta.asset_id}/linkable-wos`);
      if (res.ok) setLinkableWos(await res.json());
    } catch (e) {}
    setLinkableWosLoading(false);
  };

  const confirmLinkWo = async (woId, woNumber) => {
    const res = await authFetch(`/api/issues/task-list-templates/${linkWoModal.tplId}/assets/${linkWoModal.assetId}/link-wo`, {
      method: 'POST', body: JSON.stringify({ wo_id: woId })
    });
    if (res.ok) {
      loadTplAssets(linkWoModal.tplId);
      setLinkWoModal(null); setLinkWoSearch('');
      showToast(`WO ${woNumber} linked to template`);
    } else {
      const d = await res.json().catch(() => ({}));
      showToast(d.error || 'Error linking work order', 'error');
    }
  };

  const handleRemoveAsset = (tplId, assetId, assetName, tplName) => {
    setConfirmDialog({
      title: 'Remove Asset from Template',
      rows: [
        { label: 'Asset', value: assetName },
        { label: 'Template', value: tplName || 'PM02 Template' },
      ],
      subtitle: 'This will stop future scheduled work orders from being generated for this asset. Existing work orders will not be affected.',
      confirmLabel: 'Remove Asset',
      confirmColor: '#dc2626',
      onConfirm: async () => {
        const res = await authFetch(`/api/issues/task-list-templates/${tplId}/assets/${assetId}`, { method: 'DELETE' });
        if (res.ok) { loadTplAssets(tplId); loadTaskListsTab(); showToast('Asset removed from template'); setConfirmDialog(null); }
        else { const d = await res.json(); setConfirmDialog(null); showToast(d.error || 'Cannot remove asset', 'error'); }
      }
    });
  };

  const saveTeam = () => {
    const supervisorTeam = teams.find(t => t.id === teamForm.supervised_by);
    const memberNames = teamForm.member_ids.map(uid => { const u = allUsers.find(x => x.id === uid); return u ? u.name : `User #${uid}`; });
    setConfirmDialog({
      title: editTeam ? 'Update Team' : 'Create Team',
      rows: [
        { label: 'Team Name', value: teamForm.name },
        teamForm.description ? { label: 'Description', value: teamForm.description } : null,
        { label: 'Supervised By', value: supervisorTeam ? supervisorTeam.name : 'None (top-level)' },
        { label: 'Members', value: memberNames.length > 0 ? memberNames.join(', ') : 'None' },
      ].filter(Boolean),
      confirmLabel: 'Confirm',
      confirmColor: 'var(--accent)',
      onConfirm: async () => {
        try {
          const method = editTeam ? 'PUT' : 'POST';
          const url = editTeam ? `/api/admin/groups/${editTeam.id}` : '/api/admin/groups';
          const res = await authFetch(url, { method, body: JSON.stringify(teamForm) });
          if (res.ok) { setShowTeamModal(false); setEditTeam(null); setConfirmDialog(null); loadTeamsTab(); showToast(editTeam ? 'Team updated' : 'Team created'); }
          else { const d = await res.json().catch(() => ({})); setConfirmDialog(null); showToast(d.error || 'Error creating team', 'error'); }
        } catch (e) { setConfirmDialog(null); showToast('Error creating team', 'error'); }
      }
    });
  };

  useEffect(() => {
    if (tab === 'assets') loadAssetTab();
    if (tab === 'licences') loadLicenceTab();
    if (tab === 'teams') loadTeamsTab();
    if (tab === 'kpt') loadKptTab();
    if (tab === 'tasklists') loadTaskListsTab();
    if (tab === 'costcodes') {
      setCostCodeLoading(true);
      authFetch('/api/admin/cost-codes').then(r => r.ok ? r.json() : []).then(d => { setCostCodes(Array.isArray(d) ? d : []); setCostCodeLoading(false); }).catch(() => setCostCodeLoading(false));
    }
    if (tab === 'flocations') {
      setFlLoading(true);
      // Fetch the tree and every FL-attached asset in parallel. We use the
      // assets list (filtered server-side to functional_location=any) so the
      // tree can show attached assets inline under each node.
      Promise.all([
        authFetch('/api/functional-locations').then(r => r.ok ? r.json() : []).catch(() => []),
        authFetch('/api/assets?limit=2000').then(r => r.ok ? r.json() : { data: [] }).catch(() => ({ data: [] })),
      ]).then(([tree, assetsResp]) => {
        const nodes = Array.isArray(tree) ? tree : [];
        const attachedAssets = Array.isArray(assetsResp?.data) ? assetsResp.data : [];
        setFlNodes(nodes);
        setFlAttachedAssets(attachedAssets);
        // Auto-expand only the root(s) on load. Categories and their sub-FLs
        // stay collapsed so the page opens with a tidy single-row view; the
        // user drills down as needed.
        const expandIds = new Set();
        for (const n of nodes) if (n.parent_id == null) expandIds.add(n.id);
        if (expandIds.size > 0) setFlExpanded(prev => { const next = new Set(prev); expandIds.forEach(id => next.add(id)); return next; });
        setFlLoading(false);
      }).catch(() => setFlLoading(false));
    }
    if (tab === 'settings') loadTeamsTab();
    if (tab === 'settings' && !brandingLoaded) {
      authFetch('/api/admin/site-settings').then(r => r.ok ? r.json() : null).then(data => {
        if (data) {
          setBrandingForm({ site_name: data.site_name || '', default_theme: data.default_theme || 'system', subscription_plan_name: data.subscription_plan_name || '', otp_feature_enabled: !!data.otp_feature_enabled });
          if (data.adherence_target !== undefined) setAdherenceTarget(data.adherence_target);
          setBrandingLoaded(true);
        }
      }).catch(() => {});
    }
  }, [tab]);

  // Auto-refresh task lists tab every 30 seconds
  useEffect(() => {
    if (tab !== 'tasklists') return;
    const interval = setInterval(() => { loadTaskListsTab(true); }, 30000);
    return () => clearInterval(interval);
  }, [tab]);

  const saveCustomField = async () => {
    const method = editCf ? 'PUT' : 'POST';
    const url = editCf ? `/api/admin/asset-custom-fields/${editCf.id}` : '/api/admin/asset-custom-fields';
    const res = await authFetch(url, { method, body: JSON.stringify(cfForm) });
    if (res.ok) { setShowCfModal(false); setEditCf(null); safeFetch('/api/admin/asset-custom-fields', setCustomFields); showToast(editCf ? 'Custom field updated' : 'Custom field created'); }
  };

  const reorderCustomField = async (dragId, dropId) => {
    if (dragId === dropId) return;
    const newOrder = [...customFields];
    const dragIdx = newOrder.findIndex(f => f.id === dragId);
    const dropIdx = newOrder.findIndex(f => f.id === dropId);
    const [moved] = newOrder.splice(dragIdx, 1);
    newOrder.splice(dropIdx, 0, moved);
    setCustomFields(newOrder);
    await authFetch('/api/admin/asset-custom-fields/reorder', { method: 'PUT', body: JSON.stringify({ order: newOrder.map(f => f.id) }) });
  };

  // Group save

  // Asset save
  const saveAsset = async () => {
    try {
      const method = editAsset ? 'PUT' : 'POST';
      const url = editAsset ? `/api/admin/assets/${editAsset.id}` : '/api/admin/assets';
      const formData = { ...assetForm, supplier_name: (assetForm.supplier_name || '').trim() || null };
      const res = await authFetch(url, { method, body: JSON.stringify(formData) });
      if (res.ok) { setShowAssetModal(false); setEditAsset(null); loadAssetTab(); showToast(editAsset ? 'Asset updated' : 'Asset created'); }
      else { const d = await res.json().catch(() => ({})); showToast(d.error || 'Failed to save asset', 'error'); }
    } catch (e) { showToast('Error saving asset', 'error'); }
  };


  const toggleSchedule = async (template) => {
    const res = await authFetch(`/api/issues/schedule/${template.id}/toggle`, { method: 'PATCH' });
    if (res.ok) {
      setTemplates(prev => prev.map(t =>
        t.id === template.id ? { ...t, active: t.active ? 0 : 1 } : t
      ));
    }
  };

  if (loading) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <div className="skeleton" style={{ width: 180, height: 28, marginBottom: 8 }} />
            <div className="skeleton" style={{ width: 200, height: 14 }} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {[...Array(4)].map((_, i) => <div key={i} className="skeleton" style={{ width: 100, height: 36, borderRadius: 8 }} />)}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
          {[...Array(6)].map((_, i) => (
            <div key={i} style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 20, border: '1px solid var(--border)' }}>
              <div className="skeleton" style={{ width: 50, height: 12, marginBottom: 6 }} />
              <div className="skeleton" style={{ width: 30, height: 24 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const pm01Count = stats?.issuesByType?.find(t => t.type === 'PM01')?.count || 0;
  const pm02Count = stats?.issuesByType?.find(t => t.type === 'PM02')?.count || 0;
  const pm03Count = stats?.issuesByType?.find(t => t.type === 'PM03')?.count || 0;

  return (
    <div>
      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
            style={{ position: 'fixed', top: 20, right: 20, zIndex: 999, padding: '12px 20px', borderRadius: 10,
              background: toast.type === 'error' ? '#fef2f2' : '#f0fdf4',
              border: `1px solid ${toast.type === 'error' ? '#fecaca' : '#bbf7d0'}`,
              color: toast.type === 'error' ? '#dc2626' : '#16a34a',
              fontSize: 13, fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              display: 'flex', alignItems: 'center', gap: 8
            }}>
            <FiCheck size={16} /> {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Themed Confirmation Dialog */}
      <AnimatePresence>
        {confirmDialog && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 300 }}>
            <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
              style={{ background: 'var(--bg-card)', borderRadius: 16, width: '100%', maxWidth: 440, border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)', overflow: 'hidden' }}>
              <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{confirmDialog.title}</h3>
                <button onClick={() => { setConfirmDialog(null); setConfirmTyped(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
              </div>
              <div style={{ padding: '18px 22px' }}>
                {confirmDialog.subtitle && (
                  <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 16, background: 'var(--accent-light)', border: '1px solid var(--accent-glow)', color: 'var(--accent)', fontSize: 12, fontWeight: 500 }}>
                    {confirmDialog.subtitle}
                  </div>
                )}
                {confirmDialog.rows && confirmDialog.rows.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {confirmDialog.rows.map((row, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, fontSize: 13 }}>
                        <span style={{ fontWeight: 600, color: 'var(--text-secondary)', minWidth: 110, flexShrink: 0 }}>{row.label}</span>
                        <span style={{ color: 'var(--text-primary)', wordBreak: 'break-word' }}>{row.value}</span>
                      </div>
                    ))}
                  </div>
                )}
                {confirmDialog.typeToConfirm && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>
                      Type <strong style={{ color: confirmDialog.confirmColor || 'var(--text-primary)' }}>{confirmDialog.typeToConfirm}</strong> to confirm:
                    </div>
                    <input autoFocus value={confirmTyped} onChange={e => setConfirmTyped(e.target.value)}
                      placeholder={confirmDialog.typeToConfirm}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                  {!confirmDialog.hideCancel && (
                    <button onClick={() => { setConfirmDialog(null); setConfirmTyped(''); }}
                      style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                      Cancel
                    </button>
                  )}
                  {(() => {
                    const needsType = !!confirmDialog.typeToConfirm;
                    const matched = !needsType || confirmTyped === confirmDialog.typeToConfirm;
                    return (
                      <motion.button whileTap={matched ? { scale: 0.97 } : undefined}
                        onClick={() => { if (matched) { confirmDialog.onConfirm(); setConfirmTyped(''); } }}
                        disabled={!matched}
                        style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: confirmDialog.confirmColor || 'var(--accent)', color: '#fff', cursor: matched ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600, opacity: matched ? 1 : 0.45 }}>
                        {confirmDialog.confirmLabel || 'Confirm'}
                      </motion.button>
                    );
                  })()}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiShield size={22} /> Admin Panel
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--text-secondary)' }}>Manage system settings, users, and reports</p>
        </div>
        <div title="Subscription plan" style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999,
          background: headerPlanName ? 'rgba(37,99,235,0.08)' : 'rgba(148,163,184,0.1)',
          border: `1px solid ${headerPlanName ? 'rgba(37,99,235,0.25)' : 'rgba(148,163,184,0.3)'}`,
          color: headerPlanName ? '#2563eb' : 'var(--text-secondary)',
          fontSize: 12, fontWeight: 700, letterSpacing: '0.2px',
        }}>
          <FiShield size={12} /> Plan: {headerPlanName ? formatPlanName(headerPlanName) : 'Not set'}
        </div>
      </div>



      {/* Tab Bar */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid var(--border)', flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => { playClick(); setTab(t.key); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', border: 'none',
              borderBottom: `2px solid ${tab === t.key ? '#2563eb' : 'transparent'}`,
              background: 'none', color: tab === t.key ? '#2563eb' : 'var(--text-secondary)',
              fontWeight: 600, fontSize: 13, cursor: 'pointer', marginBottom: -2, whiteSpace: 'nowrap'
            }}
          >
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {tab === 'overview' && (
        <>
          {/* Summary Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
            {[
              { label: 'PM01 Planned', count: pm01Count, color: WO_TYPE_COLORS.PM01 },
              { label: 'PM02 Recurring', count: pm02Count, color: WO_TYPE_COLORS.PM02 },
              { label: 'PM03 Break In', count: pm03Count, color: WO_TYPE_COLORS.PM03 },
              { label: 'Total Open', count: stats?.openIssues || 0, color: '#3b82f6' },
              { label: 'Overdue', count: stats?.overdueIssues?.length || 0, color: '#ef4444' },
              { label: 'Completed', count: stats?.doneIssues || 0, color: '#10b981' },
            ].map((s, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                style={{
                  background: 'var(--bg-card)', borderRadius: 10, padding: 16, border: '1px solid var(--border)',
                  textAlign: 'center'
                }}
              >
                <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>{s.count}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500, marginTop: 2 }}>{s.label}</div>
              </motion.div>
            ))}
          </div>

          {/* System status */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 10, background: 'var(--bg-card)', border: '1px solid var(--border)', marginBottom: 24, width: 'fit-content' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: firebaseStatus?.connected ? '#16a34a' : '#94a3b8', flexShrink: 0 }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>Firebase</span>
            <span style={{ fontSize: 12, color: firebaseStatus?.connected ? '#16a34a' : 'var(--text-muted)' }}>
              {firebaseStatus?.connected ? `Connected${firebaseStatus.project_id ? ` — ${firebaseStatus.project_id}` : ''}` : 'Not connected'}
            </span>
          </div>

        </>
      )}

      {/* Users Tab */}
      {tab === 'users' && <ManageUsers embedded />}

      {/* Teams Tab */}
      {tab === 'teams' && (
        <>
          {/* Create / Edit Team Modal */}
          <AnimatePresence>
            {showTeamModal && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 200 }}>
                <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                  onClick={e => e.stopPropagation()}
                  style={{ background: 'var(--bg-card)', borderRadius: 16, width: '100%', maxWidth: 480, maxHeight: '90vh', display: 'flex', flexDirection: 'column', border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{editTeam ? 'Edit Team' : 'Create Team'}</h3>
                    <button onClick={() => setShowTeamModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><FiX size={18} /></button>
                  </div>
                  <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto' }}>
                    <div>
                      <label style={settingsLabel}>Team Name *</label>
                      <input value={teamForm.name} onChange={e => setTeamForm(f => ({ ...f, name: titleCase(e.target.value) }))} placeholder="e.g. Maintenance Team A" style={settingsInput} />
                    </div>
                    <div>
                      <label style={settingsLabel}>Description</label>
                      <input value={teamForm.description} onChange={e => { let v = e.target.value; if (v.length > 0) v = v.charAt(0).toUpperCase() + v.slice(1); v = v.replace(/([.!?]\s+)([a-z])/g, (m, p, c) => p + c.toUpperCase()); setTeamForm(f => ({ ...f, description: v })); }} placeholder="What does this team do?" style={settingsInput} />
                    </div>
                    <div>
                      <label style={settingsLabel}>Supervised By</label>
                      <select value={teamForm.supervised_by} onChange={e => setTeamForm(f => ({ ...f, supervised_by: parseInt(e.target.value) || '' }))}
                        disabled={editTeam && /managing\s*director/i.test(editTeam.name)}
                        style={{ ...settingsInput, ...(editTeam && /managing\s*director/i.test(editTeam.name) ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}>
                        <option value="">None (top-level)</option>
                        {teams.filter(t => !editTeam || t.id !== editTeam.id).map(t => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={settingsLabel}>Members ({teamForm.member_ids.length})</label>
                      {/* Current members */}
                      {teamForm.member_ids.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                          {teamForm.member_ids.map(uid => {
                            const u = allUsers.find(x => x.id === uid);
                            if (!u) return null;
                            return (
                              <span key={uid} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 12, background: 'var(--accent-light)', color: 'var(--accent)', border: '1px solid var(--accent-glow)' }}>
                                {u.name}
                                <button onClick={() => setTeamForm(f => ({ ...f, member_ids: f.member_ids.filter(id => id !== uid) }))}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: 0, lineHeight: 1, fontSize: 14 }}>&times;</button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                      {/* Search to add */}
                      <div style={{ position: 'relative' }}>
                        <FiSearch size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                        <input value={memberSearch} onChange={e => setMemberSearch(e.target.value)} placeholder="Search users to add..."
                          style={{ ...settingsInput, paddingLeft: 32 }} />
                      </div>
                      {memberSearch.trim() && (() => {
                        const q = memberSearch.toLowerCase();
                        const filtered = allUsers.filter(u => !u.disabled && u.id !== user?.id && !teamForm.member_ids.includes(u.id) && (u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.employee_id?.includes(memberSearch)));
                        return (
                          <div style={{ border: '1px solid var(--border)', borderRadius: 8, maxHeight: 160, overflowY: 'auto', background: 'var(--bg-input)', marginTop: 4 }}>
                            {allUsers.length === 0 && (
                              <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>Loading users...</div>
                            )}
                            {filtered.map(u => (
                                <div key={u.id}
                                  onClick={() => {
                                    setTeamForm(f => ({ ...f, member_ids: [...f.member_ids, u.id] }));
                                    setMemberSearch('');
                                  }}
                                  className="hover-row"
                                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 13, cursor: 'pointer' }}>
                                  <span style={{ fontWeight: 600, color: 'var(--text-primary)', flex: 1 }}>{u.name}</span>
                                  {u.email && <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{u.email}</span>}
                                  {u.team_name ? (
                                    <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4, background: '#fef3c7', color: '#d97706' }}>{u.team_name}</span>
                                  ) : (
                                    <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4, background: 'rgba(16,185,129,0.1)', color: '#059669' }}>Available</span>
                                  )}
                                </div>
                            ))}
                            {allUsers.length > 0 && filtered.length === 0 && (
                              <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>No matching users</div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                      <button onClick={() => setShowTeamModal(false)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                      <button onClick={saveTeam} disabled={!teamForm.name}
                        style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: !teamForm.name ? '#94a3b8' : 'var(--accent)', color: '#fff', cursor: !teamForm.name ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>
                        {editTeam ? 'Update' : 'Create Team'}
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>


          {/* Org Chart + Team List */}
          <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 20, border: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Teams</h2>
              </div>
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                onClick={() => { setTeamForm({ name: '', description: '', member_ids: [], supervised_by: '' }); setEditTeam(null); setMemberSearch(''); if (allUsers.length === 0) safeFetch('/api/users', setAllUsers); setShowTeamModal(true); }}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                <FiPlus size={14} /> Create Team
              </motion.button>
            </div>

            <TeamOrgChart teams={teams} currentUserId={user?.id}
              defaultExpanded={(() => {
                const myTeam = teams.find(t => t.members?.some(m => m.user_id === user?.id));
                if (!myTeam) return new Set();
                const expanded = new Set([myTeam.id]);
                let parent = teams.find(t => t.id === myTeam.supervised_by);
                while (parent) { expanded.add(parent.id); parent = teams.find(t => t.id === parent.supervised_by); }
                return expanded;
              })()}
              onEdit={(t) => { setEditTeam(t); setTeamForm({ name: t.name, description: t.description || '', member_ids: (t.members || []).map(m => m.user_id), supervised_by: t.supervised_by || '' }); if (allUsers.length === 0) safeFetch('/api/users', setAllUsers); setShowTeamModal(true); }}
              onDelete={(t) => { setConfirmDialog({ title: 'Delete Team', rows: [{ label: 'Team', value: t.name }], confirmLabel: 'Delete', confirmColor: '#dc2626', onConfirm: async () => { const r = await authFetch(`/api/admin/groups/${t.id}`, { method: 'DELETE' }); if (!r.ok) { const d = await r.json(); showToast(d.error || 'Error', 'error'); } else { loadTeamsTab(); showToast('Team deleted'); } setConfirmDialog(null); } }); }}
            />
          </div>
        </>
      )}

      {/* KPT Tab */}
      {tab === 'kpt' && (
        <>
          <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: '16px 20px', border: '1px solid var(--border)', marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <FiKey size={18} style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 2 }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>What is a KPT?</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                A <strong>Key Permission Type</strong> (KPT) is a role-based permission group that controls what actions users can perform in the system. Create KPTs with specific permissions, then assign users to grant them access to features like approving work orders, managing teams, or accessing the admin panel.
              </div>
            </div>
          </div>

          {/* Create/Edit KPT Modal */}
          <AnimatePresence>
            {showKptModal && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
                onClick={() => { setShowKptModal(false); setEditKpt(null); }}>
                <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
                  onClick={e => e.stopPropagation()}
                  style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 460, border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{editKpt ? 'Edit KPT' : 'Create KPT'}</h3>
                    <button onClick={() => { setShowKptModal(false); setEditKpt(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {/* Name with live search */}
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>KPT Name *</label>
                      <input value={kptForm.name} onChange={e => {
                        const val = toTitleCase(e.target.value);
                        setKptForm(f => ({ ...f, name: val }));
                        const exists = kpts.some(k => k.name.toLowerCase() === val.trim().toLowerCase() && (!editKpt || k.id !== editKpt.id));
                        setKptNameExists(exists);
                      }} placeholder="e.g. Site Supervisor"
                        style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${kptNameExists ? '#dc2626' : 'var(--border)'}`, background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                      {kptNameExists && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>A KPT with this name already exists</div>}
                      {kptForm.name.trim() && !kptNameExists && (() => {
                        const q = kptForm.name.toLowerCase();
                        const similar = kpts.filter(k => k.name.toLowerCase().includes(q) && (!editKpt || k.id !== editKpt.id));
                        return similar.length > 0 ? (
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Similar: {similar.map(k => k.name).join(', ')}</div>
                        ) : null;
                      })()}
                    </div>

                    {/* Permissions checkboxes - grouped by section */}
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 8 }}>Permissions</label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 320, overflowY: 'auto' }}>
                        {KPT_PERMISSION_SECTIONS.map(section => (
                          <div key={section.title}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4, paddingLeft: 2 }}>{section.title}</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              {section.perms.map(p => {
                                const checked = kptForm.permissions.includes(p.code);
                                return (
                                  <label key={p.code} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px', borderRadius: 8, background: checked ? 'rgba(37,99,235,0.06)' : 'var(--bg-input)', border: `1px solid ${checked ? 'var(--accent)' : 'var(--border)'}`, cursor: 'pointer', transition: 'all 0.15s' }}>
                                    <input type="checkbox" checked={checked} onChange={() => {
                                      setKptForm(f => ({
                                        ...f,
                                        permissions: checked ? f.permissions.filter(c => c !== p.code) : [...f.permissions, p.code]
                                      }));
                                    }} style={{ accentColor: 'var(--accent)', width: 16, height: 16 }} />
                                    <span style={{ fontSize: 13, fontWeight: 600, color: checked ? 'var(--accent)' : 'var(--text-primary)' }}>{p.label}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Color */}
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Color</label>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {['#059669', '#2563eb', '#d97706', '#7c3aed', '#dc2626', '#0891b2', '#be185d', '#4f46e5'].map(c => (
                          <button key={c} type="button" onClick={() => setKptForm(f => ({ ...f, color: c }))}
                            style={{ width: 28, height: 28, borderRadius: 8, background: c, border: kptForm.color === c ? '2px solid var(--text-primary)' : '2px solid transparent', cursor: 'pointer', transition: 'border 0.15s' }} />
                        ))}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                      <button onClick={() => { setShowKptModal(false); setEditKpt(null); }}
                        style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                      <button onClick={saveKpt} disabled={!kptForm.name.trim() || kptNameExists}
                        style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: (!kptForm.name.trim() || kptNameExists) ? '#94a3b8' : 'var(--accent)', color: '#fff', cursor: (!kptForm.name.trim() || kptNameExists) ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>
                        {editKpt ? 'Update' : 'Create'}
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* View KPT Modal */}
          <AnimatePresence>
            {viewKpt && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
                onClick={() => setViewKpt(null)}>
                <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
                  onClick={e => e.stopPropagation()}
                  style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 460, border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)', maxHeight: '90vh', overflowY: 'auto' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: viewKpt.color }} />
                      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{viewKpt.name}</h3>
                      {viewKpt.is_system ? (
                        <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: 'rgba(37,99,235,0.1)', color: '#2563eb', letterSpacing: '0.5px' }}>SYSTEM</span>
                      ) : null}
                    </div>
                    <button onClick={() => setViewKpt(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
                  </div>

                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
                    {viewKpt.user_count || 0} user{(viewKpt.user_count || 0) !== 1 ? 's' : ''} assigned
                  </div>

                  {/* Permissions by section */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {KPT_PERMISSION_SECTIONS.map(section => {
                      const viewPerms = (viewKpt.permissions || '').split(',').filter(Boolean);
                      const sectionPerms = section.perms.filter(p => viewPerms.includes(p.code));
                      if (sectionPerms.length === 0) return null;
                      return (
                        <div key={section.title}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>{section.title}</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {sectionPerms.map(p => (
                              <div key={p.code} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', borderRadius: 8, background: `${viewKpt.color}08`, border: `1px solid ${viewKpt.color}20` }}>
                                <FiCheck size={14} style={{ color: viewKpt.color, flexShrink: 0 }} />
                                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{p.label}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                    {!viewKpt.is_system && (
                      <button onClick={() => { setEditKpt(viewKpt); setKptForm({ name: viewKpt.name, permissions: (viewKpt.permissions || '').split(',').filter(Boolean), color: viewKpt.color || '#2563eb' }); setKptNameExists(false); setShowKptModal(true); setViewKpt(null); }}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                        <FiEdit2 size={13} /> Edit
                      </button>
                    )}
                    <button onClick={() => { setSelectedKpt(viewKpt); loadKptUsers(viewKpt.id); setViewKpt(null); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                      <FiUsers size={13} /> View Users
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 20, border: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Key Permission Types</h2>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>Create KPTs and assign them to users</p>
              </div>
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                onClick={() => { setKptForm({ name: '', permissions: ['create_notif', 'time_confirm'], color: '#2563eb' }); setEditKpt(null); setKptNameExists(false); setShowKptModal(true); }}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                <FiPlus size={14} /> Create KPT
              </motion.button>
            </div>

            {kpts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                <FiShield size={36} style={{ marginBottom: 10, opacity: 0.3 }} />
                <div style={{ fontSize: 14 }}>No KPTs created yet</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {kpts.map(k => (
                    <div key={k.id}
                      onClick={() => setViewKpt(k)}
                      style={{
                        padding: '14px 16px', borderRadius: 10,
                        border: `1px solid ${selectedKpt?.id === k.id ? k.color + '40' : 'var(--border)'}`,
                        borderLeft: `3px solid ${k.color}`,
                        background: selectedKpt?.id === k.id ? `${k.color}08` : 'var(--bg-card)',
                        cursor: 'pointer', transition: 'all 0.15s'
                      }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', flex: 1 }}>{k.name}</span>
                        {k.is_system ? (
                          <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: 'rgba(37,99,235,0.1)', color: '#2563eb', letterSpacing: '0.5px' }}>SYSTEM</span>
                        ) : null}
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{k.user_count || 0} user{(k.user_count || 0) !== 1 ? 's' : ''}</span>
                        {!k.is_system && (
                          <motion.button whileTap={{ scale: 0.9 }}
                            onClick={(e) => { e.stopPropagation(); setEditKpt(k); setKptForm({ name: k.name, permissions: (k.permissions || '').split(',').filter(Boolean), color: k.color || '#2563eb' }); setKptNameExists(false); setShowKptModal(true); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 4 }}><FiEdit2 size={13} /></motion.button>
                        )}
                        {!k.is_system && (
                          <motion.button whileTap={{ scale: 0.9 }}
                            onClick={(e) => { e.stopPropagation(); deleteKpt(k); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: (k.user_count || 0) > 0 ? 'var(--text-muted)' : '#ef4444', padding: 4, opacity: (k.user_count || 0) > 0 ? 0.4 : 1, cursor: (k.user_count || 0) > 0 ? 'not-allowed' : 'pointer' }}
                            disabled={(k.user_count || 0) > 0}
                            title={(k.user_count || 0) > 0 ? 'Remove all users before deleting' : 'Delete KPT'}
                          ><FiTrash2 size={13} /></motion.button>
                        )}
                      </div>
                    </div>
                ))}
              </div>
            )}
          </div>

          {/* Selected KPT - User assignments */}
          {selectedKpt && (
            <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 20, border: '1px solid var(--border)', marginTop: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: selectedKpt.color }}>{selectedKpt.name}</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>-</span>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Assigned Users</h3>
              </div>

              {/* Search to add user */}
              <div style={{ position: 'relative', marginBottom: 12 }}>
                <FiSearch size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input value={kptUserSearch} onChange={e => setKptUserSearch(e.target.value)} placeholder="Search users to assign..."
                  style={{ width: '100%', padding: '10px 14px 10px 32px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              {kptUserSearch.trim() && (() => {
                const q = kptUserSearch.toLowerCase();
                const assignedIds = new Set(kptUsers.map(u => u.id));
                const filtered = allUsers.filter(u => !u.disabled && !assignedIds.has(u.id) && (u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.employee_id?.includes(kptUserSearch)));
                return filtered.length > 0 ? (
                  <div style={{ border: '1px solid var(--border)', borderRadius: 8, maxHeight: 160, overflowY: 'auto', background: 'var(--bg-input)', marginBottom: 12 }}>
                    {filtered.map(u => (
                      <div key={u.id} onClick={() => assignUserToKpt(u.id)} className="hover-row"
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 13, cursor: 'pointer' }}>
                        <span style={{ fontWeight: 600, color: 'var(--text-primary)', flex: 1 }}>{u.name}</span>
                        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{u.email}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>No matching users</div>
                );
              })()}

              {/* Current assigned users */}
              {kptUsersLoading ? (
                <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 12 }}>Loading...</div>
              ) : kptUsers.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 13 }}>No users assigned to this KPT</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {kptUsers.map(u => (
                    <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, background: 'var(--bg-input)' }}>
                      {u.avatar ? (
                        <img src={`/api/users/avatar/${u.avatar}`} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                      ) : (
                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: u.avatar_color || selectedKpt.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                          {u.name ? (() => { const p = u.name.trim().split(/\s+/); return p.length >= 2 ? (p[0][0] + '.' + p[p.length-1][0]).toUpperCase() : p[0][0].toUpperCase(); })() : '?'}
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{u.name}</span>
                        {u.employee_id && <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6 }}>{u.employee_id}</span>}
                      </div>
                      {u.id !== user?.id && (
                        <button onClick={() => removeUserFromKpt(u.id)} title="Remove user"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px 6px', borderRadius: 6, transition: 'color 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.color = '#dc2626'}
                          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                        ><FiTrash2 size={14} /></button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Task Lists Tab */}
      {tab === 'tasklists' && (
        <>
          {/* Create/Edit Task List Modal — 2-Step Builder */}
          <AnimatePresence>
            {showTaskTplModal && (() => {
              const step1Valid = !!taskTplForm.name.trim() && !!taskTplForm.description.trim();
              const step2Valid = (taskTplForm.operations || []).filter(o => o.title.trim()).length >= 1;
              const totalItems = (taskTplForm.operations || []).filter(o => o.title.trim()).reduce((sum, op) => sum + (op.sections || []).reduce((s, sec) => s + (sec.items || []).length, 0), 0);
              const step3Valid = totalItems >= 1;
              return (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
                  style={{ background: 'var(--bg-card)', borderRadius: 16, width: '100%', maxWidth: 640, border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                  {/* Fixed Header */}
                  <div style={{ padding: '20px 28px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{editTaskTpl ? 'Edit PM02 Template' : 'Create PM02 Template'}</h3>
                      <button onClick={() => { setShowTaskTplModal(false); setEditTaskTpl(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
                    </div>
                    {/* Step indicator */}
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }} onClick={() => setTaskTplStep(1)}>
                        <span style={{ width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, background: taskTplStep === 1 ? '#2563eb' : step1Valid ? '#10b981' : 'var(--border)', color: taskTplStep === 1 || step1Valid ? '#fff' : 'var(--text-muted)', transition: 'all 0.2s' }}>
                          {step1Valid && taskTplStep !== 1 ? <FiCheck size={14} /> : '1'}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: taskTplStep === 1 ? 700 : 500, color: taskTplStep === 1 ? 'var(--text-primary)' : 'var(--text-muted)' }}>Details</span>
                      </div>
                      <div style={{ flex: 1, height: 2, background: step1Valid ? '#10b981' : 'var(--border)', borderRadius: 1, transition: 'all 0.3s' }} />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: step1Valid ? 'pointer' : 'default', opacity: step1Valid ? 1 : 0.5 }} onClick={() => step1Valid && setTaskTplStep(2)}>
                        <span style={{ width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, background: taskTplStep === 2 ? '#2563eb' : step2Valid ? '#10b981' : 'var(--border)', color: taskTplStep === 2 || step2Valid ? '#fff' : 'var(--text-muted)', transition: 'all 0.2s' }}>
                          {step2Valid && taskTplStep !== 2 ? <FiCheck size={14} /> : '2'}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: taskTplStep === 2 ? 700 : 500, color: taskTplStep === 2 ? 'var(--text-primary)' : 'var(--text-muted)' }}>Operations</span>
                      </div>
                      <div style={{ flex: 1, height: 2, background: step2Valid ? '#10b981' : 'var(--border)', borderRadius: 1, transition: 'all 0.3s' }} />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: step1Valid && step2Valid ? 'pointer' : 'default', opacity: step1Valid && step2Valid ? 1 : 0.5 }} onClick={() => step1Valid && step2Valid && setTaskTplStep(3)}>
                        <span style={{ width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, background: taskTplStep === 3 ? '#2563eb' : step3Valid ? '#10b981' : 'var(--border)', color: taskTplStep === 3 || step3Valid ? '#fff' : 'var(--text-muted)', transition: 'all 0.2s' }}>
                          {step3Valid && taskTplStep !== 3 ? <FiCheck size={14} /> : '3'}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: taskTplStep === 3 ? 700 : 500, color: taskTplStep === 3 ? 'var(--text-primary)' : 'var(--text-muted)' }}>Task Items</span>
                      </div>
                    </div>
                  </div>

                  {/* Scrollable Body */}
                  <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px' }}>
                    {taskTplStep === 1 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div>
                          <label style={settingsLabel}>Template Name *</label>
                          <input value={taskTplForm.name} onChange={e => setTaskTplForm(f => ({ ...f, name: titleCase(e.target.value) }))}
                            autoFocus={window.innerWidth > 768}
                            placeholder="e.g. Monthly Fire Extinguisher Check" style={{ ...settingsInput, borderColor: !taskTplForm.name.trim() ? 'rgba(220,38,38,0.3)' : undefined }} />
                        </div>
                        <div>
                          <label style={settingsLabel}>Description *</label>
                          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4 }}>This will be used as the work order description when assigned</div>
                          <textarea value={taskTplForm.description} onChange={e => {
                            const val = e.target.value;
                            const capitalized = val.charAt(0).toUpperCase() + val.slice(1);
                            const sentenceCased = capitalized.replace(/([.!?]\s+)([a-z])/g, (m, p, c) => p + c.toUpperCase());
                            setTaskTplForm(f => ({ ...f, description: sentenceCased }));
                          }} rows={3}
                            placeholder="Enter the work order description text..."
                            style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${!taskTplForm.description.trim() ? 'rgba(220,38,38,0.3)' : 'var(--border)'}`, fontSize: 13, outline: 'none', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit', background: 'var(--bg-input)', color: 'var(--text-primary)' }} />
                        </div>
                        {/* Sequence Mode */}
                        <div onClick={() => setTaskTplForm(f => ({ ...f, sequence_mode: !f.sequence_mode }))}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
                            borderRadius: 10, cursor: 'pointer',
                            border: `2px solid ${taskTplForm.sequence_mode ? '#f59e0b' : 'var(--border)'}`,
                            background: taskTplForm.sequence_mode ? 'rgba(245,158,11,0.06)' : 'var(--bg-card)',
                            transition: 'all 0.2s'
                          }}>
                          <div style={{ width: 20, height: 20, borderRadius: 4, border: `2px solid ${taskTplForm.sequence_mode ? '#f59e0b' : 'var(--border)'}`, background: taskTplForm.sequence_mode ? '#f59e0b' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {taskTplForm.sequence_mode && <span style={{ color: '#fff', fontSize: 12, fontWeight: 800 }}>✓</span>}
                          </div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: taskTplForm.sequence_mode ? '#f59e0b' : 'var(--text-primary)' }}>Sequence Mode</div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Each operation triggers the next when TECO'd. Last operation restarts the cycle.</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {taskTplStep === 2 && (
                      <div>
                        <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(37,99,235,0.05)', border: '1px solid rgba(37,99,235,0.12)', marginBottom: 12, fontSize: 12, color: 'var(--text-secondary)' }}>
                          Define operations for this template. Each operation will have its own checklist.
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                          <div style={{ flex: 1, padding: '6px 10px', borderRadius: 6, background: 'rgba(37,99,235,0.04)', border: '1px solid rgba(37,99,235,0.12)' }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: '#2563eb' }}>Normal</span>
                            <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 6 }}>Follows planned dates, respects leave and days off</span>
                          </div>
                          <div style={{ flex: 1, padding: '6px 10px', borderRadius: 6, background: 'rgba(245,158,11,0.04)', border: '1px solid rgba(245,158,11,0.12)' }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: '#f59e0b' }}>Push</span>
                            <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 6 }}>Bypasses leave and days off when scheduling</span>
                          </div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {(taskTplForm.operations || []).map((op, idx) => {
                            const opNum = String((idx + 1) * 10).padStart(4, '0');
                            return (
                              <div key={op._id || idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 8, background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
                                <span style={{ fontSize: 13, fontWeight: 700, color: '#2563eb', minWidth: 44, fontFamily: 'monospace' }}>{opNum}</span>
                                <input value={op.title} onChange={e => {
                                  const v = e.target.value;
                                  const titled = v.replace(/(^|\s)([a-z])/g, (m, p, l) => p + l.toUpperCase());
                                  setTaskTplForm(f => {
                                    const ops = [...f.operations];
                                    ops[idx] = { ...ops[idx], title: titled };
                                    return { ...f, operations: ops };
                                  });
                                }}
                                  placeholder="Operation title (e.g. Visual Inspection)"
                                  style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, outline: 'none', background: 'var(--bg-card)', color: 'var(--text-primary)' }} />
                                <div style={{ display: 'flex', gap: 2, background: 'var(--bg-input)', borderRadius: 6, padding: 2 }}>
                                  {['Normal', 'Push'].map(p => (
                                    <button key={p} type="button" onClick={() => setTaskTplForm(f => {
                                      const ops = [...f.operations];
                                      ops[idx] = { ...ops[idx], priority: p };
                                      return { ...f, operations: ops };
                                    })}
                                      style={{ padding: '4px 8px', borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 10, fontWeight: 700, transition: 'all 0.15s',
                                        background: (op.priority || 'Normal') === p ? (p === 'Push' ? '#f59e0b' : '#2563eb') : 'transparent',
                                        color: (op.priority || 'Normal') === p ? '#fff' : 'var(--text-muted)' }}>
                                      {p}
                                    </button>
                                  ))}
                                </div>
                                {taskTplForm.operations.length > 1 && (
                                  <>
                                    <button onClick={() => setTaskTplForm(f => {
                                      if (idx === 0) return f;
                                      const ops = [...f.operations];
                                      [ops[idx - 1], ops[idx]] = [ops[idx], ops[idx - 1]];
                                      return { ...f, operations: ops };
                                    })} disabled={idx === 0} title="Move up"
                                      style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: idx === 0 ? 'not-allowed' : 'pointer', color: 'var(--text-muted)', fontSize: 10, opacity: idx === 0 ? 0.3 : 1 }}>
                                      <FiArrowUp size={12} />
                                    </button>
                                    <button onClick={() => setTaskTplForm(f => {
                                      if (idx === f.operations.length - 1) return f;
                                      const ops = [...f.operations];
                                      [ops[idx], ops[idx + 1]] = [ops[idx + 1], ops[idx]];
                                      return { ...f, operations: ops };
                                    })} disabled={idx === taskTplForm.operations.length - 1} title="Move down"
                                      style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: idx === taskTplForm.operations.length - 1 ? 'not-allowed' : 'pointer', color: 'var(--text-muted)', fontSize: 10, opacity: idx === taskTplForm.operations.length - 1 ? 0.3 : 1 }}>
                                      <FiArrowDown size={12} />
                                    </button>
                                    <button onClick={() => setTaskTplForm(f => {
                                      const ops = f.operations.filter((_, i) => i !== idx);
                                      return { ...f, operations: ops };
                                    })}
                                      style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 10 }}>
                                      <FiTrash2 size={12} />
                                    </button>
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        <button onClick={() => setTaskTplForm(f => ({ ...f, operations: [...(f.operations || []), { _id: tplOpIdRef.current++, title: '', priority: 'Normal', sections: [{ name: 'General', items: [] }] }] }))}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 14px', borderRadius: 8, border: '1px dashed var(--border)', background: 'transparent', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#2563eb', width: '100%', justifyContent: 'center', marginTop: 12 }}>
                          <FiPlus size={12} /> Add Operation
                        </button>
                      </div>
                    )}

                    {taskTplStep === 3 && (
                      <div>
                        {/* Summary banner */}
                        <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(37,99,235,0.05)', border: '1px solid rgba(37,99,235,0.12)', marginBottom: 16, fontSize: 12, color: 'var(--text-secondary)' }}>
                          <strong style={{ color: 'var(--text-primary)' }}>{taskTplForm.name}</strong>
                          {taskTplForm.description && <span> &middot; {taskTplForm.description.length > 60 ? taskTplForm.description.slice(0, 60) + '...' : taskTplForm.description}</span>}
                          <span> &middot; {(taskTplForm.operations || []).filter(o => o.title.trim()).length} ops</span>
                          <span> &middot; {totalItems} item{totalItems !== 1 ? 's' : ''}</span>
                        </div>

                        {/* Per-operation checklist items */}
                        {(taskTplForm.operations || []).map((op, opIdx) => {
                          if (!op.title.trim()) return null;
                          const opNum = String((opIdx + 1) * 10).padStart(4, '0');
                          const opSections = op.sections || [{ name: 'General', items: [] }];
                          const opItemCount = opSections.reduce((s, sec) => s + sec.items.length, 0);
                          return (
                            <div key={op._id || opIdx} style={{ marginBottom: 20, borderRadius: 10, border: '1px solid var(--border)', overflow: 'hidden' }}>
                              {/* Operation header */}
                              <div style={{ padding: '12px 14px', background: 'rgba(37,99,235,0.04)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 13, fontWeight: 700, color: '#2563eb', fontFamily: 'monospace' }}>{opNum}</span>
                                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', flex: 1 }}>{op.title}</span>
                                {(op.priority || 'Normal') === 'Push' && <span style={{ padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700, color: '#fff', background: '#2563eb' }}>PUSH</span>}
                                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{opItemCount} item{opItemCount !== 1 ? 's' : ''}</span>
                              </div>

                              <div style={{ padding: '12px 14px' }}>
                                {/* Sections within this operation */}
                                {opSections.map((section, si) => {
                                  const secKey = `${opIdx}-${si}`;
                                  const isActiveSection = activeSectionIdx === secKey;
                                  return (
                                    <div key={si} style={{ marginBottom: 16 }}>
                                      {/* Section header */}
                                      <div style={{ borderTop: si > 0 ? '2px solid var(--border)' : 'none', marginBottom: 8, paddingTop: si > 0 ? 10 : 0 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                          {editingSectionIdx === secKey ? (
                                            <div style={{ display: 'flex', gap: 4, alignItems: 'center', flex: 1 }}>
                                              <input value={editingSectionName} onChange={e => setEditingSectionName(e.target.value)} autoFocus
                                                onKeyDown={e => { if (e.key === 'Enter' && editingSectionName.trim()) { setTaskTplForm(f => { const ops = [...f.operations]; const secs = [...ops[opIdx].sections]; secs[si] = { ...secs[si], name: editingSectionName.trim() }; ops[opIdx] = { ...ops[opIdx], sections: secs }; return { ...f, operations: ops }; }); setEditingSectionIdx(null); } }}
                                                style={{ flex: 1, padding: '4px 8px', borderRadius: 4, border: '1px solid #2563eb', fontSize: 13, fontWeight: 700, outline: 'none', background: 'var(--bg-input)', color: 'var(--text-primary)', textTransform: 'uppercase' }} />
                                              <button onClick={() => { if (editingSectionName.trim()) { setTaskTplForm(f => { const ops = [...f.operations]; const secs = [...ops[opIdx].sections]; secs[si] = { ...secs[si], name: editingSectionName.trim() }; ops[opIdx] = { ...ops[opIdx], sections: secs }; return { ...f, operations: ops }; }); } setEditingSectionIdx(null); }}
                                                style={{ padding: '4px 8px', borderRadius: 4, border: 'none', background: '#2563eb', color: '#fff', fontSize: 10, cursor: 'pointer' }}>Save</button>
                                            </div>
                                          ) : (
                                            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', flex: 1, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.5px' }}
                                              onClick={() => { setEditingSectionIdx(secKey); setEditingSectionName(section.name); }}>
                                              {section.name} <FiEdit2 size={10} style={{ color: 'var(--text-muted)', verticalAlign: 'middle' }} />
                                            </span>
                                          )}
                                          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{section.items.length} item{section.items.length !== 1 ? 's' : ''}</span>
                                          {opSections.length > 1 && (
                                            <button onClick={() => {
                                              if (!window.confirm(`Delete section "${section.name}" and all its items?`)) return;
                                              setTaskTplForm(f => { const ops = [...f.operations]; ops[opIdx] = { ...ops[opIdx], sections: ops[opIdx].sections.filter((_, i) => i !== si) }; return { ...f, operations: ops }; });
                                            }} style={{ padding: '3px 6px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', color: '#dc2626', fontSize: 10 }}>
                                              <FiTrash2 size={10} />
                                            </button>
                                          )}
                                        </div>
                                      </div>

                                      {/* Items in this section */}
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: section.items.length > 0 ? 10 : 0 }}>
                                        {section.items.map((item, idx) => (
                                          <div key={item._id || idx}
                                            draggable
                                            onDragStart={() => setDragItem({ opIdx, si, idx })}
                                            onDragOver={e => { e.preventDefault(); setDragOverItem({ opIdx, si, idx }); }}
                                            onDragEnd={() => { setDragItem(null); setDragOverItem(null); }}
                                            onDrop={e => {
                                              e.preventDefault();
                                              if (!dragItem || dragItem.opIdx !== opIdx || dragItem.si !== si) return;
                                              setTaskTplForm(f => {
                                                const ops = [...f.operations]; const secs = [...ops[opIdx].sections]; const items = [...secs[si].items];
                                                const [moved] = items.splice(dragItem.idx, 1); items.splice(idx, 0, moved);
                                                secs[si] = { ...secs[si], items }; ops[opIdx] = { ...ops[opIdx], sections: secs };
                                                return { ...f, operations: ops };
                                              });
                                              setDragItem(null); setDragOverItem(null);
                                            }}
                                            style={{ padding: '10px 12px', borderRadius: 8, background: 'var(--bg-input)', border: dragOverItem && dragOverItem.opIdx === opIdx && dragOverItem.si === si && dragOverItem.idx === idx ? '2px solid #2563eb' : '1px solid var(--border-subtle)', cursor: 'grab', opacity: dragItem && dragItem.opIdx === opIdx && dragItem.si === si && dragItem.idx === idx ? 0.4 : 1 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                              <span style={{ fontSize: 14, color: 'var(--text-muted)', cursor: 'grab' }}>&#8942;&#8942;</span>
                                              <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 700, minWidth: 20, textAlign: 'center' }}>{idx + 1}</span>
                                              <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                                                {['Pass', 'Fail', 'N/A'].map(opt => (
                                                  <span key={opt} style={{ padding: '2px 5px', borderRadius: 4, fontSize: 8, fontWeight: 700, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-muted)', opacity: 0.5 }}>{opt}</span>
                                                ))}
                                              </div>
                                              <div style={{ flex: 1 }}>
                                                <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{item.label || <span style={{ color: '#dc2626', fontStyle: 'italic' }}>No instruction</span>}</span>
                                                {item.description && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{item.description}</div>}
                                              </div>
                                              {item.required && <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: 'rgba(220,38,38,0.08)', color: '#dc2626', fontWeight: 600, border: '1px solid rgba(220,38,38,0.15)' }}>Task Required</span>}
                                              {item.require_photo && <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: 'rgba(37,99,235,0.08)', color: '#f59e0b', fontWeight: 600, border: '1px solid rgba(245,158,11,0.15)' }}>Photo Required</span>}
                                              <button onClick={() => setTaskTplForm(f => {
                                                const ops = [...f.operations]; const secs = [...ops[opIdx].sections];
                                                secs[si] = { ...secs[si], items: secs[si].items.filter((_, i) => i !== idx) };
                                                ops[opIdx] = { ...ops[opIdx], sections: secs }; return { ...f, operations: ops };
                                              })}
                                                style={{ padding: '4px 6px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', color: '#dc2626', fontSize: 10 }}>
                                                <FiTrash2 size={11} />
                                              </button>
                                            </div>
                                          </div>
                                        ))}
                                      </div>

                                      {/* Add item to this section */}
                                      {isActiveSection ? (
                                        <div style={{ padding: '14px', borderRadius: 10, border: '2px dashed var(--border)', background: 'var(--bg-card)' }}>
                                          <div style={{ marginBottom: 8 }}>
                                            <input value={newTaskItem.label} onChange={e => {
                                                const v = e.target.value.replace(/(^|[.!?]\s+)([a-z])/g, (m, p, l) => p + l.toUpperCase());
                                                setNewTaskItem(p => ({ ...p, label: v }));
                                              }}
                                              placeholder="Task instruction..." autoFocus
                                              style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, outline: 'none', background: 'var(--bg-input)', color: 'var(--text-primary)', boxSizing: 'border-box' }} />
                                          </div>
                                          <div style={{ marginBottom: 8 }}>
                                            <input value={newTaskItem.description || ''} onChange={e => {
                                                const val = e.target.value;
                                                const capitalized = val.charAt(0).toUpperCase() + val.slice(1);
                                                const sentenceCased = capitalized.replace(/([.!?]\s+)([a-z])/g, (m, p, c) => p + c.toUpperCase());
                                                setNewTaskItem(p => ({ ...p, description: sentenceCased }));
                                              }}
                                              placeholder="Task description (optional)"
                                              style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, outline: 'none', background: 'var(--bg-input)', color: 'var(--text-primary)', boxSizing: 'border-box' }} />
                                          </div>
                                          <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                            <button type="button" onClick={() => setNewTaskItem(p => ({ ...p, required: !p.required }))}
                                              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 6, cursor: 'pointer', transition: 'all 0.15s', border: `1px solid ${newTaskItem.required ? 'rgba(220,38,38,0.3)' : 'var(--border)'}`, background: newTaskItem.required ? 'rgba(220,38,38,0.08)' : 'var(--bg-card)', color: newTaskItem.required ? '#dc2626' : 'var(--text-muted)' }}>
                                              <span style={{ width: 6, height: 6, borderRadius: '50%', background: newTaskItem.required ? '#dc2626' : 'var(--border)', transition: 'all 0.15s' }} />
                                              Task Required
                                            </button>
                                            <button type="button" onClick={() => setNewTaskItem(p => ({ ...p, require_photo: !p.require_photo }))}
                                              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 6, cursor: 'pointer', transition: 'all 0.15s', border: `1px solid ${newTaskItem.require_photo ? 'rgba(245,158,11,0.3)' : 'var(--border)'}`, background: newTaskItem.require_photo ? 'rgba(37,99,235,0.08)' : 'var(--bg-card)', color: newTaskItem.require_photo ? '#f59e0b' : 'var(--text-muted)' }}>
                                              <span style={{ width: 6, height: 6, borderRadius: '50%', background: newTaskItem.require_photo ? '#f59e0b' : 'var(--border)', transition: 'all 0.15s' }} />
                                              Photo Required
                                            </button>
                                          </div>
                                          <button onClick={() => {
                                            if (!newTaskItem.label.trim()) return;
                                            setTaskTplForm(f => {
                                              const ops = [...f.operations]; const secs = [...ops[opIdx].sections];
                                              secs[si] = { ...secs[si], items: [...secs[si].items, { ...newTaskItem, _id: tplItemIdRef.current++, item_type: 'checkbox' }] };
                                              ops[opIdx] = { ...ops[opIdx], sections: secs }; return { ...f, operations: ops };
                                            });
                                            setNewTaskItem({ label: '', description: '', item_type: 'checkbox', required: true, require_photo: false });
                                          }} disabled={!newTaskItem.label.trim()} style={{ padding: '8px 14px', borderRadius: 6, border: 'none', background: newTaskItem.label.trim() ? '#2563eb' : '#94a3b8', color: '#fff', fontSize: 12, fontWeight: 600, cursor: newTaskItem.label.trim() ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <FiPlus size={12} /> Add
                                          </button>
                                        </div>
                                      ) : (
                                        <button onClick={() => { setActiveSectionIdx(secKey); setNewTaskItem({ label: '', description: '', item_type: 'checkbox', required: true, require_photo: false }); }}
                                          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px dashed var(--border)', background: 'transparent', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#2563eb', width: '100%', justifyContent: 'center' }}>
                                          <FiPlus size={12} /> Add Item
                                        </button>
                                      )}
                                    </div>
                                  );
                                })}

                                {/* Add new section within this operation */}
                                {newSectionName !== null && editingSectionIdx === `newSec-${opIdx}` ? (
                                  <div style={{ padding: '10px 14px', borderRadius: 8, border: '2px dashed var(--border)', background: 'var(--bg-card)', display: 'flex', gap: 8, alignItems: 'center' }}>
                                    <input value={newSectionName} onChange={e => setNewSectionName(e.target.value)} autoFocus
                                      placeholder="Section name..."
                                      onKeyDown={e => {
                                        if (e.key === 'Enter' && newSectionName.trim()) {
                                          setTaskTplForm(f => { const ops = [...f.operations]; ops[opIdx] = { ...ops[opIdx], sections: [...ops[opIdx].sections, { name: newSectionName.trim(), items: [] }] }; return { ...f, operations: ops }; });
                                          setActiveSectionIdx(`${opIdx}-${opSections.length}`); setNewSectionName(null); setEditingSectionIdx(null);
                                        }
                                        if (e.key === 'Escape') { setNewSectionName(null); setEditingSectionIdx(null); }
                                      }}
                                      style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, fontWeight: 600, outline: 'none', background: 'var(--bg-input)', color: 'var(--text-primary)', textTransform: 'uppercase' }} />
                                    <button onClick={() => {
                                      if (!newSectionName.trim()) return;
                                      setTaskTplForm(f => { const ops = [...f.operations]; ops[opIdx] = { ...ops[opIdx], sections: [...ops[opIdx].sections, { name: newSectionName.trim(), items: [] }] }; return { ...f, operations: ops }; });
                                      setActiveSectionIdx(`${opIdx}-${opSections.length}`); setNewSectionName(null); setEditingSectionIdx(null);
                                    }} disabled={!newSectionName.trim()}
                                      style={{ padding: '8px 14px', borderRadius: 6, border: 'none', background: newSectionName.trim() ? '#2563eb' : '#94a3b8', color: '#fff', fontSize: 12, fontWeight: 600, cursor: newSectionName.trim() ? 'pointer' : 'not-allowed' }}>Add</button>
                                    <button onClick={() => { setNewSectionName(null); setEditingSectionIdx(null); }}
                                      style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                                  </div>
                                ) : (
                                  <button onClick={() => { setNewSectionName(''); setEditingSectionIdx(`newSec-${opIdx}`); }}
                                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '2px dashed var(--border)', background: 'transparent', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', width: '100%', justifyContent: 'center', marginTop: 8 }}>
                                    <FiPlus size={13} /> Add Section
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Fixed Footer */}
                  <div style={{ padding: '16px 28px 20px', borderTop: '1px solid var(--border)', flexShrink: 0, display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      {taskTplStep > 1 && (
                        <button onClick={() => setTaskTplStep(taskTplStep - 1)}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>
                          <FiArrowLeft size={14} /> Back
                        </button>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => { setShowTaskTplModal(false); setEditTaskTpl(null); }}
                        style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>Cancel</button>
                      {taskTplStep === 1 && (
                        <button onClick={() => {
                          // Auto-default operation 0010 title to template name if empty
                          const ops = taskTplForm.operations || [{ title: '' }];
                          if (!ops[0]?.title?.trim() && taskTplForm.name?.trim()) {
                            const newOps = [...ops];
                            newOps[0] = { ...newOps[0], title: taskTplForm.name.trim() };
                            setTaskTplForm(f => ({ ...f, operations: newOps }));
                          }
                          setTaskTplStep(2);
                        }} disabled={!step1Valid}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', borderRadius: 8, border: 'none', background: step1Valid ? '#2563eb' : '#94a3b8', color: '#fff', cursor: step1Valid ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600 }}>
                          Next: Operations <FiArrowRight size={14} />
                        </button>
                      )}
                      {taskTplStep === 2 && (
                        <button onClick={() => setTaskTplStep(3)} disabled={!step2Valid}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', borderRadius: 8, border: 'none', background: step2Valid ? '#2563eb' : '#94a3b8', color: '#fff', cursor: step2Valid ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600 }}>
                          Next: Task Items <FiArrowRight size={14} />
                        </button>
                      )}
                      {taskTplStep === 3 && (
                        <button onClick={saveTaskTemplate} disabled={!step3Valid}
                          style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: step3Valid ? '#10b981' : '#94a3b8', color: '#fff', cursor: step3Valid ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600 }}>
                          {editTaskTpl ? 'Update' : 'Create'} Template
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              </motion.div>
              );
            })()}
          </AnimatePresence>

          {/* Edit Confirmation Modal */}
          <AnimatePresence>
            {showEditConfirm && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
                  style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 28, maxWidth: 460, width: '100%', border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(37,99,235,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FiAlertTriangle size={20} color="#2563eb" />
                    </div>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', flex: 1 }}>Confirm Edit</h3>
                    <button onClick={() => { setShowEditConfirm(false); setPendingEditSave(false); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                    Updating this template will create a new version ({editTaskTpl?.tskl_number}/{String((editTaskTpl?.version || 1) + 10).padStart(4, '0')}). The previous version will be saved in the version history.
                  </p>
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
                    Existing work orders using this template will <strong>not</strong> be affected. Only new work orders will use the updated version.
                  </p>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button onClick={() => { setShowEditConfirm(false); setPendingEditSave(false); }}
                      style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>Cancel</button>
                    <button onClick={confirmSaveTaskTemplate}
                      style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                      Save New Version
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Delete Confirmation Modal */}
          <AnimatePresence>
            {showDeleteConfirm && deletePreview && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
                  style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 28, maxWidth: 500, width: '100%', border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(220,38,38,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FiTrash2 size={20} color="#dc2626" />
                    </div>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', flex: 1 }}>Delete PM02 Template</h3>
                    <button onClick={() => { setShowDeleteConfirm(false); setDeletePreview(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
                  </div>
                  <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12 }}>
                    "{deletePreview.tpl.name}" ({deletePreview.tpl.tskl_number || 'No TSKL'})
                  </p>
                  <div style={{ padding: 14, background: 'rgba(220,38,38,0.04)', borderRadius: 10, border: '1px solid rgba(220,38,38,0.15)', marginBottom: 16 }}>
                    <div style={{ fontSize: 13, color: '#dc2626', fontWeight: 600, marginBottom: 8 }}>The following will happen:</div>
                    <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                      <li>Template and all items will be permanently deleted</li>
                      {deletePreview.will_delete_count > 0 && (
                        <li style={{ color: '#dc2626', fontWeight: 600 }}>{deletePreview.will_delete_count} pending work order{deletePreview.will_delete_count !== 1 ? 's' : ''} (not time confirmed) will be deleted</li>
                      )}
                      {deletePreview.completed_wo_count > 0 && (
                        <li>{deletePreview.completed_wo_count} completed work order{deletePreview.completed_wo_count !== 1 ? 's' : ''} will be kept but unlinked from this template</li>
                      )}
                      <li>Version history will be deleted</li>
                    </ul>
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button onClick={() => { setShowDeleteConfirm(false); setDeletePreview(null); }}
                      style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>Cancel</button>
                    <button onClick={confirmDeleteTaskTemplate}
                      style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                      Delete Permanently
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Detail/History Modal */}
          <AnimatePresence>
            {showTaskTplDetail && taskTplDetail && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
                  style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 620, border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)', maxHeight: '90vh', overflowY: 'auto' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{taskTplDetail.name}</h3>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{taskTplDetail.tskl_number}/{String((taskTplDetail.version || 1) + 9).padStart(4, '0')}</div>
                    </div>
                    <button onClick={() => setShowTaskTplDetail(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><FiX size={20} /></button>
                  </div>
                  {taskTplDetail.description && <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12, padding: '8px 12px', background: 'var(--bg-input)', borderRadius: 8 }}>{taskTplDetail.description}</p>}
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                    {taskTplDetail.planned_hours > 0 && <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 5, background: 'rgba(16,185,129,0.1)', color: '#059669' }}>{(() => { const s = Math.round(parseFloat(taskTplDetail.planned_hours) * 4) / 4; const h = Math.floor(s); const m = Math.round((s - h) * 60); return m > 0 ? `${h}h${m}m` : `${h}h`; })()}</span>}
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 5, background: 'rgba(139,92,246,0.1)', color: '#7c3aed' }}>{taskTplDetail.planned_resources} resource{taskTplDetail.planned_resources !== 1 ? 's' : ''}</span>
                  </div>

                  {/* Operations & Task Items */}
                  <div style={{ marginBottom: 16 }}>
                    {(taskTplDetail.operations?.length > 0 ? taskTplDetail.operations : [{ operation_number: '0010', title: 'General', priority: 'Normal' }]).map((op, opIdx) => {
                      const opNum = op.operation_number || String((opIdx + 1) * 10).padStart(4, '0');
                      const opItems = (taskTplDetail.items || []).filter(item => item.operation_number === opNum || (!item.operation_number && opIdx === 0));
                      const sections = {};
                      opItems.forEach(item => { const s = item.section || 'General'; if (!sections[s]) sections[s] = []; sections[s].push(item); });
                      return (
                        <div key={opIdx} style={{ marginBottom: opIdx < (taskTplDetail.operations?.length || 1) - 1 ? 12 : 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{opNum} — {op.title || 'General'}</span>
                            <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 600, background: op.priority === 'Push' ? 'rgba(245,158,11,0.1)' : 'rgba(37,99,235,0.1)', color: op.priority === 'Push' ? '#f59e0b' : '#2563eb' }}>{op.priority || 'Normal'}</span>
                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{opItems.length} item{opItems.length !== 1 ? 's' : ''}</span>
                          </div>
                          {Object.entries(sections).map(([secName, secItems]) => (
                            <div key={secName} style={{ marginBottom: 6 }}>
                              {Object.keys(sections).length > 1 && <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 3, paddingLeft: 4 }}>{secName}</div>}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                {secItems.map((item, idx) => (
                                  <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 6, background: 'var(--bg-input)', flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, minWidth: 20 }}>{idx + 1}.</span>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>{item.label}</span>
                                      {item.description && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{item.description}</div>}
                                    </div>
                                    <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'rgba(16,185,129,0.08)', color: '#059669', fontWeight: 600, border: '1px solid rgba(16,185,129,0.15)' }}>Pass/Fail/N/A</span>
                                    {item.required && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'rgba(220,38,38,0.08)', color: '#dc2626', fontWeight: 600, border: '1px solid rgba(220,38,38,0.15)', display: 'inline-flex', alignItems: 'center', gap: 3 }}><span style={{ width: 5, height: 5, borderRadius: '50%', background: '#dc2626', flexShrink: 0 }} />Task Required</span>}
                                    {item.require_photo && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'rgba(37,99,235,0.08)', color: '#f59e0b', fontWeight: 600, border: '1px solid rgba(245,158,11,0.15)', display: 'inline-flex', alignItems: 'center', gap: 3 }}><span style={{ width: 5, height: 5, borderRadius: '50%', background: '#2563eb', flexShrink: 0 }} />Photo Required</span>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>

                  {/* Associated Work Orders */}
                  {taskTplDetail.workOrders?.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>Associated Work Orders ({taskTplDetail.workOrders.length})</div>
                      <div style={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {taskTplDetail.workOrders.map(wo => (
                          <div key={wo.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 6, background: 'var(--bg-input)', fontSize: 12 }}>
                            <span style={{ fontWeight: 700, color: 'var(--text-primary)', minWidth: 80 }}>{wo.wo_number}</span>
                            <span style={{ flex: 1, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{wo.name}</span>
                            <span style={{ padding: '2px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: (wo.status === 'Complete' || wo.status === 'TECO') ? 'rgba(16,185,129,0.1)' : wo.status === 'In Progress' ? 'rgba(59,130,246,0.1)' : 'rgba(107,114,128,0.1)', color: (wo.status === 'Complete' || wo.status === 'TECO') ? '#059669' : wo.status === 'In Progress' ? '#2563eb' : '#6b7280' }}>{wo.status}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Version History */}
                  {taskTplDetail.history?.length > 0 && (
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>Version History ({taskTplDetail.history.length})</div>
                      <div style={{ maxHeight: 400, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {taskTplDetail.history.map(h => {
                          const snap = h.snapshot ? (typeof h.snapshot === 'string' ? (() => { try { return JSON.parse(h.snapshot); } catch { return null; } })() : h.snapshot) : null;
                          const itemChanges = snap?.itemChanges || [];
                          return (
                            <div key={h.id} style={{ borderRadius: 8, background: 'var(--bg-input)', borderLeft: `3px solid ${h.change_type === 'created' ? '#10b981' : '#2563eb'}`, overflow: 'hidden' }}>
                              <div style={{ padding: '10px 12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, marginBottom: 4 }}>
                                  <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 13 }}>{taskTplDetail.tskl_number}/{String((h.version || 1) + 9).padStart(4, '0')}</span>
                                  <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: h.change_type === 'created' ? 'rgba(16,185,129,0.1)' : 'rgba(37,99,235,0.1)', color: h.change_type === 'created' ? '#10b981' : '#2563eb' }}>{h.change_type === 'created' ? 'Created' : 'Updated'}</span>
                                  <span style={{ color: 'var(--text-muted)', marginLeft: 'auto', fontSize: 11 }}>{h.changed_by_name} &bull; {new Date(h.created_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                </div>
                                {/* Item-level changes */}
                                {itemChanges.length > 0 && (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 6 }}>
                                    {itemChanges.map((c, ci) => (
                                      <div key={ci} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 11, padding: '3px 8px', borderRadius: 4, background: c.type === 'added' ? 'rgba(16,185,129,0.06)' : c.type === 'removed' ? 'rgba(239,68,68,0.06)' : 'rgba(245,158,11,0.06)' }}>
                                        <span style={{ fontWeight: 700, color: c.type === 'added' ? '#10b981' : c.type === 'removed' ? '#ef4444' : '#f59e0b', flexShrink: 0 }}>{c.type === 'added' ? '+' : c.type === 'removed' ? '−' : '~'}</span>
                                        <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{c.label}</span>
                                        {c.details && <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>({c.details})</span>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {/* Non-item changes */}
                                {h.change_summary && (() => {
                                  const parts = h.change_summary.split('; ').filter(p => !p.startsWith('+ ') && !p.startsWith('- ') && !p.startsWith('~ '));
                                  return parts.length > 0 ? (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: itemChanges.length > 0 ? 6 : 2 }}>
                                      {parts.map((p, pi) => (
                                        <span key={pi} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: 'rgba(100,116,139,0.08)', color: 'var(--text-secondary)', fontWeight: 500 }}>{p}</span>
                                      ))}
                                    </div>
                                  ) : null;
                                })()}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>PM02 Templates</h2>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={async () => {
                const r = await authFetch('/api/issues/pm02/notifications/pending-count');
                const data = r.ok ? await r.json() : { count: 0 };
                if (data.count === 0) {
                  showToast('No Pending PM02 notifications to clear');
                  return;
                }
                setConfirmDialog({
                  title: 'Clear Pending PM02 Notifications',
                  subtitle: `This will permanently delete all ${data.count} Pending PM02 notification${data.count === 1 ? '' : 's'} awaiting supervisor approval. Approved/active work orders are not affected. Use this to wipe a bad backfill before re-running.`,
                  rows: [],
                  confirmLabel: `Delete ${data.count}`,
                  confirmColor: '#dc2626',
                  onConfirm: async () => {
                    const res = await authFetch('/api/issues/pm02/notifications/clear-pending', { method: 'POST' });
                    if (res.ok) {
                      const d = await res.json();
                      setConfirmDialog(null);
                      showToast(`Deleted ${d.deleted} Pending PM02 notification${d.deleted === 1 ? '' : 's'}`);
                      loadTaskListsTab();
                    } else {
                      showToast('Clear failed', 'error');
                    }
                  }
                });
              }} style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px',
                borderRadius: 10, border: '1px solid rgba(220,38,38,0.4)', background: 'rgba(220,38,38,0.08)', color: '#dc2626',
                cursor: 'pointer', fontSize: 13, fontWeight: 600
              }}><FiTrash2 size={14} /> Clear Pending</button>
              <button onClick={async () => {
                // Preview first — nothing is created until the admin confirms
                // the exact list.
                let preview = null;
                try {
                  const r = await authFetch('/api/issues/pm02/backfill-renewals?preview=1', { method: 'POST' });
                  if (r.ok) preview = await r.json();
                } catch { /* handled below */ }
                if (!preview) { showToast('Could not check PM02 renewals', 'error'); return; }
                const toCreate = preview.would_create || [];
                if (toCreate.length === 0) {
                  showToast(`Nothing to backfill — every PM02 already has a pending notification or open work order (${preview.scanned} checked)`);
                  return;
                }
                setConfirmDialog({
                  title: `Backfill ${toCreate.length} PM02 Renewal${toCreate.length === 1 ? '' : 's'}?`,
                  subtitle: `These PM02s have no pending notification and no open work order, so a new Pending notification will be created for each (due on the last TECO date plus the frequency). ${(preview.would_skip || []).length} others already have their next cycle and will be skipped.`,
                  rows: toCreate.slice(0, 10).map(j => ({ label: j.wo_number || '—', value: j.name })),
                  confirmLabel: toCreate.length > 10 ? `Create ${toCreate.length} (showing first 10)` : `Create ${toCreate.length}`,
                  confirmColor: '#2563eb',
                  onConfirm: async () => {
                    const res = await authFetch('/api/issues/pm02/backfill-renewals', { method: 'POST' });
                    if (res.ok) {
                      const data = await res.json();
                      setConfirmDialog(null);
                      loadTaskListsTab();
                      if (data.created > 0 && Array.isArray(data.created_ids) && data.created_ids.length > 0) {
                        // Surface the created notifications immediately with
                        // an Undo path while the IDs are fresh. Once a
                        // supervisor approves any into real WOs, the undo
                        // endpoint silently skips them.
                        setConfirmDialog({
                          title: 'Backfill complete',
                          subtitle: `Created ${data.created} new PM02 notification${data.created === 1 ? '' : 's'} (${data.skipped} skipped, ${data.scanned} scanned). Review them in the supervisor panel — or undo the whole batch if anything looks wrong.`,
                          rows: (data.created_jobs || []).slice(0, 8).map(j => ({ label: j.wo_number || '—', value: `${j.name} · due ${j.due_date}` })),
                          confirmLabel: (data.created_jobs || []).length > 8 ? `Undo all ${data.created} (showing first 8)` : 'Undo all',
                          confirmColor: '#dc2626',
                          onConfirm: async () => {
                            const r = await authFetch('/api/issues/pm02/notifications/bulk-delete', {
                              method: 'POST',
                              body: JSON.stringify({ ids: data.created_ids })
                            });
                            if (r.ok) {
                              const d = await r.json();
                              setConfirmDialog(null);
                              showToast(`Removed ${d.deleted} of ${d.requested} notifications`);
                              loadTaskListsTab();
                            } else {
                              showToast('Undo failed', 'error');
                            }
                          }
                        });
                      } else {
                        showToast(`Backfill complete — 0 created, ${data.skipped} skipped (${data.scanned} scanned)`);
                      }
                    } else {
                      const err = await res.json().catch(() => ({}));
                      showToast(err.error || 'Backfill failed', 'error');
                    }
                  }
                });
              }} style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px',
                borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)',
                cursor: 'pointer', fontSize: 13, fontWeight: 600
              }}><FiRefreshCw size={14} /> Backfill Renewals</button>
              <button onClick={async () => {
                // Preview first so the admin can see what will change.
                let preview = null;
                try {
                  const r = await authFetch('/api/issues/pm02/rebase-successors?preview=1', { method: 'POST' });
                  if (!r.ok) { const e = await r.json().catch(() => ({})); showToast(e.error || 'Preview failed', 'error'); return; }
                  preview = await r.json();
                } catch { showToast('Preview failed', 'error'); return; }
                const changes = preview.changes || [];
                const skipped = preview.skipped || [];
                // Log skipped rows to console so the admin can eyeball
                // why a specific WO wasn't picked up.
                if (skipped.length > 0) {
                  console.log('[Rebase PM02] skipped rows:', skipped);
                }
                if (changes.length === 0) {
                  const msg = skipped.length > 0
                    ? `No changes needed. ${skipped.length} predecessor${skipped.length === 1 ? '' : 's'} scanned — see console for reasons.`
                    : 'All PM02 successor due dates are correct.';
                  showToast(msg);
                  return;
                }
                setConfirmDialog({
                  title: `Rebase ${changes.length} PM02 due date${changes.length === 1 ? '' : 's'}?`,
                  subtitle: `Repairs PM02 work orders whose predecessor was TECO'd in the last ${preview.recency_days || 30} days and whose next-scheduled due date is earlier than it should be (bug fixed in v2.6+: auto-schedule used to fire on final time-confirmation instead of TECO). Only successors that are New or In Progress with no time confirmations are eligible, and only ones whose current due sits before the correct date. Older cycles are left alone. Operation planned_dates that matched the old due_date are shifted too.${skipped.length > 0 ? ` (${skipped.length} row${skipped.length === 1 ? '' : 's'} skipped — see browser console.)` : ''}`,
                  rows: changes.slice(0, 8).map(c => ({
                    label: c.successor_wo || `#${c.successor_id}`,
                    value: `${c.successor_name} · ${c.current_due || '(no date)'} → ${c.proposed_due}  (from ${c.predecessor_wo})`
                  })),
                  confirmLabel: changes.length > 8 ? `Apply all ${changes.length} (showing first 8)` : `Apply all ${changes.length}`,
                  confirmColor: '#2563eb',
                  onConfirm: async () => {
                    const r = await authFetch('/api/issues/pm02/rebase-successors', { method: 'POST' });
                    if (r.ok) {
                      const d = await r.json();
                      setConfirmDialog(null);
                      showToast(`Rebased ${d.updated} PM02 due date${d.updated === 1 ? '' : 's'}`);
                      loadTaskListsTab();
                    } else {
                      const e = await r.json().catch(() => ({}));
                      showToast(e.error || 'Rebase failed', 'error');
                    }
                  }
                });
              }} style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px',
                borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)',
                cursor: 'pointer', fontSize: 13, fontWeight: 600
              }}><FiCalendar size={14} /> Rebase PM02 Dates</button>
              <button onClick={() => {
                setEditTaskTpl(null);
                setTaskTplForm({ name: '', description: '', schedule_frequency: '', sequence_mode: false, operations: [{ _id: tplOpIdRef.current++, title: '', priority: 'Normal', sections: [{ name: 'General', items: [] }] }] });
                setActiveSectionIdx(0);
                setTaskTplStep(1);
                setShowTaskTplModal(true);
              }} style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px',
                borderRadius: 10, border: 'none', background: '#2563eb', color: '#fff',
                cursor: 'pointer', fontSize: 13, fontWeight: 600
              }}><FiPlus size={14} /> Create Template</button>
            </div>
          </div>

          {taskTemplates.length > 0 && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <FiSearch size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input value={tplSearch} onChange={e => setTplSearch(e.target.value)} placeholder="Search templates..."
                  style={{ width: '100%', padding: '10px 14px 10px 34px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <button onClick={() => setTplFilterNoAssets(f => !f)}
                style={{ padding: '10px 14px', borderRadius: 8, border: `1px solid ${tplFilterNoAssets ? '#2563eb' : 'var(--border)'}`, background: tplFilterNoAssets ? 'rgba(37,99,235,0.08)' : 'var(--bg-input)', color: tplFilterNoAssets ? '#2563eb' : 'var(--text-muted)', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s' }}>
                No Assets
              </button>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {taskTemplates.length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 14 }}>No PM02 templates yet. Create one to get started.</div>
            )}
            {taskTemplates.filter(tpl => { if (tplFilterNoAssets && (tpl.asset_count || 0) > 0) return false; if (!tplSearch.trim()) return true; const q = tplSearch.toLowerCase(); return (tpl.name || '').toLowerCase().includes(q) || (tpl.description || '').toLowerCase().includes(q) || (tpl.tskl_number || '').toLowerCase().includes(q) || (tpl.created_by_name || '').toLowerCase().includes(q); }).map(tpl => (
              <div key={tpl.id} style={{ background: expandedTplId === tpl.id ? 'var(--bg-card)' : 'var(--bg-card)', borderRadius: 12, border: expandedTplId === tpl.id ? '2px solid #2563eb' : '1px solid var(--border)', overflow: 'hidden', boxShadow: expandedTplId === tpl.id ? '0 0 0 3px rgba(37,99,235,0.15)' : 'none', transition: 'border 0.2s, box-shadow 0.2s' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 16, cursor: 'pointer', background: expandedTplId === tpl.id ? 'rgba(37,99,235,0.04)' : 'transparent' }}
                  onClick={() => { playClick(); toggleExpandTpl(tpl.id); }}>
                  <FiChevronRight size={16} style={{ color: 'var(--text-muted)', transition: 'transform 0.2s', transform: expandedTplId === tpl.id ? 'rotate(90deg)' : 'none', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{tpl.name}</span>
                      {tpl.checklist_ref && <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}> - {tpl.checklist_ref}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 10, marginTop: 6, fontSize: 12, color: 'var(--text-muted)', flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ fontSize: 10, fontWeight: 600 }}>{tpl.tskl_number}/{String((tpl.version || 1) + 9).padStart(4, '0')}</span>
                      <span>{tpl.item_count} task items</span>
                      <span style={{ fontWeight: 600, color: tpl.asset_count > 0 ? '#10b981' : 'var(--text-muted)' }}>{tpl.asset_count || 0} asset{tpl.asset_count !== 1 ? 's' : ''}</span>
                      {tpl.wo_count > 0 && <span>{tpl.wo_count} work order{tpl.wo_count !== 1 ? 's' : ''}</span>}
                      {tpl.created_by_name && <span>Created by {tpl.created_by_name}</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                    <button onClick={async () => {
                      const res = await authFetch(`/api/issues/task-list-templates/${tpl.id}`);
                      if (res.ok) {
                        const data = await res.json();
                        setEditTaskTpl({ ...tpl, version: data.version, tskl_number: data.tskl_number || tpl.tskl_number });
                        // Load operations and distribute items into per-operation sections
                        const allItems = data.items || [];
                        const hasOpNumbers = allItems.some(item => item.operation_number);
                        const loadedOps = (data.operations && data.operations.length > 0)
                          ? data.operations.map((o, i) => {
                              const opNum = String((i + 1) * 10).padStart(4, '0');
                              // If no items have operation_number, assign all items to the first operation
                              const opItems = hasOpNumbers
                                ? allItems.filter(item => item.operation_number === opNum)
                                : (i === 0 ? allItems : []);
                              const sectionMap = new Map();
                              for (const item of opItems) {
                                const sName = item.section || 'General';
                                if (!sectionMap.has(sName)) sectionMap.set(sName, []);
                                sectionMap.get(sName).push({ label: item.label, description: item.description || '', item_type: item.item_type, options: item.options || [], required: !!item.required, allow_photo: !!item.allow_photo, require_photo: !!item.require_photo });
                              }
                              const sections = sectionMap.size > 0 ? Array.from(sectionMap.entries()).map(([name, items]) => ({ name, items })) : [{ name: 'General', items: [] }];
                              return { _id: tplOpIdRef.current++, title: o.title || '', priority: o.priority || 'Normal', sections: sections.map(s => ({ ...s, items: s.items.map(it => ({ ...it, _id: tplItemIdRef.current++ })) })) };
                            })
                          : (() => {
                              // No operations — build a single operation from all items
                              const sectionMap = new Map();
                              for (const item of allItems) {
                                const sName = item.section || 'General';
                                if (!sectionMap.has(sName)) sectionMap.set(sName, []);
                                sectionMap.get(sName).push({ _id: tplItemIdRef.current++, label: item.label, description: item.description || '', item_type: item.item_type, options: item.options || [], required: !!item.required, allow_photo: !!item.allow_photo, require_photo: !!item.require_photo });
                              }
                              const sections = sectionMap.size > 0 ? Array.from(sectionMap.entries()).map(([name, items]) => ({ name, items })) : [{ name: 'General', items: [] }];
                              return [{ _id: tplOpIdRef.current++, title: '', priority: 'Normal', sections }];
                            })();
                        setTaskTplForm({
                          name: data.name || '', description: data.description || '', schedule_frequency: data.schedule_frequency || '',
                          sequence_mode: !!data.sequence_mode,
                          operations: loadedOps
                        });
                        setActiveSectionIdx(0);
                        setTaskTplStep(1);
                        setShowTaskTplModal(true);
                      }
                    }} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <FiEdit2 size={12} /> Edit
                    </button>
                    <button onClick={() => loadTaskListDetail(tpl)}
                      style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <FiClock size={12} /> History
                    </button>
                    <button onClick={() => { if ((tpl.asset_count || 0) > 0) { showToast('Cannot delete template with assigned assets. Remove all assets first.', 'error'); return; } initiateDeleteTaskTemplate(tpl); }}
                      title={(tpl.asset_count || 0) > 0 ? 'Cannot delete: has assigned assets' : 'Delete template'}
                      style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid ' + ((tpl.asset_count || 0) > 0 ? 'var(--border)' : 'var(--border)'), background: (tpl.asset_count || 0) > 0 ? 'var(--bg-secondary)' : 'var(--bg-card)', cursor: (tpl.asset_count || 0) > 0 ? 'not-allowed' : 'pointer', color: (tpl.asset_count || 0) > 0 ? 'var(--text-muted)' : '#dc2626', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, opacity: (tpl.asset_count || 0) > 0 ? 0.5 : 1 }}>
                      <FiTrash2 size={12} /> Delete
                    </button>
                  </div>
                </div>

                {/* Expanded: Asset list */}
                <AnimatePresence>
                  {expandedTplId === tpl.id && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                      ref={el => { if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 300); }}
                      style={{ borderTop: '1px solid var(--border)', background: 'var(--bg-input)', overflow: 'hidden' }}>
                      <div style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)' }}>Assigned Assets</span>
                          <button onClick={async () => {
                            setAddAssetTplId(tpl.id);
                            setAddAssetSearch(''); setAddAssetResults([]); setAddAssetLabel('');
                            setAddAssetOps([]);
                            // Refresh assigned assets to ensure filter is current
                            loadTplAssets(tpl.id);
                            const r = await authFetch(`/api/issues/task-list-templates/${tpl.id}`);
                            if (r.ok) {
                              const d = await r.json();
                              const ops = (d.operations || []).map((o, i) => ({ number: o.operation_number || String((i + 1) * 10).padStart(4, '0'), title: o.title || `Operation ${i + 1}` }));
                              setAddAssetOps(ops);
                              const perOp = {};
                              ops.forEach(o => { perOp[o.number] = { planned_hours: 0, planned_minutes: 0, planned_resources: 1 }; });
                              setAddAssetForm({ asset_id: '', schedule_frequency: '', start_date: '', op_hours: perOp });
                            }
                            setShowAddAssetModal(true);
                          }} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 12px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                            <FiPlus size={11} /> Add Asset
                          </button>
                        </div>
                        {tplAssetsLoading ? (
                          <div style={{ textAlign: 'center', padding: 16, fontSize: 12, color: 'var(--text-muted)' }}>Loading...</div>
                        ) : tplAssets.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: 20, fontSize: 12, color: 'var(--text-muted)' }}>No assets assigned yet. Add an asset to start scheduling PM02 work orders.</div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {tplAssets.map(ta => (
                              <div key={ta.id} onClick={() => { playClick(); setSelectedAssetId(selectedAssetId === ta.asset_id ? null : ta.asset_id); }}
                                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', background: selectedAssetId === ta.asset_id ? 'rgba(37,99,235,0.06)' : 'var(--bg-card)', border: selectedAssetId === ta.asset_id ? '2px solid #2563eb' : '1px solid var(--border)', boxShadow: selectedAssetId === ta.asset_id ? '0 0 0 2px rgba(37,99,235,0.12)' : 'none', transition: 'all 0.15s' }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                                    {ta.asset_tag && <span style={{ fontWeight: 700, color: '#2563eb', marginRight: 6 }}>{ta.asset_tag}</span>}
                                    {ta.asset_name || 'Unnamed Asset'}
                                  </div>
                                  {ta.legacy_wo_number && (
                                    <div style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600, marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                                      <FiLink2 size={10} /> Legacy: {ta.legacy_wo_number}
                                      <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 5px', borderRadius: 3, color: '#fff',
                                        background: ta.legacy_wo_teco_status === 'completed' ? '#059669' : ta.legacy_wo_status === 'Complete' ? '#10b981' : ta.legacy_wo_status === 'In Progress' ? '#2563eb' : '#6b7280'
                                      }}>{ta.legacy_wo_teco_status === 'completed' ? 'TECO' : ta.legacy_wo_status || 'Unknown'}</span>
                                    </div>
                                  )}
                                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    <span style={{ color: '#2563eb', fontWeight: 600 }}>{ta.schedule_frequency}</span>
                                    {(() => {
                                      const opH = ta.op_hours_json ? (typeof ta.op_hours_json === 'string' ? JSON.parse(ta.op_hours_json) : ta.op_hours_json) : null;
                                      if (opH && Object.keys(opH).length > 0) {
                                        return Object.keys(opH).sort().map(k => {
                                          const o = opH[k];
                                          const h = parseFloat(o.planned_hours) || 0;
                                          const hrs = Math.floor(h);
                                          const mins = Math.round((h % 1) * 60);
                                          const timeStr = mins > 0 ? `${hrs}h${mins}m` : `${hrs}h`;
                                          return <span key={k}>{k}: {timeStr} / {o.planned_resources || 1}r</span>;
                                        });
                                      }
                                      return ta.planned_hours > 0 ? <span>{(() => { const s = Math.round(parseFloat(ta.planned_hours) * 4) / 4; const h = Math.floor(s); const m = Math.round((s - h) * 60); return m > 0 ? `${h}h${m}m` : `${h}h`; })()} / {ta.planned_resources}r</span> : null;
                                    })()}
                                    {ta.start_date && <span>Start: {new Date(ta.start_date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}</span>}
                                    {(ta.next_notif_due || ta.next_wo_due) ? (() => {
                                      const due = ta.next_notif_due || ta.next_wo_due;
                                      const label = ta.next_notif_due ? (ta.next_notif_status === 'Pending' ? 'Next (Pending)' : 'Next (Approved)') : 'Next (Active WO)';
                                      const dateStr = new Date(due + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
                                      if (!ta.next_notif_due && ta.next_wo_number) {
                                        return <span onClick={() => navigate(`/work-orders/${ta.next_wo_number}`)} style={{ color: '#10b981', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline dotted' }} title={`Open work order ${ta.next_wo_number}`}>{label}: {dateStr}</span>;
                                      }
                                      return <span style={{ color: '#10b981', fontWeight: 600 }}>{label}: {dateStr}</span>;
                                    })() : (
                                      <span style={{ color: '#dc2626', fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.2)', fontSize: 10 }}>NO NEXT DUE</span>
                                    )}
                                  </div>
                                </div>
                                <div style={{ display: 'flex', gap: 4 }}>
                                  {!ta.next_notif_due && !ta.next_wo_due && (
                                    <button onClick={() => handleRenewAsset(tpl.id, ta)}
                                      title="No active WO or notification — click to recreate based on last completion + frequency"
                                      style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #2563eb', background: 'rgba(37,99,235,0.06)', cursor: 'pointer', color: '#2563eb', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                                      <FiRepeat size={12} /> Recreate WO
                                    </button>
                                  )}
                                  <button onClick={() => openLinkWoModal(tpl.id, ta)}
                                    title="Link existing legacy work order"
                                    style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #f59e0b', background: 'rgba(245,158,11,0.06)', cursor: 'pointer', color: '#f59e0b', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <FiLink2 size={12} /> Link
                                  </button>
                                  <button onClick={() => openEditAssetModal(tpl.id, ta)}
                                    title="Edit operation hours"
                                    style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <FiEdit2 size={12} /> Edit
                                  </button>
                                  <button onClick={() => openAssetHistory(tpl.id, ta)}
                                    title="View change history"
                                    style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <FiClock size={12} /> History
                                  </button>
                                  <button onClick={() => handleRemoveAsset(tpl.id, ta.asset_id, ta.asset_name || ta.asset_tag, tpl.name)}
                                    title="Remove asset"
                                    style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', color: '#dc2626', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <FiTrash2 size={12} /> Delete
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

          {/* Add Asset to Template Modal */}
          <AnimatePresence>
            {showAddAssetModal && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
                  style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Add Asset to Template</h3>
                    <button onClick={() => setShowAddAssetModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {/* Asset search */}
                    <div style={{ position: 'relative' }}>
                      <label style={settingsLabel}>Asset *</label>
                      {addAssetForm.asset_id ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)' }}>
                          <span style={{ flex: 1, fontSize: 13, color: 'var(--text-primary)' }}>{addAssetLabel}</span>
                          <button onClick={() => { setAddAssetForm(f => ({ ...f, asset_id: '' })); setAddAssetLabel(''); setAddAssetSearch(''); }}
                            style={{ padding: '2px 6px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-secondary)', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 11 }}>Clear</button>
                        </div>
                      ) : (
                        <>
                          <input value={addAssetSearch} onChange={e => searchAddAssets(e.target.value)}
                            placeholder="Search by Asset ID, name, or serial..."
                            style={{ ...settingsInput, borderColor: !addAssetForm.asset_id ? 'rgba(220,38,38,0.3)' : undefined }} />
                          {addAssetResults.length > 0 && (
                            <div style={{ position: 'absolute', left: 0, right: 0, top: '100%', zIndex: 20, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', maxHeight: 200, overflowY: 'auto' }}>
                              {addAssetResults.map(a => (
                                <div key={a.id} onClick={() => { setAddAssetForm(f => ({ ...f, asset_id: a.id })); setAddAssetLabel(`${a.tag} — ${a.name || 'Unnamed'}`); setAddAssetSearch(''); setAddAssetResults([]); }}
                                  style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--border)', color: 'var(--text-primary)' }}
                                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-input)'}
                                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                  <strong>{a.tag}</strong> — {a.name || 'Unnamed'}
                                  {a.serial_number && <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>S/N: {a.serial_number}</span>}
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <div>
                      <label style={settingsLabel}>Frequency *</label>
                      <select value={addAssetForm.schedule_frequency} onChange={e => setAddAssetForm(f => ({ ...f, schedule_frequency: e.target.value }))}
                        style={{ ...settingsInput, borderColor: !addAssetForm.schedule_frequency ? 'rgba(220,38,38,0.3)' : undefined }}>
                        <option value="">Select frequency...</option>
                        {VALID_FREQUENCIES.map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={settingsLabel}>Start Date *</label>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4 }}>The first PM02 notification will be created for this date</div>
                      <input type="date" value={addAssetForm.start_date} min={new Date().toISOString().split('T')[0]} onChange={e => setAddAssetForm(f => ({ ...f, start_date: e.target.value }))}
                        style={{ ...settingsInput, borderColor: !addAssetForm.start_date ? 'rgba(220,38,38,0.3)' : undefined }} />
                    </div>
                    {addAssetOps.length > 0 && addAssetForm.op_hours && addAssetOps.map((op, idx) => {
                      const oh = addAssetForm.op_hours[op.number] || { planned_hours: 0, planned_minutes: 0, planned_resources: 1 };
                      const updateOp = (field, val) => setAddAssetForm(f => ({ ...f, op_hours: { ...f.op_hours, [op.number]: { ...f.op_hours[op.number], [field]: val } } }));
                      return (
                        <div key={op.number} style={{ padding: idx > 0 ? '12px 0 0' : 0, borderTop: idx > 0 ? '1px solid var(--border)' : 'none' }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>{op.number} — {op.title}</div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                            <div>
                              <label style={{ ...settingsLabel, fontSize: 10 }}>Hours</label>
                              <select value={oh.planned_hours} onChange={e => updateOp('planned_hours', parseInt(e.target.value))} style={settingsInput}>
                                {Array.from({ length: 13 }, (_, i) => <option key={i} value={i}>{i}h</option>)}
                              </select>
                            </div>
                            <div>
                              <label style={{ ...settingsLabel, fontSize: 10 }}>Minutes</label>
                              <select value={oh.planned_minutes} onChange={e => updateOp('planned_minutes', parseInt(e.target.value))} style={settingsInput}>
                                <option value={0}>0m</option><option value={15}>15m</option><option value={30}>30m</option><option value={45}>45m</option>
                              </select>
                            </div>
                            <div>
                              <label style={{ ...settingsLabel, fontSize: 10 }}>Resources</label>
                              <select value={oh.planned_resources} onChange={e => updateOp('planned_resources', parseInt(e.target.value))} style={settingsInput}>
                                {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}</option>)}
                              </select>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                    <button onClick={() => setShowAddAssetModal(false)}
                      style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>Cancel</button>
                    <button onClick={handleAddAsset}
                      disabled={!addAssetForm.asset_id || !addAssetForm.schedule_frequency || !addAssetForm.start_date || (addAssetOps.length > 0 && addAssetForm.op_hours && addAssetOps.some(op => { const oh = addAssetForm.op_hours[op.number] || {}; return (parseInt(oh.planned_hours) || 0) * 60 + (parseInt(oh.planned_minutes) || 0) < 15; }))}
                      style={(() => { const allTimeOk = addAssetOps.length === 0 || !addAssetForm.op_hours || addAssetOps.every(op => { const oh = addAssetForm.op_hours[op.number] || {}; return (parseInt(oh.planned_hours) || 0) * 60 + (parseInt(oh.planned_minutes) || 0) >= 15; }); const valid = addAssetForm.asset_id && addAssetForm.schedule_frequency && addAssetForm.start_date && allTimeOk; return { padding: '10px 20px', borderRadius: 8, border: 'none', background: valid ? '#10b981' : '#94a3b8', color: '#fff', cursor: valid ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600 }; })()}>
                      Add Asset
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Add Asset Success Dialog */}
          <AnimatePresence>
            {addAssetSuccess && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={() => setAddAssetSuccess(null)}
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <motion.div
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  onClick={e => e.stopPropagation()}
                  style={{ background: 'var(--bg-card)', borderRadius: 20, padding: '40px 32px', width: '90%', maxWidth: 360, textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
                  <motion.div
                    initial={{ scale: 0 }} animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 15, delay: 0.1 }}
                    style={{ width: 72, height: 72, borderRadius: '50%', background: 'linear-gradient(135deg, #10b981, #059669)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', boxShadow: '0 8px 32px rgba(16,185,129,0.4)' }}>
                    <FiCheck size={36} color="#fff" strokeWidth={3} />
                  </motion.div>
                  <motion.h2 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                    style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>
                    Asset Assigned
                  </motion.h2>
                  <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
                    style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, color: '#2563eb' }}>
                    {addAssetSuccess}
                  </motion.p>
                  <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}
                    style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
                    Notification created for supervisor approval
                  </motion.p>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Link Legacy WO Modal */}
          <AnimatePresence>
            {linkWoModal && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={() => { setLinkWoModal(null); setLinkWoSearch(''); }}
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }}
                  onClick={e => e.stopPropagation()}
                  style={{ background: 'var(--bg-card)', borderRadius: 16, width: '100%', maxWidth: 520, border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)', overflow: 'hidden', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Link Legacy Work Order</h3>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{linkWoModal.assetName}</div>
                    </div>
                    <button onClick={() => { setLinkWoModal(null); setLinkWoSearch(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
                  </div>
                  <div style={{ padding: '12px 20px', overflowY: 'auto', flex: 1 }}>
                    {!linkableWosLoading && linkableWos.length > 0 && (
                      <input value={linkWoSearch} onChange={e => setLinkWoSearch(e.target.value)} placeholder="Search by WO number or name..."
                        style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12, outline: 'none', boxSizing: 'border-box', marginBottom: 10 }} />
                    )}
                    {linkableWosLoading ? (
                      <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)', fontSize: 13 }}>Loading...</div>
                    ) : linkableWos.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)', fontSize: 13 }}>No unlinked work orders found for this asset</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {linkableWos.filter(wo => {
                          if (!linkWoSearch.trim()) return true;
                          const q = linkWoSearch.trim().toLowerCase();
                          return (wo.wo_number || '').toLowerCase().includes(q) || (wo.name || '').toLowerCase().includes(q);
                        }).map(wo => (
                          <div key={wo.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)' }}>{wo.wo_number}</span>
                                <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4, color: '#fff',
                                  background: wo.teco_status ? '#059669' : wo.status === 'Complete' ? '#10b981' : wo.status === 'In Progress' ? '#2563eb' : '#6b7280'
                                }}>{wo.teco_status ? 'TECO' : wo.status || 'Unknown'}</span>
                              </div>
                              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{wo.name || 'Untitled'}</div>
                              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1, display: 'flex', gap: 10 }}>
                                {wo.teco_at && <span>TECO: {new Date(wo.teco_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}</span>}
                                {wo.due_date && <span>Due: {new Date(wo.due_date + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}</span>}
                                {wo.created_at && <span>Created: {new Date(wo.created_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}</span>}
                              </div>
                            </div>
                            <button onClick={() => confirmLinkWo(wo.id, wo.wo_number)}
                              style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                              Link
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Edit Asset Operation Hours Modal */}
          <AnimatePresence>
            {editAssetModal && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
                  style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Edit Asset Schedule</h3>
                    <button onClick={() => setEditAssetModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>{editAssetModal.assetName}</div>
                  {editAssetModal.nextDue && (
                    <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(37,99,235,0.06)', border: '1px solid rgba(37,99,235,0.15)', marginBottom: 12, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <FiCalendar size={13} color="#2563eb" />
                      <span style={{ color: 'var(--text-secondary)' }}>Next due:</span>
                      <strong style={{ color: '#2563eb' }}>{new Date(editAssetModal.nextDue + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}</strong>
                      <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: editAssetModal.nextDueStatus === 'Pending' ? 'rgba(245,158,11,0.1)' : 'rgba(16,163,74,0.1)', color: editAssetModal.nextDueStatus === 'Pending' ? '#f59e0b' : '#16a34a', fontWeight: 600 }}>{editAssetModal.nextDueStatus}</span>
                    </div>
                  )}
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ ...settingsLabel, fontSize: 10 }}>Frequency</label>
                    <select value={editAssetModal.schedule_frequency} onChange={e => setEditAssetModal(m => ({ ...m, schedule_frequency: e.target.value }))}
                      style={settingsInput}>
                      <option value="">— Select —</option>
                      {VALID_FREQUENCIES.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {editAssetModal.ops.map((op, idx) => {
                      const oh = editAssetModal.op_hours[op.number] || { planned_hours: 0, planned_minutes: 0, planned_resources: 1 };
                      const updateOp = (field, val) => setEditAssetModal(m => ({ ...m, op_hours: { ...m.op_hours, [op.number]: { ...m.op_hours[op.number], [field]: val } } }));
                      return (
                        <div key={op.number} style={{ padding: idx > 0 ? '12px 0 0' : 0, borderTop: idx > 0 ? '1px solid var(--border)' : 'none' }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>{op.number} — {op.title}</div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                            <div>
                              <label style={{ ...settingsLabel, fontSize: 10 }}>Hours</label>
                              <select value={oh.planned_hours} onChange={e => updateOp('planned_hours', parseInt(e.target.value))} style={settingsInput}>
                                {Array.from({ length: 13 }, (_, i) => <option key={i} value={i}>{i}h</option>)}
                              </select>
                            </div>
                            <div>
                              <label style={{ ...settingsLabel, fontSize: 10 }}>Minutes</label>
                              <select value={oh.planned_minutes} onChange={e => updateOp('planned_minutes', parseInt(e.target.value))} style={settingsInput}>
                                <option value={0}>0m</option><option value={15}>15m</option><option value={30}>30m</option><option value={45}>45m</option>
                              </select>
                            </div>
                            <div>
                              <label style={{ ...settingsLabel, fontSize: 10 }}>Resources</label>
                              <select value={oh.planned_resources} onChange={e => updateOp('planned_resources', parseInt(e.target.value))} style={settingsInput}>
                                {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}</option>)}
                              </select>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                    <button onClick={() => setEditAssetModal(null)}
                      style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>Cancel</button>
                    {(() => {
                      const hasChanges = editAssetModal._initial !== JSON.stringify({ op_hours: editAssetModal.op_hours, schedule_frequency: editAssetModal.schedule_frequency });
                      const allValid = editAssetModal.ops.every(op => { const oh = editAssetModal.op_hours[op.number] || {}; return (parseInt(oh.planned_hours) || 0) * 60 + (parseInt(oh.planned_minutes) || 0) >= 15; });
                      const canSave = hasChanges && allValid && !editAssetSaving;
                      return (
                        <button onClick={handleEditAssetSave} disabled={!canSave}
                          style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: canSave ? '#10b981' : '#94a3b8', color: '#fff', cursor: canSave ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600 }}>
                          {editAssetSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                      );
                    })()}
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Asset Change History Modal */}
          <AnimatePresence>
            {viewAssetHistory && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
                  style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Change History</h3>
                    <button onClick={() => setViewAssetHistory(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>{viewAssetHistory.assetName}</div>
                  {viewAssetHistory.nextDue && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 8, background: 'rgba(37,99,235,0.06)', border: '1px solid rgba(37,99,235,0.15)', marginBottom: 16 }}>
                      <FiCalendar size={14} style={{ color: '#2563eb', flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#2563eb' }}>{viewAssetHistory.nextDueLabel}</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                          {new Date(viewAssetHistory.nextDue).toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
                          {viewAssetHistory.frequency && <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>({viewAssetHistory.frequency})</span>}
                        </div>
                      </div>
                    </div>
                  )}
                  {!editAssetHistory ? (
                    <div style={{ textAlign: 'center', padding: 20, fontSize: 12, color: 'var(--text-muted)' }}>Loading...</div>
                  ) : editAssetHistory.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 20, fontSize: 12, color: 'var(--text-muted)' }}>No changes recorded yet. History will appear here after editing operation hours.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {editAssetHistory.map(h => {
                        const oldVals = h.old_values ? (typeof h.old_values === 'string' ? JSON.parse(h.old_values) : h.old_values) : null;
                        const newVals = h.new_values ? (typeof h.new_values === 'string' ? JSON.parse(h.new_values) : h.new_values) : null;
                        const oldOps = oldVals?.op_hours || {};
                        const newOps = newVals?.op_hours || {};
                        const allKeys = [...new Set([...Object.keys(oldOps), ...Object.keys(newOps)])].sort();
                        return (
                          <div key={h.id} style={{ padding: '10px 12px', borderRadius: 8, background: 'var(--bg-secondary)', border: '1px solid var(--border)', fontSize: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                              <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{h.changed_by_name || 'System'}</span>
                              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{new Date(h.created_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                            {oldVals?.schedule_frequency !== newVals?.schedule_frequency && newVals?.schedule_frequency && (
                              <div style={{ color: 'var(--text-secondary)', marginTop: 2 }}>Frequency: <span style={{ color: '#dc2626', textDecoration: 'line-through' }}>{oldVals?.schedule_frequency || 'none'}</span> → <span style={{ color: '#16a34a', fontWeight: 600 }}>{newVals.schedule_frequency}</span></div>
                            )}
                            {allKeys.map(k => {
                              const oldH = parseFloat(oldOps[k]?.planned_hours) || 0;
                              const newH = parseFloat(newOps[k]?.planned_hours) || 0;
                              const oldR = parseInt(oldOps[k]?.planned_resources) || 1;
                              const newR = parseInt(newOps[k]?.planned_resources) || 1;
                              const oldHrs = Math.floor(oldH); const oldMins = Math.round((oldH % 1) * 60);
                              const newHrs = Math.floor(newH); const newMins = Math.round((newH % 1) * 60);
                              const oldStr = `${oldHrs}h${oldMins > 0 ? oldMins + 'm' : ''} / ${oldR}r`;
                              const newStr = `${newHrs}h${newMins > 0 ? newMins + 'm' : ''} / ${newR}r`;
                              if (oldStr === newStr) return null;
                              return <div key={k} style={{ color: 'var(--text-secondary)', marginTop: 2 }}>{k}: <span style={{ color: '#dc2626', textDecoration: 'line-through' }}>{oldStr}</span> → <span style={{ color: '#16a34a', fontWeight: 600 }}>{newStr}</span></div>;
                            })}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
                    <button onClick={() => setViewAssetHistory(null)}
                      style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>Close</button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}

      {/* Assets Tab */}
      {tab === 'assets' && (
        <>
          {/* Sub-tab pills */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
            {[{ id: 'list', label: 'Assets' }, { id: 'fields', label: 'Custom Fields' }].map(t => (
              <button key={t.id} onClick={() => setAssetsSubTab(t.id)}
                style={{
                  padding: '7px 16px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  border: `1px solid ${assetsSubTab === t.id ? '#2563eb' : 'var(--border)'}`,
                  background: assetsSubTab === t.id ? '#2563eb' : 'var(--bg-card)',
                  color: assetsSubTab === t.id ? '#fff' : 'var(--text-secondary)',
                }}>
                {t.label}
              </button>
            ))}
          </div>

          {assetsSubTab === 'list' && (
          <>
          {/* Stats */}
          {assetStats && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                style={{ background: 'var(--bg-card)', borderRadius: 10, padding: 16, border: '1px solid var(--border)', textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#0ea5e9' }}>{assetStats.total}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500, marginTop: 2 }}>Total Assets</div>
              </motion.div>
            </div>
          )}
          </>
          )}

          {assetsSubTab === 'fields' && (
          <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 20, border: '1px solid var(--border)', marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}><FiSliders size={15} color="#2563eb" /> Custom Fields</h2>
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                onClick={() => { setCfForm({ name: '', field_type: 'text' }); setEditCf(null); setShowCfModal(true); }}
                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                <FiPlus size={12} /> Add Field
              </motion.button>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>Drag to reorder. These fields appear on every asset.</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {customFields.map(f => (
                <div key={f.id} draggable
                  onDragStart={() => setDraggingCfId(f.id)}
                  onDragOver={e => e.preventDefault()}
                  onDrop={() => { reorderCustomField(draggingCfId, f.id); setDraggingCfId(null); }}
                  onDragEnd={() => setDraggingCfId(null)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: draggingCfId === f.id ? 'rgba(37,99,235,0.08)' : 'var(--bg-input)', borderRadius: 8, border: `1px solid ${draggingCfId === f.id ? '#2563eb' : 'var(--border)'}`, cursor: 'grab', transition: 'background 0.15s, border 0.15s' }}>
                  <FiMenu size={12} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{f.name}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', padding: '2px 8px', background: 'rgba(107,114,128,0.08)', borderRadius: 4 }}>{f.field_type}</span>
                  <motion.button whileTap={{ scale: 0.9 }}
                    onClick={() => { setEditCf(f); setCfForm({ name: f.name, field_type: f.field_type }); setShowCfModal(true); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 2 }}><FiEdit2 size={12} /></motion.button>
                  <motion.button whileTap={{ scale: 0.9 }}
                    onClick={() => { setConfirmDialog({ title: 'Delete Custom Field', rows: [{ label: 'Field', value: f.name }], confirmLabel: 'Delete', confirmColor: '#dc2626', onConfirm: async () => { await authFetch(`/api/admin/asset-custom-fields/${f.id}`, { method: 'DELETE' }); safeFetch('/api/admin/asset-custom-fields', setCustomFields); showToast('Custom field deleted'); setConfirmDialog(null); } }); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 2 }}><FiTrash2 size={12} /></motion.button>
                </div>
              ))}
              {customFields.length === 0 && <div style={{ textAlign: 'center', padding: 16, color: 'var(--text-muted)', fontSize: 12 }}>No custom fields yet. Add one to extend asset data.</div>}
            </div>
          </div>
          )}

          {assetsSubTab === 'list' && (
          <>
          {/* Assets Table */}
          <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 20, border: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Assets</h2>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
                <select value={assetStatusFilter} onChange={e => setAssetStatusFilter(e.target.value)}
                  style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 12, outline: 'none', background: 'var(--bg-input)', color: 'var(--text-primary)' }}>
                  <option value="">All Statuses</option>
                  {statuses.map(s => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
                </select>
                <div style={{ position: 'relative', maxWidth: 250 }}>
                  <FiSearch size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                  <input value={assetSearch} onChange={e => setAssetSearch(e.target.value)} placeholder="Search assets..."
                    style={{ padding: '8px 10px 8px 30px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 12, outline: 'none', background: 'var(--bg-input)', color: 'var(--text-primary)', width: 200 }} />
                </div>
                <button onClick={() => setShowRedundantAssets(s => !s)}
                  title={showRedundantAssets ? 'Hide redundant assets' : 'Show redundant assets'}
                  style={{
                    padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    border: `1px solid ${showRedundantAssets ? '#7A7982' : 'var(--border)'}`,
                    background: showRedundantAssets ? 'rgba(122,121,130,0.12)' : 'var(--bg-input)',
                    color: showRedundantAssets ? '#7A7982' : 'var(--text-secondary)',
                    whiteSpace: 'nowrap',
                  }}>
                  {showRedundantAssets ? 'Showing redundant' : 'Show redundant'}
                </button>
                <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setEditAsset(null);
                    if (costCodes.length === 0) authFetch('/api/admin/cost-codes').then(r => r.ok ? r.json() : []).then(d => setCostCodes(Array.isArray(d) ? d : [])).catch(() => {});
                    if (flNodes.length === 0) authFetch('/api/functional-locations').then(r => r.ok ? r.json() : []).then(d => setFlNodes(Array.isArray(d) ? d : [])).catch(() => {});
                    setShowAssetWizard(true);
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                  <FiPlus size={14} /> Create Asset
                </motion.button>
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border)', background: 'var(--bg-input)' }}>
                    {['Asset ID', 'Name', 'Supplier', 'Status', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textAlign: 'left' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {adminAssets
                    .filter(a => (!assetSearch || a.tag?.toLowerCase().includes(assetSearch.toLowerCase()) || a.name?.toLowerCase().includes(assetSearch.toLowerCase())) && (!assetStatusFilter || String(a.status_id) === assetStatusFilter))
                    .map((a, idx) => (
                    <motion.tr key={a.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: idx * 0.02 }}
                      style={{ borderBottom: '1px solid var(--border)', opacity: Number(a.status_id) === 4 ? 0.55 : 1, textDecoration: Number(a.status_id) === 4 ? 'line-through' : 'none' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-input)'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{a.tag}</td>
                      <td style={{ padding: '10px 14px', fontSize: 13, color: 'var(--text-primary)' }}>{a.name}</td>
                      <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-secondary)' }}>{a.supplier_name || '—'}</td>
                      <td style={{ padding: '10px 14px' }}>
                        {a.status_name && <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, color: '#fff', background: a.status_color || '#6b7280' }}>{a.status_name}</span>}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <motion.button whileTap={{ scale: 0.9 }}
                            onClick={() => { setEditAsset(a); setAssetForm({ name: a.name, tag: a.tag || '', status_id: String(a.status_id || '3'), serial_number: a.serial_number || '', notes: a.notes || '', supplier_name: a.supplier_name || '', purchase_date: a.purchase_date || '', warranty_end: a.warranty_end || '', capacity: a.capacity || '', cost_code_id: a.cost_code_id ? String(a.cost_code_id) : '', functional_location_id: a.functional_location_id ? String(a.functional_location_id) : '' }); if (costCodes.length === 0) authFetch('/api/admin/cost-codes').then(r => r.ok ? r.json() : []).then(d => setCostCodes(Array.isArray(d) ? d : [])).catch(() => {}); setShowAssetModal(true); }}
                            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 11 }}>
                            <FiEdit2 size={11} /> Edit
                          </motion.button>
                          <motion.button whileTap={{ scale: 0.9 }}
                            onClick={() => {
                              setConfirmDialog({
                                title: 'Delete asset permanently',
                                subtitle: 'This deletes the asset AND every work order, comment, time confirmation, history row, and file attachment linked to it. This cannot be undone. If you only want to retire the asset, mark it Redundant instead.',
                                rows: [{ label: 'Asset', value: a.tag + (a.name ? ` — ${a.name}` : '') }],
                                confirmLabel: 'Delete forever',
                                confirmColor: '#dc2626',
                                typeToConfirm: 'delete',
                                onConfirm: async () => {
                                  try {
                                    const res = await authFetch(`/api/admin/assets/${a.id}`, { method: 'DELETE' });
                                    const data = await res.json().catch(() => ({}));
                                    if (!res.ok) { showToast(data.error || 'Delete failed'); return; }
                                    loadAssetTab();
                                    showToast(`Asset deleted (${data.deleted_work_orders || 0} work order${data.deleted_work_orders === 1 ? '' : 's'} removed)`);
                                  } finally {
                                    setConfirmDialog(null);
                                  }
                                },
                              });
                            }}
                            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.06)', color: '#ef4444', cursor: 'pointer', fontSize: 11 }}>
                            <FiTrash2 size={11} />
                          </motion.button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
              {adminAssets.length === 0 && <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)', fontSize: 13 }}>No assets yet</div>}
            </div>
          </div>
          </>
          )}
        </>
      )}

      {/* Groups Tab */}

      {/* Licences Tab — embedded Licences page */}
      {tab === 'licences' && <Licences />}

      {/* Cost Centres Tab */}
      {tab === 'costcodes' && (
        <div style={{ background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Cost Centres</h2>
            <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
              onClick={() => {
                setEditCostCode(null);
                setCostCodeForm({ name: '', description: '', allocated_budget: '' });
                authFetch('/api/admin/cost-codes/next').then(r => r.ok ? r.json() : { code: '460001' }).then(d => setNextCostCode(d.code)).catch(() => setNextCostCode('460001'));
                setShowCostCodeModal(true);
              }}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              <FiPlus size={14} /> New Cost Centre
            </motion.button>
          </div>
          {costCodeLoading ? (
            <div style={{ padding: 40, textAlign: 'center' }}>{[...Array(4)].map((_, i) => <div key={i} className="skeleton" style={{ height: 48, borderRadius: 8, marginBottom: 8 }} />)}</div>
          ) : costCodes.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
              <FiDollarSign size={32} style={{ marginBottom: 8, opacity: 0.3 }} />
              <div style={{ fontSize: 14 }}>No cost centres created yet</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>Code</th>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>Name</th>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>Description</th>
                    <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 600, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>Budget</th>
                    <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 600, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>Assets</th>
                    <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 600, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {costCodes.map(cc => (
                    <tr key={cc.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: '12px 16px', fontWeight: 700, fontFamily: 'monospace', fontSize: 14, color: 'var(--accent)' }}>{cc.code}</td>
                      <td style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text-primary)' }}>{cc.name || '-'}</td>
                      <td style={{ padding: '12px 16px', color: 'var(--text-secondary)', fontSize: 12 }}>{cc.description || '-'}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'monospace' }}>{cc.allocated_budget ? `$${Number(cc.allocated_budget).toLocaleString()}` : '-'}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', color: 'var(--text-muted)', fontSize: 12 }}>{cc.asset_count || 0}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button onClick={() => { setEditCostCode(cc); setCostCodeForm({ name: cc.name || '', description: cc.description || '', allocated_budget: cc.allocated_budget || '' }); setNextCostCode(cc.code); setShowCostCodeModal(true); }}
                            style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 11 }}><FiEdit2 size={12} /></button>
                          {(cc.asset_count || 0) === 0 && (
                            <button onClick={async () => {
                              if (!window.confirm(`Delete cost centre ${cc.code}?`)) return;
                              await authFetch(`/api/admin/cost-codes/${cc.id}`, { method: 'DELETE' });
                              setCostCodes(prev => prev.filter(c => c.id !== cc.id));
                            }}
                              style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(220,38,38,0.3)', background: 'rgba(220,38,38,0.04)', cursor: 'pointer', color: '#dc2626', fontSize: 11 }}><FiTrash2 size={12} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Cost Centre Modal */}
      <AnimatePresence>
        {showCostCodeModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 20 }}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 24, width: '100%', maxWidth: 420, border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{editCostCode ? 'Edit Cost Centre' : 'New Cost Centre'}</h3>
                <button onClick={() => setShowCostCodeModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Code</label>
                <div style={{ padding: '10px 14px', borderRadius: 8, background: 'var(--bg-input)', border: '1px solid var(--border)', fontFamily: 'monospace', fontSize: 18, fontWeight: 700, color: 'var(--accent)' }}>
                  {nextCostCode}
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>Auto-generated 6-digit code starting with 46</div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Name *</label>
                <input value={costCodeForm.name || ''} onChange={e => setCostCodeForm(f => ({ ...f, name: e.target.value.replace(/\b\w/g, c => c.toUpperCase()) }))} autoFocus
                  placeholder="e.g. Building A"
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Description</label>
                <input value={costCodeForm.description || ''} onChange={e => {
                  let v = e.target.value;
                  if (v.length === 1) v = v.toUpperCase();
                  else if (v.length > 1) v = v.replace(/(^|[.!?]\s+)([a-z])/g, (m, p, c) => p + c.toUpperCase());
                  setCostCodeForm(f => ({ ...f, description: v }));
                }}
                  placeholder="e.g. Maintenance and upkeep for Building A"
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Allocated Budget</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 14, fontWeight: 600, color: 'var(--text-muted)' }}>$</span>
                  <input type="number" value={costCodeForm.allocated_budget || ''} onChange={e => setCostCodeForm(f => ({ ...f, allocated_budget: e.target.value }))}
                    placeholder="0.00" min="0" step="0.01"
                    style={{ width: '100%', padding: '10px 14px 10px 24px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>Annual budget allocation for this cost centre</div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => setShowCostCodeModal(false)}
                  style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                <button disabled={!(costCodeForm.name || '').trim()} onClick={async () => {
                  try {
                    if (editCostCode) {
                      const res = await authFetch(`/api/admin/cost-codes/${editCostCode.id}`, { method: 'PUT', body: JSON.stringify(costCodeForm) });
                      if (res.ok) { const d = await res.json(); setCostCodes(prev => prev.map(c => c.id === d.id ? { ...c, ...d } : c)); }
                    } else {
                      const res = await authFetch('/api/admin/cost-codes', { method: 'POST', body: JSON.stringify(costCodeForm) });
                      if (res.ok) { const d = await res.json(); setCostCodes(prev => [...prev, d]); }
                    }
                    setShowCostCodeModal(false);
                  } catch {}
                }}
                  style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: (costCodeForm.name || '').trim() ? 1 : 0.5 }}>
                  {editCostCode ? 'Update' : 'Create'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Functional Locations Tab — SAP-style hierarchy builder */}
      {tab === 'flocations' && (() => {
        // Build a child-map once per render so the recursive renderer is cheap.
        // FLs with parent_asset_id set live under an asset, not under another
        // FL — they get their own map (flsByParentAsset) and are excluded
        // from childrenByParent so they don't render twice (or as bogus roots).
        const childrenByParent = new Map();
        const flsByParentAsset = new Map();
        for (const n of flNodes) {
          if (n.parent_asset_id) {
            if (!flsByParentAsset.has(n.parent_asset_id)) flsByParentAsset.set(n.parent_asset_id, []);
            flsByParentAsset.get(n.parent_asset_id).push(n);
            continue;
          }
          const k = n.parent_id == null ? 0 : n.parent_id;
          if (!childrenByParent.has(k)) childrenByParent.set(k, []);
          childrenByParent.get(k).push(n);
        }
        const sortFl = arr => arr.sort((a, b) => (a.sort_order - b.sort_order) || a.code.localeCompare(b.code));
        for (const arr of childrenByParent.values()) sortFl(arr);
        for (const arr of flsByParentAsset.values()) sortFl(arr);

        // Search: when a query is active, match FL nodes by code/name AND
        // assets by tag/name/manufacturer/model. Auto-expand every ancestor
        // (FL parents and parent-asset chains) so the match is visible
        // without manual clicks — makes drag-drop onto a buried target easy.
        const matchIds = new Set();
        const matchedAssetIds = new Set();
        // When search is active, only nodes on a match's ancestor chain
        // (plus matches themselves) render — siblings of expanded parents
        // are hidden so the result list isn't drowned in unrelated rows.
        const visibleFlIds = new Set();
        const visibleAssetIds = new Set();
        const searchActive = !!flSearch.trim();
        // Local-only expansion overlays for the search auto-expand path.
        // We never mutate flExpanded / flAssetExpanded directly — those are
        // React state and mutating them in render would (a) persist the
        // expansion across search-clears (every typed search would leak more
        // and more open branches into the user's actual state) and (b) skip
        // re-render. Anything in flExpanded is the user's real expansion;
        // anything in searchExpandedFls is just for this render.
        const userExpandedFls = flExpanded;
        const userExpandedAssets = flAssetExpanded;
        const searchExpandedFls = new Set();
        const searchExpandedAssets = new Set();
        if (searchActive) {
          const q = flSearch.trim().toLowerCase();
          for (const n of flNodes) {
            if (n.code.toLowerCase().includes(q) || n.name.toLowerCase().includes(q)) matchIds.add(n.id);
          }
          for (const a of flAttachedAssets) {
            const hay = `${a.tag || ''} ${a.name || ''} ${a.manufacturer_name || ''} ${a.model_name || ''}`.toLowerCase();
            if (hay.includes(q)) matchedAssetIds.add(a.id);
          }
          const assetById = new Map(flAttachedAssets.map(a => [a.id, a]));
          // Walk an FL upward, marking ancestors visible and expanding any
          // intermediate asset nodes (for FL-under-asset hops).
          const expandUpFromFl = (flId) => {
            let curFl = flNodes.find(n => n.id === flId);
            while (curFl) {
              visibleFlIds.add(curFl.id);
              if (curFl.parent_id != null) {
                searchExpandedFls.add(curFl.parent_id);
                curFl = flNodes.find(n => n.id === curFl.parent_id);
              } else if (curFl.parent_asset_id) {
                let aid = curFl.parent_asset_id;
                while (aid) {
                  visibleAssetIds.add(aid);
                  searchExpandedAssets.add(aid);
                  const a = assetById.get(aid);
                  if (!a) break;
                  if (a.parent_asset_id) { aid = a.parent_asset_id; continue; }
                  if (a.functional_location_id) { searchExpandedFls.add(a.functional_location_id); curFl = flNodes.find(n => n.id === a.functional_location_id); break; }
                  curFl = null;
                  break;
                }
                if (!aid) break;
              } else {
                break;
              }
            }
          };
          for (const id of matchIds) expandUpFromFl(id);
          for (const aid of matchedAssetIds) {
            visibleAssetIds.add(aid);
            let cur = assetById.get(aid);
            while (cur && cur.parent_asset_id) {
              visibleAssetIds.add(cur.parent_asset_id);
              searchExpandedAssets.add(cur.parent_asset_id);
              cur = assetById.get(cur.parent_asset_id);
            }
            if (cur && cur.functional_location_id) {
              searchExpandedFls.add(cur.functional_location_id);
              expandUpFromFl(cur.functional_location_id);
            }
          }
        }

        const toggleExpand = (id) => setFlExpanded(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });

        // Refresh tree + attached assets after any mutation
        const reloadFl = () => Promise.all([
          authFetch('/api/functional-locations').then(r => r.ok ? r.json() : []).catch(() => []),
          authFetch('/api/assets?limit=2000').then(r => r.ok ? r.json() : { data: [] }).catch(() => ({ data: [] })),
        ]).then(([tree, assetsResp]) => {
          setFlNodes(Array.isArray(tree) ? tree : []);
          setFlAttachedAssets(Array.isArray(assetsResp?.data) ? assetsResp.data : []);
        });

        // Build asset lookups for the renderer:
        //   assetsByFl  → top-level assets per FL (parent_asset_id NULL, or
        //                 parent not present in this dataset) so children
        //                 don't render flat under the FL — they nest below
        //                 their parent asset.
        //   childAssetsByParent → for nesting beneath parent asset rows.
        const allAssetIds = new Set(flAttachedAssets.map(a => a.id));
        const assetsByFl = new Map();
        const childAssetsByParent = new Map();
        for (const a of flAttachedAssets) {
          const parentId = a.parent_asset_id || null;
          if (parentId && allAssetIds.has(parentId)) {
            if (!childAssetsByParent.has(parentId)) childAssetsByParent.set(parentId, []);
            childAssetsByParent.get(parentId).push(a);
          } else {
            const k = a.functional_location_id;
            if (!k) continue;
            if (!assetsByFl.has(k)) assetsByFl.set(k, []);
            assetsByFl.get(k).push(a);
          }
        }
        const sortAssets = arr => arr.sort((a, b) => (a.tag || '').localeCompare(b.tag || '') || (a.name || '').localeCompare(b.name || ''));
        for (const arr of assetsByFl.values()) sortAssets(arr);
        for (const arr of childAssetsByParent.values()) sortAssets(arr);

        // Live duplicate-code check. Case-insensitive. excludeId lets the
        // edit row skip its own row when checking — otherwise renaming a
        // node back to its existing code would falsely flag.
        const codeExists = (code, excludeId) => {
          const k = (code || '').trim().toLowerCase();
          if (!k) return false;
          return flNodes.some(n => n.code.toLowerCase() === k && n.id !== excludeId);
        };

        // Compute the next code under a given parent. Walks the parent's
        // existing children, picks the lowest unused 2-digit suffix, and
        // appends it as "parentCode.NN". Root creates (parentId === null)
        // return empty so the user enters the root code manually.
        const nextCodeForParent = (parentId) => {
          if (parentId == null) return '';
          const parent = flNodes.find(n => n.id === parentId);
          if (!parent) return '';
          const prefix = parent.code + '.';
          const used = new Set();
          // Scan every FL globally — childrenByParent excludes FL-under-asset,
          // so siblings nested under attached assets were invisible here and
          // the auto-generated code would collide with their code (which the
          // global codeExists check then flagged as duplicate).
          for (const fl of flNodes) {
            if (fl.code.startsWith(prefix)) {
              const suffix = fl.code.slice(prefix.length);
              if (/^\d+$/.test(suffix)) used.add(parseInt(suffix, 10));
            }
          }
          let i = 1;
          while (used.has(i)) i++;
          return `${parent.code}.${String(i).padStart(2, '0')}`;
        };

        // Same picker, but the FL is being parented under an ASSET — use the
        // asset's tag as the prefix. Result looks like 'GEN-01.NN'.
        const nextCodeForAssetParent = (parentAssetId) => {
          const parent = flAttachedAssets.find(a => a.id === parentAssetId);
          if (!parent || !parent.tag) return '';
          const prefix = `${parent.tag}.`;
          const used = new Set();
          for (const fl of flNodes) {
            if (fl.code.startsWith(prefix)) {
              const suffix = fl.code.slice(prefix.length);
              if (/^\d+$/.test(suffix)) used.add(parseInt(suffix, 10));
            }
          }
          let i = 1;
          while (used.has(i)) i++;
          return `${parent.tag}.${String(i).padStart(2, '0')}`;
        };

        // Generate a fresh asset tag derived from the new FL code so that an
        // asset moved between FLs follows its new parent's numbering. Format:
        // '<flCode>-NN'. Used after a move; existing tags are only replaced
        // when they already follow the auto-pattern of the OLD parent (so
        // user-customised tags like 'PRA-001' aren't clobbered).
        const nextAssetTagForFl = (flId) => {
          const fl = flNodes.find(n => n.id === flId);
          if (!fl) return '';
          const prefix = `${fl.code}-`;
          const used = new Set();
          for (const a of flAttachedAssets) {
            if (a.tag && a.tag.startsWith(prefix)) {
              const suffix = a.tag.slice(prefix.length);
              if (/^\d+$/.test(suffix)) used.add(parseInt(suffix, 10));
            }
          }
          let i = 1;
          while (used.has(i)) i++;
          return `${fl.code}-${String(i).padStart(2, '0')}`;
        };

        // Was an FL's code auto-derived from a previous FL/asset parent?
        // Match either '<parentCode>.NN' or '<parentTag>.NN'.
        const isAutoFlCode = (code) => /^.+\.\d{2,}$/.test(code || '');
        const isAutoAssetTag = (tag) => /^.+-\d{2,}$/.test(tag || '');

        // After a successful move, rename the moved FL (and optionally an
        // asset) to the next code under its new parent — but only when the
        // existing code was auto-generated. Custom codes are preserved.
        const renameFlAfterMove = async (flId, newCode) => {
          if (!newCode) return;
          await authFetch(`/api/functional-locations/${flId}`, {
            method: 'PUT',
            body: JSON.stringify({ code: newCode })
          });
        };
        const renameAssetAfterMove = async (assetId, newTag) => {
          if (!newTag) return;
          await authFetch(`/api/assets/${assetId}`, {
            method: 'PUT',
            body: JSON.stringify({ tag: newTag })
          });
        };


        // Sum assets across this node and every descendant — drives the
        // "cannot delete if assets attached" rule.
        const subtreeAssetCount = (nodeId) => {
          let total = 0;
          const stack = [nodeId];
          while (stack.length) {
            const id = stack.pop();
            const n = flNodes.find(x => x.id === id);
            if (n) total += (n.asset_count || 0);
            for (const c of (childrenByParent.get(id) || [])) stack.push(c.id);
          }
          return total;
        };

        const addCodeDuplicate = codeExists(flAddForm.code);
        const addCanSubmit = !!flAddForm.code.trim() && !!flAddForm.name.trim() && !addCodeDuplicate;
        const editCodeDuplicate = flEditing ? codeExists(flEditing.code, flEditing.id) : false;
        const editCanSubmit = !!flEditing && !!flEditing.code?.trim() && !!flEditing.name?.trim() && !editCodeDuplicate;

        const handleCreate = async () => {
          if (!addCanSubmit) {
            if (addCodeDuplicate) showToast('That code already exists', 'error');
            else showToast('Code and name required', 'error');
            return;
          }
          const res = await authFetch('/api/functional-locations', {
            method: 'POST',
            body: JSON.stringify({ code: flAddForm.code.trim(), name: flAddForm.name.trim(), parent_id: flAddingUnder })
          });
          if (res.ok) {
            setFlDirty(true);
            showToast('Functional location created');
            setFlAddForm({ code: '', name: '' });
            setFlAddingUnder(undefined);
            await reloadFl();
            if (flAddingUnder != null) setFlExpanded(prev => new Set(prev).add(flAddingUnder));
          } else {
            const e = await res.json().catch(() => ({}));
            showToast(e.error || 'Create failed', 'error');
          }
        };

        const handleSaveEdit = async () => {
          if (!editCanSubmit) {
            if (editCodeDuplicate) showToast('That code already exists', 'error');
            else showToast('Code and name required', 'error');
            return;
          }
          const res = await authFetch(`/api/functional-locations/${flEditing.id}`, {
            method: 'PUT',
            body: JSON.stringify({ code: flEditing.code.trim(), name: flEditing.name.trim() })
          });
          if (res.ok) {
            setFlDirty(true);
            showToast('Functional location updated');
            setFlEditing(null);
            reloadFl();
          } else {
            const e = await res.json().catch(() => ({}));
            showToast(e.error || 'Update failed', 'error');
          }
        };

        // Show a confirm dialog, then attempt server delete. The server is
        // the source of truth for whether assets are still attached — the
        // client cache can be stale right after a move/drag-drop. If the
        // server rejects (HTTP 400 with asset_count), surface its actual
        // count in a follow-up 'cannot delete' dialog.
        const handleDelete = (node) => {
          const localCount = subtreeAssetCount(node.id);
          setConfirmDialog({
            title: 'Delete Functional Location',
            rows: [
              { label: 'Code', value: node.code },
              { label: 'Name', value: node.name },
              { label: 'Child locations', value: String(node.child_count) },
              { label: 'Assets in subtree (cached)', value: String(localCount) },
            ],
            subtitle: localCount > 0
              ? `${localCount} asset${localCount === 1 ? '' : 's'} appear${localCount === 1 ? 's' : ''} attached in our cache — re-assign them first if so. The server has the final say and will refuse the delete if any are still attached.`
              : (node.child_count > 0
                ? 'This will also delete every functional location beneath it.'
                : 'This will permanently delete the location.'),
            confirmLabel: 'Delete',
            confirmColor: '#dc2626',
            onConfirm: async () => {
              const res = await authFetch(`/api/functional-locations/${node.id}`, { method: 'DELETE' });
              if (res.ok) {
                setConfirmDialog(null);
                setFlDirty(true);
                showToast('Deleted');
                reloadFl();
                if (flRollup?.node?.id === node.id) setFlRollup(null);
                return;
              }
              const er = await res.json().catch(() => ({}));
              // Server returns asset_count when blocked by attached assets.
              if (typeof er.asset_count === 'number' && er.asset_count > 0) {
                setConfirmDialog({
                  title: 'Cannot Delete',
                  rows: [
                    { label: 'Code', value: node.code },
                    { label: 'Name', value: node.name },
                    { label: 'Assets still attached', value: String(er.asset_count) },
                  ],
                  subtitle: `The server found ${er.asset_count} asset${er.asset_count === 1 ? '' : 's'} attached anywhere in this subtree. Re-assign or remove them, then try again.`,
                  confirmLabel: 'OK', confirmColor: '#64748b', hideCancel: true,
                  onConfirm: () => { setConfirmDialog(null); reloadFl(); },
                });
              } else {
                showToast(er.error || 'Delete failed', 'error');
              }
            }
          });
        };

        // Native HTML5 drag-and-drop: drag a node onto another node to set it
        // as the drop target's child. We don't support drop-into-gap reorder
        // in v1; that can land later if needed.
        const handleDrop = async (draggedId, targetParentId) => {
          if (draggedId === targetParentId) return;
          const dragged = flNodes.find(n => n.id === draggedId);
          // Auto-renumber: only when the existing code was itself
          // auto-generated. Preserves anything the user typed by hand.
          const newCode = (dragged && isAutoFlCode(dragged.code) && targetParentId != null) ? nextCodeForParent(targetParentId) : null;
          const res = await authFetch(`/api/functional-locations/${draggedId}/move`, {
            method: 'PUT',
            body: JSON.stringify({ parent_id: targetParentId })
          });
          if (res.ok) {
            if (newCode) await renameFlAfterMove(draggedId, newCode);
            setFlDirty(true);
            showToast('Moved');
            reloadFl();
          } else {
            const e = await res.json().catch(() => ({}));
            showToast(e.error || 'Move failed', 'error');
          }
        };

        const loadRollup = async (node) => {
          setFlRollupLoading(true);
          setFlRollup({ node, _loading: true });
          try {
            const res = await authFetch(`/api/functional-locations/${node.id}/rollup`);
            const data = res.ok ? await res.json() : null;
            setFlRollup(data);
          } finally {
            setFlRollupLoading(false);
          }
        };

        // Walk the tree using only the user's pre-search expansion state to
        // mark every FL / asset that would have been visible right before
        // they started typing. Those rows stay rendered during search so the
        // user can drag a match into an already-open branch.
        const flKeptOpen = new Set();
        const assetKeptOpen = new Set();
        if (searchActive) {
          const visitAsset = (aid) => {
            for (const c of (childAssetsByParent.get(aid) || [])) {
              assetKeptOpen.add(c.id);
              if (userExpandedAssets.has(c.id)) visitAsset(c.id);
            }
            for (const fl of (flsByParentAsset.get(aid) || [])) visitFl(fl.id);
          };
          const visitFl = (flId) => {
            flKeptOpen.add(flId);
            if (!userExpandedFls.has(flId)) return;
            for (const a of (assetsByFl.get(flId) || [])) {
              assetKeptOpen.add(a.id);
              if (userExpandedAssets.has(a.id)) visitAsset(a.id);
            }
            for (const ch of (childrenByParent.get(flId) || [])) visitFl(ch.id);
          };
          for (const root of (childrenByParent.get(0) || [])) visitFl(root.id);
        }
        // Final visibility sets: match path OR was-already-open branch.
        const renderableFlIds = searchActive ? new Set([...visibleFlIds, ...flKeptOpen]) : null;
        const renderableAssetIds = searchActive ? new Set([...visibleAssetIds, ...assetKeptOpen]) : null;

        // Recursive renderer
        const renderNode = (node, depth) => {
          // When search is active, hide branches that aren't on the path
          // to a match AND weren't open before search started.
          if (searchActive && !renderableFlIds.has(node.id)) return null;
          const kids = childrenByParent.get(node.id) || [];
          const hasKids = kids.length > 0;
          const allNodeAssets = assetsByFl.get(node.id) || [];
          const nodeAssets = searchActive ? allNodeAssets.filter(a => renderableAssetIds.has(a.id)) : allNodeAssets;
          // Disclosure is enabled if the node has either children or
          // attached assets — otherwise a leaf with a couple of assets
          // attached would have no way to reveal them.
          const hasContent = hasKids || nodeAssets.length > 0;
          const isExpanded = flExpanded.has(node.id) || searchExpandedFls.has(node.id);
          const isMatch = matchIds.has(node.id);
          const isEditing = flEditing?.id === node.id;
          // When expanded, tint the whole subtree wrapper + drop a thin
          // blue scope bar on the left edge so the user can see at a glance
          // which rows belong inside the expanded FL.
          const expandedStyle = isExpanded && hasContent
            ? { background: 'rgba(37,99,235,0.025)', boxShadow: 'inset 3px 0 0 rgba(37,99,235,0.28)' }
            : null;
          return (
            <div key={node.id} style={expandedStyle || undefined}>
              <div
                draggable={!isEditing && !flLocked}
                onDragStart={e => { if (flLocked) { e.preventDefault(); return; } e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(node.id)); }}
                onDragOver={e => { if (flLocked) return; e.preventDefault(); e.dataTransfer.dropEffect = 'move'; e.currentTarget.style.background = 'rgba(34,197,94,0.18)'; e.currentTarget.style.outline = '2px dashed #22c55e'; e.currentTarget.style.outlineOffset = '-2px'; }}
                onDragLeave={e => { e.currentTarget.style.background = isMatch ? 'rgba(37,99,235,0.06)' : 'transparent'; e.currentTarget.style.outline = ''; }}
                onDrop={async e => {
                  e.preventDefault();
                  e.currentTarget.style.background = isMatch ? 'rgba(37,99,235,0.06)' : 'transparent';
                  e.currentTarget.style.outline = '';
                  // Asset dropped on an FL row → reattach to this FL and
                  // clear parent_asset_id so it becomes top-level under it.
                  const assetRaw = e.dataTransfer.getData('application/x-asset-id');
                  if (assetRaw) {
                    const assetId = parseInt(assetRaw);
                    if (!assetId) return;
                    const movingAsset = flAttachedAssets.find(x => x.id === assetId);
                    const newTag = (movingAsset && isAutoAssetTag(movingAsset.tag)) ? nextAssetTagForFl(node.id) : null;
                    // Clear parent first, then bulk-assign-fl to this node.
                    await authFetch(`/api/assets/${assetId}/parent`, { method: 'PUT', body: JSON.stringify({ parent_asset_id: null }) });
                    const res = await authFetch('/api/assets/bulk-assign-fl', { method: 'POST', body: JSON.stringify({ asset_ids: [assetId], functional_location_id: node.id }) });
                    if (res.ok) {
                      if (newTag) await renameAssetAfterMove(assetId, newTag);
                      setFlDirty(true);
                      showToast('Asset moved'); reloadFl();
                    } else { showToast('Move failed', 'error'); }
                    return;
                  }
                  const dragged = parseInt(e.dataTransfer.getData('text/plain'));
                  if (dragged && dragged !== node.id) handleDrop(dragged, node.id);
                }}
                onMouseEnter={e => { if (!isMatch) e.currentTarget.style.background = 'rgba(37,99,235,0.04)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = isMatch ? 'rgba(37,99,235,0.06)' : 'transparent'; }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, position: 'relative',
                  padding: '5px 12px', paddingLeft: 12 + depth * 18,
                  borderBottom: '1px solid var(--border)',
                  background: isMatch ? 'rgba(37,99,235,0.06)' : 'transparent',
                  transition: 'background 0.15s', cursor: 'default',
                }}>
                <button onClick={() => hasContent && toggleExpand(node.id)}
                  style={{ background: 'none', border: 'none', cursor: hasContent ? 'pointer' : 'default', color: 'var(--text-muted)', padding: 0, width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {hasContent ? (isExpanded ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />) : <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--text-muted)', opacity: 0.4 }} />}
                </button>

                {isEditing ? (
                  <>
                    <input value={flEditing.code} onChange={e => setFlEditing({ ...flEditing, code: e.target.value })}
                      onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(); else if (e.key === 'Escape') setFlEditing(null); }}
                      style={{ width: 100, padding: '2px 6px', borderRadius: 5, border: `1px solid ${editCodeDuplicate ? '#dc2626' : 'var(--border)'}`, background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 11, fontFamily: 'monospace' }}
                      title={editCodeDuplicate ? 'That code already exists' : ''} />
                    <input value={flEditing.name} onChange={e => setFlEditing({ ...flEditing, name: capitalizeFirst(e.target.value) })}
                      onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(); else if (e.key === 'Escape') setFlEditing(null); }}
                      style={{ flex: 1, padding: '2px 6px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 11 }} />
                    <button onClick={handleSaveEdit} disabled={!editCanSubmit}
                      style={{ padding: '2px 7px', borderRadius: 5, border: 'none', background: editCanSubmit ? '#16a34a' : '#94a3b8', color: '#fff', cursor: editCanSubmit ? 'pointer' : 'not-allowed', fontSize: 10, fontWeight: 700 }}
                      title={editCodeDuplicate ? 'That code already exists' : ''}>Save</button>
                    <button onClick={() => setFlEditing(null)} style={{ padding: '2px 7px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 10 }}>Cancel</button>
                  </>
                ) : (
                  <>
                    <span style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700, color: 'var(--text-primary)', minWidth: 80 }}>{node.code}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-primary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{node.name}</span>
                    {(() => {
                      const subCount = subtreeAssetCount(node.id);
                      return (
                        <span style={{ fontSize: 10, color: 'var(--text-muted)' }} title={node.asset_count !== subCount ? `${node.asset_count} direct · ${subCount} including sub-locations` : ''}>
                          {subCount} asset{subCount === 1 ? '' : 's'}
                        </span>
                      );
                    })()}
                    {!flLocked && (
                      <>
                        <button onClick={() => { setFlAddingUnder(node.id); setFlAddForm({ code: nextCodeForParent(node.id), name: '' }); setFlExpanded(prev => new Set(prev).add(node.id)); }} title="Add child location"
                          style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 2 }}><FiPlus size={12} /></button>
                        {(node.parent_id != null || node.parent_asset_id != null) && (
                          <button onClick={e => { e.stopPropagation(); setFlMovePopoverFor(flMovePopoverFor === node.id ? null : node.id); setFlMoveSearch(''); }}
                            title="Move this functional location (children + assets follow)"
                            style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-secondary)', cursor: 'pointer', padding: '2px 5px', borderRadius: 5, display: 'flex', alignItems: 'center', gap: 2, fontSize: 9, fontWeight: 600 }}>
                            <FiArrowRight size={10} /> Move
                          </button>
                        )}
                        <button onClick={() => setFlEditing({ id: node.id, code: node.code, name: node.name })} title="Rename"
                          style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 2 }}><FiEdit2 size={11} /></button>
                        <button onClick={() => handleDelete(node)} title="Delete"
                          style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 2 }}><FiTrash2 size={11} /></button>
                      </>
                    )}
                  </>
                )}
                {/* Move-FL popover — picks the new parent. Self and every
                    descendant are excluded so a cycle can't be created. */}
                {flMovePopoverFor === node.id && (() => {
                  const subtreeIds = (() => {
                    const ids = new Set([node.id]);
                    const stack = [node.id];
                    while (stack.length) {
                      const id = stack.pop();
                      for (const c of (childrenByParent.get(id) || [])) { ids.add(c.id); stack.push(c.id); }
                    }
                    return ids;
                  })();
                  const moveOptions = (() => {
                    const out = [];
                    const walk = (parentKey, dep) => {
                      for (const ch of (childrenByParent.get(parentKey) || [])) {
                        if (subtreeIds.has(ch.id)) continue; // can't parent under self / own descendants
                        out.push({ ...ch, depth: dep });
                        walk(ch.id, dep + 1);
                      }
                    };
                    walk(0, 0);
                    const q = flMoveSearch.trim().toLowerCase();
                    if (!q) return out;
                    return out.filter(n => n.code.toLowerCase().includes(q) || (n.name || '').toLowerCase().includes(q));
                  })();
                  const moveNode = async (newParentId) => {
                    setFlMoveSaving(true);
                    try {
                      const newCode = (isAutoFlCode(node.code) && newParentId != null) ? nextCodeForParent(newParentId) : null;
                      const res = await authFetch(`/api/functional-locations/${node.id}/move`, {
                        method: 'PUT',
                        body: JSON.stringify({ parent_id: newParentId })
                      });
                      if (res.ok) {
                        if (newCode) await renameFlAfterMove(node.id, newCode);
                        setFlDirty(true);
                        showToast('Moved');
                        setFlMovePopoverFor(null);
                        setFlMoveSearch('');
                        if (newParentId != null) setFlExpanded(prev => new Set(prev).add(newParentId));
                        reloadFl();
                      } else {
                        const e = await res.json().catch(() => ({}));
                        showToast(e.error || 'Move failed', 'error');
                      }
                    } finally {
                      setFlMoveSaving(false);
                    }
                  };
                  return (
                    <>
                      <div onClick={() => setFlMovePopoverFor(null)}
                        style={{ position: 'fixed', inset: 0, zIndex: 90 }} />
                      <div onClick={e => e.stopPropagation()}
                        style={{
                          position: 'absolute', top: '100%', right: 14, marginTop: 6, zIndex: 95,
                          width: 360, maxHeight: 380, background: 'var(--bg-card)',
                          border: '1px solid var(--border)', borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
                          display: 'flex', flexDirection: 'column', overflow: 'hidden',
                        }}>
                        <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>
                            Move <span style={{ fontFamily: 'monospace', color: '#8b5cf6' }}>{node.code}</span> — {node.name}
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 8 }}>
                            All children and attached assets follow automatically.
                          </div>
                          <input autoFocus value={flMoveSearch} onChange={e => setFlMoveSearch(e.target.value)}
                            placeholder="Search target FL by code or name…"
                            style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto' }}>
                          {moveOptions.length === 0 ? (
                            <div style={{ padding: 14, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>No valid targets</div>
                          ) : moveOptions.map(n => {
                            const isCurrent = n.id === node.parent_id;
                            return (
                              <button key={n.id} onClick={() => !isCurrent && moveNode(n.id)}
                                disabled={isCurrent || flMoveSaving}
                                style={{
                                  display: 'block', width: '100%', textAlign: 'left',
                                  padding: '4px 10px', paddingLeft: 10 + n.depth * 12,
                                  background: isCurrent ? 'rgba(139,92,246,0.10)' : 'transparent',
                                  border: 'none', borderBottom: '1px solid var(--border)',
                                  color: 'var(--text-primary)',
                                  cursor: isCurrent ? 'not-allowed' : 'pointer',
                                  fontSize: 11, fontFamily: 'monospace',
                                }}>
                                <span style={{ fontWeight: 700, color: '#8b5cf6' }}>{n.code}</span>
                                <span style={{ fontFamily: 'inherit', marginLeft: 8, color: 'var(--text-secondary)' }}>{n.name}</span>
                                {isCurrent && <span style={{ fontFamily: 'inherit', marginLeft: 6, fontSize: 10, color: '#8b5cf6' }}>(current parent)</span>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Inline add-child row — code is auto-derived from parent and locked. */}
              {flAddingUnder === node.id && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '6px 12px', paddingLeft: 12 + (depth + 1) * 18, background: 'rgba(37,99,235,0.06)', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FiPlus size={12} style={{ color: '#2563eb' }} />
                    <input value={flAddForm.code} readOnly title="Auto-generated from parent code" placeholder="(auto)"
                      style={{ width: 140, padding: '2px 6px', borderRadius: 5, border: '1px solid var(--border)', background: 'rgba(255,255,255,0.04)', color: 'var(--text-muted)', fontSize: 11, fontFamily: 'monospace', cursor: 'not-allowed' }} />
                    <input autoFocus value={flAddForm.name} onChange={e => setFlAddForm({ ...flAddForm, name: capitalizeFirst(e.target.value) })} placeholder="Name"
                      onKeyDown={e => { if (e.key === 'Enter' && addCanSubmit) handleCreate(); else if (e.key === 'Escape') { setFlAddingUnder(undefined); setFlAddForm({ code: '', name: '' }); } }}
                      style={{ flex: 1, padding: '2px 6px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 11 }} />
                    <button onClick={handleCreate} disabled={!addCanSubmit}
                      style={{ padding: '2px 7px', borderRadius: 5, border: 'none', background: addCanSubmit ? '#2563eb' : '#94a3b8', color: '#fff', cursor: addCanSubmit ? 'pointer' : 'not-allowed', fontSize: 10, fontWeight: 700 }}>Add</button>
                    <button onClick={() => { setFlAddingUnder(undefined); setFlAddForm({ code: '', name: '' }); }} style={{ padding: '2px 7px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 10 }}>Cancel</button>
                  </div>
                  {addCodeDuplicate && (
                    <div style={{ fontSize: 10, color: '#dc2626', fontWeight: 600, paddingLeft: 18 }}>That code already exists</div>
                  )}
                </div>
              )}

              {/* Inline "create asset under this FL" row */}
              {flAssetAddingUnder === node.id && (() => {
                // Asset ID is auto-generated server-side, so no duplicate
                // check is needed and the form just needs a name.
                const tagDup = false;
                const canSave = !!flAssetForm.name.trim();
                const handleSaveAsset = async () => {
                  if (!canSave) return;
                  const res = await authFetch('/api/assets', {
                    method: 'POST',
                    body: JSON.stringify({
                      name: capitalizeFirst(flAssetForm.name.trim()),
                      functional_location_id: node.id,
                    })
                  });
                  if (res.ok) {
                    showToast('Asset added');
                    setFlAssetAddingUnder(undefined);
                    setFlAssetForm({ tag: '', name: '' });
                    reloadFl();
                  } else {
                    const e = await res.json().catch(() => ({}));
                    showToast(e.error || 'Asset create failed', 'error');
                  }
                };
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '6px 12px', paddingLeft: 12 + (depth + 1) * 18, background: 'rgba(139,92,246,0.06)', borderLeft: '3px solid #8b5cf6', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <FiTool size={12} style={{ color: '#8b5cf6' }} />
                      <span title="Asset ID is auto-generated on save" style={{ width: 140, padding: '2px 6px', borderRadius: 5, border: '1px dashed var(--border)', background: 'var(--bg-input)', color: 'var(--text-muted)', fontSize: 11, fontFamily: 'monospace', fontStyle: 'italic' }}>Auto-assigned</span>
                      <input autoFocus value={flAssetForm.name} onChange={e => setFlAssetForm({ ...flAssetForm, name: capitalizeFirst(e.target.value) })} placeholder="Asset name (e.g. Prado)"
                        onKeyDown={e => { if (e.key === 'Enter' && canSave) handleSaveAsset(); else if (e.key === 'Escape') { setFlAssetAddingUnder(undefined); setFlAssetForm({ tag: '', name: '' }); } }}
                        style={{ flex: 1, padding: '2px 6px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 11 }} />
                      <button onClick={handleSaveAsset} disabled={!canSave}
                        style={{ padding: '2px 7px', borderRadius: 5, border: 'none', background: canSave ? '#8b5cf6' : '#94a3b8', color: '#fff', cursor: canSave ? 'pointer' : 'not-allowed', fontSize: 10, fontWeight: 700 }}>Add asset</button>
                      <button onClick={() => { setFlAssetAddingUnder(undefined); setFlAssetForm({ tag: '', name: '' }); }} style={{ padding: '2px 7px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 10 }}>Cancel</button>
                    </div>
                    {tagDup && (
                      <div style={{ fontSize: 10, color: '#dc2626', fontWeight: 600, paddingLeft: 18 }}>That Asset ID already exists</div>
                    )}
                  </div>
                );
              })()}

              {/* Attached assets — rendered inline. Each asset gets a
                  derived FL-style code of the form '<parent code>.NN' (e.g.
                  5061.01.01 → 5061.01.01.01) to mirror SAP equipment-number
                  conventions and let supervisors reference assets by their
                  position in the hierarchy. */}
              {isExpanded && (() => {
                // Build the cycle-prevention move-FL options once.
                const buildMoveOptions = () => {
                  const out = [];
                  const walk = (parentKey, dep) => {
                    for (const ch of (childrenByParent.get(parentKey) || [])) {
                      const hasKids = (childrenByParent.get(ch.id) || []).length > 0;
                      out.push({ ...ch, depth: dep, hasKids });
                      walk(ch.id, dep + 1);
                    }
                  };
                  walk(0, 0);
                  const q = flAssetMoveSearch.trim().toLowerCase();
                  if (!q) return out;
                  return out.filter(n => n.code.toLowerCase().includes(q) || (n.name || '').toLowerCase().includes(q));
                };
                // Recursive asset-row renderer. Top-level assets attached to
                // an FL get a code like '5061.10-01'; their children get a
                // suffix appended (e.g. '5061.10-01.01') and a deeper indent.
                const renderAssetRow = (a, code, extraIndent) => {
                  const isMoving = flAssetMovePopoverFor?.id === a.id;
                  const allChildren = childAssetsByParent.get(a.id) || [];
                  const allChildFls = flsByParentAsset.get(a.id) || [];
                  const myChildren = searchActive ? allChildren.filter(c => renderableAssetIds.has(c.id)) : allChildren;
                  const myChildFls = searchActive ? allChildFls.filter(f => renderableFlIds.has(f.id)) : allChildFls;
                  const hasChildren = myChildren.length > 0 || myChildFls.length > 0;
                  const isAssetExpanded = flAssetExpanded.has(a.id) || searchExpandedAssets.has(a.id);
                  const toggleAssetExpand = () => setFlAssetExpanded(prev => {
                    const next = new Set(prev);
                    next.has(a.id) ? next.delete(a.id) : next.add(a.id);
                    return next;
                  });
                  const moveOptions = isMoving ? buildMoveOptions() : [];
                  const moveAsset = async (targetFlId) => {
                    setFlAssetMoveSaving(true);
                    try {
                      const newTag = (targetFlId && isAutoAssetTag(a.tag)) ? nextAssetTagForFl(targetFlId) : null;
                      const res = await authFetch('/api/assets/bulk-assign-fl', {
                        method: 'POST',
                        body: JSON.stringify({ asset_ids: [a.id], functional_location_id: targetFlId })
                      });
                      if (res.ok) {
                        if (newTag) await renameAssetAfterMove(a.id, newTag);
                        setFlDirty(true);
                        showToast(targetFlId ? 'Moved' : 'Detached');
                        setFlAssetMovePopoverFor(null);
                        setFlAssetMoveSearch('');
                        reloadFl();
                      } else {
                        showToast('Move failed', 'error');
                      }
                    } finally {
                      setFlAssetMoveSaving(false);
                    }
                  };
                  return (
                    <React.Fragment key={`a-${a.id}`}>
                      <div
                        draggable={!flLocked}
                        onDragStart={e => { if (flLocked) { e.preventDefault(); return; } e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('application/x-asset-id', String(a.id)); }}
                        onDragOver={e => { if (flLocked) return; e.preventDefault(); e.dataTransfer.dropEffect = 'move'; e.currentTarget.style.background = 'rgba(34,197,94,0.22)'; e.currentTarget.style.outline = '2px dashed #22c55e'; e.currentTarget.style.outlineOffset = '-2px'; }}
                        onDragLeave={e => { e.currentTarget.style.background = matchedAssetIds.has(a.id) ? 'rgba(250,204,21,0.18)' : 'var(--bg-input)'; e.currentTarget.style.outline = ''; }}
                        onDrop={async e => {
                          e.preventDefault();
                          e.currentTarget.style.background = matchedAssetIds.has(a.id) ? 'rgba(250,204,21,0.18)' : 'var(--bg-input)';
                          e.currentTarget.style.outline = '';
                          const draggedRaw = e.dataTransfer.getData('application/x-asset-id');
                          // FL dropped onto an asset → re-parent the FL under
                          // this asset (parent_asset_id). Server clears the
                          // old parent_id automatically as part of /move.
                          if (!draggedRaw) {
                            const flRaw = e.dataTransfer.getData('text/plain');
                            if (!flRaw) return;
                            const flId = parseInt(flRaw);
                            if (!flId) return;
                            const draggedFl = flNodes.find(n => n.id === flId);
                            const newCode = (draggedFl && isAutoFlCode(draggedFl.code)) ? nextCodeForAssetParent(a.id) : null;
                            const res = await authFetch(`/api/functional-locations/${flId}/move`, {
                              method: 'PUT',
                              body: JSON.stringify({ parent_asset_id: a.id })
                            });
                            if (res.ok) {
                              if (newCode) await renameFlAfterMove(flId, newCode);
                              setFlDirty(true);
                              showToast('FL nested under asset'); reloadFl();
                            }
                            else { const er = await res.json().catch(() => ({})); showToast(er.error || 'Move failed', 'error'); }
                            return;
                          }
                          const draggedId = parseInt(draggedRaw);
                          if (!draggedId || draggedId === a.id) return;
                          const draggedAsset = flAttachedAssets.find(x => x.id === draggedId);
                          const newTag = (draggedAsset && isAutoAssetTag(draggedAsset.tag) && a.functional_location_id) ? nextAssetTagForFl(a.functional_location_id) : null;
                          // Step 1: set parent (cycle-guarded server-side)
                          const res = await authFetch(`/api/assets/${draggedId}/parent`, {
                            method: 'PUT',
                            body: JSON.stringify({ parent_asset_id: a.id })
                          });
                          if (!res.ok) {
                            const er = await res.json().catch(() => ({}));
                            showToast(er.error || 'Move failed', 'error');
                            return;
                          }
                          // Step 2: when the drop crosses an FL boundary, also
                          // re-attach the child to the parent's FL so the
                          // sub-asset 'lives' under the same location. The
                          // server's auto-inherit only fires when the child's
                          // FL is null; here we always sync.
                          if (a.functional_location_id) {
                            await authFetch('/api/assets/bulk-assign-fl', {
                              method: 'POST',
                              body: JSON.stringify({ asset_ids: [draggedId], functional_location_id: a.functional_location_id })
                            });
                          }
                          if (newTag) await renameAssetAfterMove(draggedId, newTag);
                          setFlDirty(true);
                          showToast('Nested as sub-asset');
                          reloadFl();
                        }}
                        title="Drag an asset here to nest as sub-asset, or drag an FL here to make it live under this asset"
                        style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 12px', paddingLeft: 12 + (depth + 1) * 18 + 14 + extraIndent, borderBottom: '1px solid var(--border)', background: matchedAssetIds.has(a.id) ? 'rgba(250,204,21,0.18)' : 'var(--bg-input)', borderLeft: matchedAssetIds.has(a.id) ? '3px solid #facc15' : '3px solid var(--accent)', transition: 'background 0.15s', position: 'relative', cursor: 'grab' }}
                        onMouseEnter={e => e.currentTarget.style.background = matchedAssetIds.has(a.id) ? 'rgba(250,204,21,0.28)' : 'rgba(37,99,235,0.06)'}
                        onMouseLeave={e => e.currentTarget.style.background = matchedAssetIds.has(a.id) ? 'rgba(250,204,21,0.18)' : 'var(--bg-input)'}>
                        {/* Tree connector for nested children: a thin vertical
                            line up to the row centre, then a horizontal stub
                            into the icon. Makes 'child of the asset above'
                            visually obvious without changing the layout. */}
                        {extraIndent > 0 && (
                          <>
                            <span style={{ position: 'absolute', left: 12 + (depth + 1) * 18 + 14 + extraIndent - 11, top: 0, bottom: '50%', width: 1, background: 'var(--border)' }} />
                            <span style={{ position: 'absolute', left: 12 + (depth + 1) * 18 + 14 + extraIndent - 11, top: '50%', width: 8, height: 1, background: 'var(--border)' }} />
                          </>
                        )}
                        <button
                          draggable={false}
                          onMouseDown={e => e.stopPropagation()}
                          onDragStart={e => { e.preventDefault(); e.stopPropagation(); }}
                          onClick={e => { e.stopPropagation(); e.preventDefault(); if (hasChildren) toggleAssetExpand(); }}
                          title={hasChildren ? (isAssetExpanded ? 'Collapse sub-assets' : 'Expand sub-assets') : 'No sub-assets'}
                          style={{ background: 'none', border: 'none', cursor: hasChildren ? 'pointer' : 'default', color: 'var(--text-secondary)', padding: 0, width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {hasChildren ? (isAssetExpanded ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />) : <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--text-muted)', opacity: 0.5 }} />}
                        </button>
                        <div style={{ width: 18, height: 18, borderRadius: 5, background: 'rgba(37,99,235,0.12)', border: '1px solid rgba(37,99,235,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <FiTool size={10} style={{ color: 'var(--accent)' }} />
                        </div>
                        <span style={{ fontFamily: 'monospace', fontSize: 10, fontWeight: 700, color: 'var(--accent)', minWidth: 100 }}>{code}</span>
                        <span style={{ fontFamily: 'monospace', fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)', padding: '1px 6px', borderRadius: 4, background: 'rgba(37,99,235,0.06)', border: '1px solid var(--border)' }}>{a.tag}</span>
                        <span onClick={() => navigate(`/assets/${a.id}`)} title={`Open asset · ${code}`}
                          style={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                          {[a.name, a.manufacturer_name, a.model_name].filter(Boolean).join(' ')}
                        </span>
                        {hasChildren && (
                          <span title={`${myChildren.length} sub-asset${myChildren.length === 1 ? '' : 's'}`}
                            style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4, background: 'rgba(37,99,235,0.10)', color: 'var(--accent)', border: '1px solid rgba(37,99,235,0.25)' }}>
                            +{myChildren.length}
                          </span>
                        )}
                        <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.5px', padding: '1px 5px', borderRadius: 4, background: 'rgba(100,116,139,0.15)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>ASSET</span>
                        {!flLocked && (
                          <button
                            draggable={false}
                            onMouseDown={e => e.stopPropagation()}
                            onClick={e => {
                              e.stopPropagation();
                              if (isMoving) {
                                setFlAssetMovePopoverFor(null);
                              } else {
                                const r = e.currentTarget.getBoundingClientRect();
                                setFlAssetMovePopoverFor({ id: a.id, x: r.right, y: r.bottom });
                              }
                              setFlAssetMoveSearch('');
                            }}
                            title="Move to another functional location"
                            style={{ padding: '2px 7px', borderRadius: 5, border: '1px solid var(--border)', background: isMoving ? 'rgba(37,99,235,0.10)' : 'transparent', color: 'var(--accent)', cursor: 'pointer', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 3 }}>
                            <FiRepeat size={10} /> Move
                          </button>
                        )}
                        {isMoving && (() => {
                          // Position the popover with fixed coords so the
                          // outer card's overflow:hidden can't clip it.
                          // Clamp to keep within viewport on small windows.
                          const POPW = 340;
                          const POPH = 360;
                          const left = Math.max(8, Math.min(window.innerWidth - POPW - 8, (flAssetMovePopoverFor.x || 0) - POPW));
                          const top = Math.max(8, Math.min(window.innerHeight - POPH - 8, (flAssetMovePopoverFor.y || 0) + 6));
                          return (
                          <>
                            <div onClick={() => setFlAssetMovePopoverFor(null)}
                              style={{ position: 'fixed', inset: 0, zIndex: 990 }} />
                            <div onClick={e => e.stopPropagation()}
                              style={{
                                position: 'fixed', left, top, zIndex: 995,
                                width: POPW, maxHeight: POPH, background: 'var(--bg-card)',
                                border: '1px solid var(--border)', borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
                                display: 'flex', flexDirection: 'column', overflow: 'hidden',
                              }}>
                              <div style={{ padding: 8, borderBottom: '1px solid var(--border)' }}>
                                <input autoFocus value={flAssetMoveSearch} onChange={e => setFlAssetMoveSearch(e.target.value)}
                                  placeholder="Search FL by code or name…"
                                  style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
                              </div>
                              <div style={{ flex: 1, overflowY: 'auto' }}>
                                <button onClick={() => moveAsset(null)} disabled={flAssetMoveSaving}
                                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: 'transparent', border: 'none', borderBottom: '1px solid var(--border)', color: '#dc2626', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                                  <FiX size={11} style={{ verticalAlign: 'middle', marginRight: 6 }} /> Unassign (clear FL)
                                </button>
                                {moveOptions.length === 0 ? (
                                  <div style={{ padding: 14, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>No matches</div>
                                ) : moveOptions.map(n => {
                                  const isCurrent = n.id === a.functional_location_id;
                                  return (
                                    <button key={n.id} onClick={() => !n.hasKids && !isCurrent && moveAsset(n.id)}
                                      disabled={n.hasKids || flAssetMoveSaving || isCurrent}
                                      style={{
                                        display: 'block', width: '100%', textAlign: 'left',
                                        padding: '4px 10px', paddingLeft: 10 + n.depth * 12,
                                        background: isCurrent ? 'rgba(37,99,235,0.10)' : 'transparent',
                                        border: 'none', borderBottom: '1px solid var(--border)',
                                        color: n.hasKids ? 'var(--text-muted)' : 'var(--text-primary)',
                                        cursor: (n.hasKids || isCurrent) ? 'not-allowed' : 'pointer',
                                        fontSize: 11, fontFamily: 'monospace',
                                        opacity: n.hasKids ? 0.6 : 1,
                                      }}>
                                      <span style={{ fontWeight: 700, color: n.hasKids ? 'var(--text-muted)' : 'var(--accent)' }}>{n.code}</span>
                                      <span style={{ fontFamily: 'inherit', marginLeft: 8, color: 'var(--text-secondary)' }}>{n.name}</span>
                                      {n.hasKids && <span style={{ fontFamily: 'inherit', marginLeft: 6, fontSize: 10 }}>(container)</span>}
                                      {isCurrent && <span style={{ fontFamily: 'inherit', marginLeft: 6, fontSize: 10, color: 'var(--accent)' }}>(current)</span>}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </>
                          );
                        })()}
                      </div>
                      {isAssetExpanded && myChildren.map((c, ci) => renderAssetRow(c, `${code}.${String(ci + 1).padStart(2, '0')}`, extraIndent + 18))}
                      {/* Child FLs nested directly under this asset (v106).
                          Wrapped in a div with extra left padding + a vertical
                          accent line so the FL clearly reads as living inside
                          the asset above. Depth still advances by one so any
                          deeper FL/asset descendants keep stepping right. */}
                      {isAssetExpanded && myChildFls.length > 0 && (
                        <div style={{ marginLeft: 12 + (depth + 1) * 18 + 32 + extraIndent, borderLeft: '2px solid rgba(37,99,235,0.25)', background: 'rgba(37,99,235,0.03)' }}>
                          {myChildFls.map(fl => renderNode(fl, 0))}
                        </div>
                      )}
                    </React.Fragment>
                  );
                };
                return nodeAssets.map((a, i) => renderAssetRow(a, `${node.code}-${String(i + 1).padStart(2, '0')}`, 0));
              })()}

              {/* Children */}
              {isExpanded && hasKids && kids.map(c => renderNode(c, depth + 1))}
            </div>
          );
        };

        const rootNodes = childrenByParent.get(0) || [];

        return (
          <div style={{ display: 'grid', gridTemplateColumns: flRollup ? '1fr 360px' : '1fr', gap: 16 }}>
            <div style={{ background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Functional Locations</h2>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input value={flSearch} onChange={e => setFlSearch(e.target.value)} placeholder="Search…"
                    style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12, width: 180 }} />
                  {/* Three-state toggle:
                      - Locked            → click to Unlock (tree read-only).
                      - Unlocked + clean  → 'Lock' (re-lock instantly, nothing to save).
                      - Unlocked + dirty  → green 'Save' that re-locks with a
                        confirm dialog so the user has a clear 'I'm done' moment.
                      Every individual change has already hit the server when
                      it happened — the Save button just confirms the session
                      and returns the tree to read-only. */}
                  <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                    onClick={() => {
                      if (flLocked) {
                        // Snapshot before edits so the Cancel button can
                        // revert renames / moves / creates done this session.
                        setFlSnapshot({
                          fls: flNodes.map(n => ({ id: n.id, code: n.code, name: n.name, parent_id: n.parent_id ?? null, parent_asset_id: n.parent_asset_id ?? null, sort_order: n.sort_order ?? 0 })),
                          assets: flAttachedAssets.map(a => ({ id: a.id, tag: a.tag, functional_location_id: a.functional_location_id ?? null, parent_asset_id: a.parent_asset_id ?? null })),
                        });
                        setFlLocked(false); setFlDirty(false); showToast('Edit mode on'); return;
                      }
                      if (!flDirty) { setFlLocked(true); setFlSnapshot(null); showToast('Locked'); return; }
                      setConfirmDialog({
                        title: 'Save and lock',
                        subtitle: 'You\'ve made changes in this session. Each one was already saved as you went — clicking Save will just lock the tree back to read-only.',
                        confirmLabel: 'Save & lock', confirmColor: '#16a34a',
                        onConfirm: () => { setFlLocked(true); setFlDirty(false); setFlSnapshot(null); setConfirmDialog(null); showToast('Saved'); }
                      });
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8,
                      border: flLocked || !flDirty ? '1px solid var(--border)' : 'none',
                      background: flLocked ? 'var(--bg-card)' : (flDirty ? '#16a34a' : 'rgba(245,158,11,0.12)'),
                      color: flLocked ? 'var(--text-primary)' : (flDirty ? '#fff' : '#f59e0b'),
                      fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    }}
                    title={flLocked ? 'Click to enable editing' : (flDirty ? 'Click to save & lock' : 'Click to lock the tree (read-only)')}>
                    {flLocked
                      ? <><FiLock size={13} /> Unlock</>
                      : (flDirty ? <><FiCheck size={13} /> Save</> : <><FiUnlock size={13} /> Lock</>)}
                  </motion.button>
                  {/* Cancel — only shown alongside Save. Diffs the live tree
                      against the unlock-time snapshot and reverses every
                      rename / move / create. Deletes cannot be restored
                      (the row is gone server-side); we warn the user about
                      that up-front. */}
                  {!flLocked && flDirty && (
                    <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                      onClick={() => {
                        if (!flSnapshot) return;
                        const snapFlMap = new Map(flSnapshot.fls.map(f => [f.id, f]));
                        const snapAssetMap = new Map(flSnapshot.assets.map(a => [a.id, a]));
                        const liveFlMap = new Map(flNodes.map(n => [n.id, n]));
                        const liveAssetMap = new Map(flAttachedAssets.map(a => [a.id, a]));
                        const createdFls = flNodes.filter(n => !snapFlMap.has(n.id));
                        const createdAssets = flAttachedAssets.filter(a => !snapAssetMap.has(a.id));
                        const lostFls = flSnapshot.fls.filter(f => !liveFlMap.has(f.id));
                        const lostAssets = flSnapshot.assets.filter(a => !liveAssetMap.has(a.id));
                        const lostCount = lostFls.length + lostAssets.length;
                        setConfirmDialog({
                          title: 'Discard changes',
                          subtitle: lostCount > 0
                            ? `Renames and moves will be reverted, and ${createdFls.length + createdAssets.length} newly-created item(s) will be deleted. ${lostCount} item(s) deleted this session CANNOT be restored — they're gone server-side.`
                            : `Renames and moves will be reverted, and ${createdFls.length + createdAssets.length} newly-created item(s) will be deleted.`,
                          confirmLabel: 'Discard', confirmColor: '#dc2626',
                          onConfirm: async () => {
                            setConfirmDialog(null);
                            // 1. Revert FL rename / move
                            for (const snap of flSnapshot.fls) {
                              const live = liveFlMap.get(snap.id);
                              if (!live) continue;
                              if ((live.parent_id ?? null) !== snap.parent_id || (live.parent_asset_id ?? null) !== snap.parent_asset_id) {
                                await authFetch(`/api/functional-locations/${snap.id}/move`, { method: 'PUT', body: JSON.stringify({ parent_id: snap.parent_id, parent_asset_id: snap.parent_asset_id, sort_order: snap.sort_order }) });
                              }
                              if (live.code !== snap.code || live.name !== snap.name) {
                                await authFetch(`/api/functional-locations/${snap.id}`, { method: 'PUT', body: JSON.stringify({ code: snap.code, name: snap.name }) });
                              }
                            }
                            // 2. Revert asset tag / FL / parent
                            for (const snap of flSnapshot.assets) {
                              const live = liveAssetMap.get(snap.id);
                              if (!live) continue;
                              if ((live.parent_asset_id ?? null) !== snap.parent_asset_id) {
                                await authFetch(`/api/assets/${snap.id}/parent`, { method: 'PUT', body: JSON.stringify({ parent_asset_id: snap.parent_asset_id }) });
                              }
                              if ((live.functional_location_id ?? null) !== snap.functional_location_id) {
                                await authFetch('/api/assets/bulk-assign-fl', { method: 'POST', body: JSON.stringify({ asset_ids: [snap.id], functional_location_id: snap.functional_location_id }) });
                              }
                              if (live.tag !== snap.tag) {
                                await authFetch(`/api/assets/${snap.id}`, { method: 'PUT', body: JSON.stringify({ tag: snap.tag }) });
                              }
                            }
                            // 3. Delete items created this session
                            for (const c of createdAssets) {
                              await authFetch(`/api/assets/${c.id}`, { method: 'DELETE' });
                            }
                            for (const c of createdFls) {
                              await authFetch(`/api/functional-locations/${c.id}`, { method: 'DELETE' });
                            }
                            setFlLocked(true); setFlDirty(false); setFlSnapshot(null);
                            showToast('Changes discarded');
                            // Reload the live tree.
                            const [tree, assetsResp] = await Promise.all([
                              authFetch('/api/functional-locations').then(r => r.ok ? r.json() : []).catch(() => []),
                              authFetch('/api/assets?limit=2000').then(r => r.ok ? r.json() : { data: [] }).catch(() => ({ data: [] })),
                            ]);
                            setFlNodes(Array.isArray(tree) ? tree : []);
                            setFlAttachedAssets(Array.isArray(assetsResp?.data) ? assetsResp.data : []);
                          }
                        });
                      }}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: '#dc2626', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                      title="Discard this session's changes and lock">
                      <FiX size={13} /> Cancel
                    </motion.button>
                  )}
                  {!flLocked && (
                    <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                      onClick={() => {
                        setUnattachedSelected(new Set());
                        setUnattachedSearch('');
                        setUnattachedTargetFl('');
                        setShowAssetMgr(true);
                        setUnattachedLoading(true);
                        authFetch('/api/assets?functional_location=none&limit=1000').then(r => r.ok ? r.json() : { data: [] }).then(d => {
                          const list = Array.isArray(d?.data) ? d.data : [];
                          list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                          setUnattachedAssets(list);
                          setUnattachedLoading(false);
                        }).catch(() => setUnattachedLoading(false));
                      }}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                      <FiBox size={14} /> Manage Assets
                    </motion.button>
                  )}
                  {/* Only one root supported — once a root exists, hide the button. Also hidden when locked. */}
                  {!flLocked && rootNodes.length === 0 && (
                    <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                      onClick={() => { setFlAddingUnder(null); setFlAddForm({ code: '', name: '' }); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                      <FiPlus size={14} /> Root
                    </motion.button>
                  )}
                </div>
              </div>

              {/* Inline add-root row — only available when no root exists yet. */}
              {flAddingUnder === null && rootNodes.length === 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '6px 12px', background: 'rgba(37,99,235,0.06)', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FiPlus size={12} style={{ color: '#2563eb' }} />
                    <input autoFocus value={flAddForm.code} onChange={e => setFlAddForm({ ...flAddForm, code: e.target.value })} placeholder="Root code (e.g. 5061)"
                      onKeyDown={e => { if (e.key === 'Enter' && addCanSubmit) handleCreate(); else if (e.key === 'Escape') { setFlAddingUnder(undefined); setFlAddForm({ code: '', name: '' }); } }}
                      style={{ width: 140, padding: '2px 6px', borderRadius: 5, border: `1px solid ${addCodeDuplicate ? '#dc2626' : 'var(--border)'}`, background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 11, fontFamily: 'monospace' }} />
                    <input value={flAddForm.name} onChange={e => setFlAddForm({ ...flAddForm, name: capitalizeFirst(e.target.value) })} placeholder="Name (e.g. Acme Mining)"
                      onKeyDown={e => { if (e.key === 'Enter' && addCanSubmit) handleCreate(); else if (e.key === 'Escape') { setFlAddingUnder(undefined); setFlAddForm({ code: '', name: '' }); } }}
                      style={{ flex: 1, padding: '2px 6px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 11 }} />
                    <button onClick={handleCreate} disabled={!addCanSubmit}
                      style={{ padding: '2px 7px', borderRadius: 5, border: 'none', background: addCanSubmit ? '#2563eb' : '#94a3b8', color: '#fff', cursor: addCanSubmit ? 'pointer' : 'not-allowed', fontSize: 10, fontWeight: 700 }}>Add</button>
                    <button onClick={() => { setFlAddingUnder(undefined); setFlAddForm({ code: '', name: '' }); }} style={{ padding: '2px 7px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 10 }}>Cancel</button>
                  </div>
                  {addCodeDuplicate && (
                    <div style={{ fontSize: 10, color: '#dc2626', fontWeight: 600, paddingLeft: 18 }}>That code already exists</div>
                  )}
                </div>
              )}

              {flLoading ? (
                <div style={{ padding: 40, textAlign: 'center' }}>{[...Array(4)].map((_, i) => <div key={i} className="skeleton" style={{ height: 40, borderRadius: 8, marginBottom: 8 }} />)}</div>
              ) : rootNodes.length === 0 && flAddingUnder !== null ? (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
                  <FiMapPin size={32} style={{ marginBottom: 8, opacity: 0.3 }} />
                  <div style={{ fontSize: 14, marginBottom: 4 }}>No functional locations yet</div>
                  <div style={{ fontSize: 12 }}>Click <strong>+ Root</strong> to add your first node (e.g. <code>5061</code> Acme Mining).</div>
                </div>
              ) : (
                <div>
                  {rootNodes.map(n => renderNode(n, 0))}
                </div>
              )}
            </div>

            {/* Roll-up side panel */}
            {flRollup && (
              <div style={{ background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden', height: 'fit-content' }}>
                <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Roll-up</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{flRollup.node?.code} {flRollup.node?.name}</div>
                  </div>
                  <button onClick={() => setFlRollup(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><FiX size={16} /></button>
                </div>
                <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {flRollupLoading || flRollup._loading ? (
                    <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>Loading…</div>
                  ) : (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div style={{ padding: 10, borderRadius: 8, background: 'var(--bg-input)' }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Labour</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: '#059669' }}>${(flRollup.labour_cost ?? 0).toLocaleString()}</div>
                        </div>
                        <div style={{ padding: 10, borderRadius: 8, background: 'var(--bg-input)' }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Materials</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: '#f59e0b' }}>${(flRollup.materials_cost ?? 0).toLocaleString()}</div>
                        </div>
                        <div style={{ padding: 10, borderRadius: 8, background: 'var(--bg-input)' }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>WOs open</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: '#2563eb' }}>{flRollup.wo_open ?? 0}</div>
                        </div>
                        <div style={{ padding: 10, borderRadius: 8, background: 'var(--bg-input)' }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>WOs overdue</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: '#dc2626' }}>{flRollup.wo_overdue ?? 0}</div>
                        </div>
                        <div style={{ padding: 10, borderRadius: 8, background: 'var(--bg-input)' }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>WOs done</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)' }}>{flRollup.wo_completed ?? 0}</div>
                        </div>
                        <div style={{ padding: 10, borderRadius: 8, background: 'var(--bg-input)' }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Downtime h</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: '#8b5cf6' }}>{flRollup.downtime_hours ?? 0}</div>
                        </div>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                        Includes {(flRollup.descendant_asset_ids || []).length} asset{(flRollup.descendant_asset_ids || []).length === 1 ? '' : 's'} · period {flRollup.period?.from} → {flRollup.period?.to}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Asset Manager modal — bulk-assign unattached assets to a leaf FL */}
            {showAssetMgr && (() => {
              const filtered = unattachedAssets.filter(a => {
                const q = unattachedSearch.trim().toLowerCase();
                if (!q) return true;
                return (a.name || '').toLowerCase().includes(q) || (a.tag || '').toLowerCase().includes(q);
              });
              const allVisibleIds = filtered.map(a => a.id);
              const allVisibleSelected = allVisibleIds.length > 0 && allVisibleIds.every(id => unattachedSelected.has(id));
              const toggleAllVisible = () => {
                setUnattachedSelected(prev => {
                  const next = new Set(prev);
                  if (allVisibleSelected) allVisibleIds.forEach(id => next.delete(id));
                  else allVisibleIds.forEach(id => next.add(id));
                  return next;
                });
              };
              const canAssign = unattachedSelected.size > 0 && !!unattachedTargetFl && !unattachedSaving;
              const handleAssign = async () => {
                setUnattachedSaving(true);
                try {
                  const res = await authFetch('/api/assets/bulk-assign-fl', {
                    method: 'POST',
                    body: JSON.stringify({ asset_ids: Array.from(unattachedSelected), functional_location_id: parseInt(unattachedTargetFl) })
                  });
                  if (res.ok) {
                    const d = await res.json();
                    showToast(`Assigned ${d.updated} asset${d.updated === 1 ? '' : 's'}`);
                    setShowAssetMgr(false);
                    reloadFl();
                  } else {
                    const e = await res.json().catch(() => ({}));
                    showToast(e.error || 'Assign failed', 'error');
                  }
                } finally {
                  setUnattachedSaving(false);
                }
              };
              return (
                <div onClick={() => !unattachedSaving && setShowAssetMgr(false)}
                  style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 300 }}>
                  <div onClick={e => e.stopPropagation()}
                    style={{ background: 'var(--bg-card)', borderRadius: 16, width: '100%', maxWidth: 720, maxHeight: '80vh', display: 'flex', flexDirection: 'column', border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)', overflow: 'hidden' }}>
                    <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Unattached Assets</h3>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Pick assets and move them under a functional location.</div>
                      </div>
                      <button onClick={() => setShowAssetMgr(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
                    </div>
                    <div style={{ padding: '14px 22px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10, alignItems: 'center' }}>
                      <div style={{ position: 'relative', flex: 1 }}>
                        <FiSearch size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                        <input value={unattachedSearch} onChange={e => setUnattachedSearch(e.target.value)} placeholder="Search by name or Asset ID…"
                          style={{ width: '100%', padding: '8px 10px 8px 30px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                      </div>
                      <button onClick={toggleAllVisible} disabled={allVisibleIds.length === 0}
                        style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: allVisibleIds.length ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {allVisibleSelected ? 'Unselect all' : 'Select all'} ({filtered.length})
                      </button>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', minHeight: 200 }}>
                      {unattachedLoading ? (
                        <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>
                      ) : filtered.length === 0 ? (
                        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
                          <FiBox size={28} style={{ opacity: 0.3, marginBottom: 6 }} />
                          <div style={{ fontSize: 13 }}>{unattachedAssets.length === 0 ? 'Every asset already has a functional location.' : 'No matches.'}</div>
                        </div>
                      ) : filtered.map(a => {
                        const selected = unattachedSelected.has(a.id);
                        return (
                          <div key={a.id}
                            onClick={() => setUnattachedSelected(prev => { const next = new Set(prev); selected ? next.delete(a.id) : next.add(a.id); return next; })}
                            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 22px', borderBottom: '1px solid var(--border)', cursor: 'pointer', background: selected ? 'rgba(37,99,235,0.08)' : 'transparent', transition: 'background 0.12s' }}>
                            <input type="checkbox" checked={selected} readOnly style={{ accentColor: '#2563eb', cursor: 'pointer' }} />
                            <span style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: 'rgba(37,99,235,0.08)', color: '#2563eb' }}>{a.tag}</span>
                            <span style={{ fontSize: 13, color: 'var(--text-primary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ padding: '14px 22px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600, whiteSpace: 'nowrap' }}>Move {unattachedSelected.size} to:</span>
                      <select value={unattachedTargetFl} onChange={e => setUnattachedTargetFl(e.target.value)}
                        style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13 }}>
                        <option value="">Select a functional location…</option>
                        {flNodes.slice().sort((a, b) => a.code.localeCompare(b.code)).map(n => {
                          const isContainer = (childrenByParent.get(n.id) || []).length > 0;
                          return <option key={n.id} value={n.id} disabled={isContainer}>{n.code} — {n.name}{isContainer ? '  (container)' : ''}</option>;
                        })}
                      </select>
                      <button onClick={handleAssign} disabled={!canAssign}
                        style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: canAssign ? '#2563eb' : '#94a3b8', color: '#fff', cursor: canAssign ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 700 }}>
                        {unattachedSaving ? 'Assigning…' : 'Assign'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        );
      })()}

      {/* Reports Tab */}
      {tab === 'reports' && <Reports embedded />}

      {/* Settings Tab - Site Branding */}
      {tab === 'settings' && (
        <div style={{ background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)', marginBottom: 16, overflow: 'hidden' }}>
          <div onClick={() => toggleSection('branding')} style={{ padding: '16px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', userSelect: 'none' }}>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-primary)' }}>
              <FiImage size={18} /> Site Branding
            </h2>
            {settingsOpen.branding ? <FiChevronDown size={18} color="var(--text-secondary)" /> : <FiChevronRight size={18} color="var(--text-secondary)" />}
          </div>
          {settingsOpen.branding && <div style={{ padding: '0 20px 20px' }}>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
            Customize the site name, logo, and favicon used across the entire application.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, marginBottom: 16 }}>
            <div>
              <label style={settingsLabel}>Site Name</label>
              <input value={brandingForm.site_name} onChange={e => setBrandingForm(s => ({ ...s, site_name: e.target.value }))}
                placeholder="Asset Logic" style={settingsInput} />
            </div>
            <div>
              <label style={settingsLabel}>Default Theme</label>
              <select value={brandingForm.default_theme} onChange={e => setBrandingForm(s => ({ ...s, default_theme: e.target.value }))}
                style={settingsInput}>
                <option value="system">System (detect from device)</option>
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            </div>
            <div>
              <label style={settingsLabel}>Subscription Plan</label>
              <input value={brandingForm.subscription_plan_name}
                onChange={e => setBrandingForm(s => ({ ...s, subscription_plan_name: e.target.value }))}
                placeholder="e.g. home_edition" style={settingsInput} />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                {brandingForm.subscription_plan_name
                  ? <>Displays as <strong style={{ color: 'var(--text-primary)' }}>{formatPlanName(brandingForm.subscription_plan_name)}</strong></>
                  : 'Slug stored as-is (lowercase, underscores). Display name is auto-formatted.'}
              </div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={settingsLabel}>Two-Step Login (email OTP)</label>
              <div style={{ padding: '12px 14px', borderRadius: 10, background: 'var(--bg-input)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 3 }}>
                    {brandingForm.otp_feature_enabled ? 'Enabled for this workspace' : 'Disabled for this workspace'}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                    When enabled, users can opt into a 4-digit email code on every sign in. Individual users still choose whether to turn it on for themselves (off by default for new users). Wrong codes lock the account for 60 minutes after 3 attempts.
                  </div>
                </div>
                <button type="button" onClick={() => setBrandingForm(s => ({ ...s, otp_feature_enabled: !s.otp_feature_enabled }))}
                  style={{
                    width: 52, height: 28, borderRadius: 14, border: 'none', cursor: 'pointer',
                    background: brandingForm.otp_feature_enabled ? '#2563eb' : 'var(--border)',
                    position: 'relative', transition: 'background 0.2s', flexShrink: 0
                  }}>
                  <div style={{
                    width: 22, height: 22, borderRadius: '50%', background: '#fff',
                    position: 'absolute', top: 3,
                    left: brandingForm.otp_feature_enabled ? 27 : 3,
                    transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                  }} />
                </button>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16, marginBottom: 16 }}>
            {/* Logo Upload */}
            <div style={{ padding: 16, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-input)' }}>
              <label style={{ ...settingsLabel, marginBottom: 8, display: 'block' }}>Logo</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                {(siteCtx?.logo || brandingLogo) ? (
                  <img src={brandingLogo ? URL.createObjectURL(brandingLogo) : siteCtx.logoUrl} alt="Logo"
                    style={{ width: 48, height: 48, borderRadius: 10, objectFit: 'contain', background: 'var(--bg-card)', border: '1px solid var(--border)' }} />
                ) : (
                  <div style={{ width: 48, height: 48, borderRadius: 10, background: 'var(--bg-card)', border: '1px dashed var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FiImage size={20} color="var(--text-muted)" />
                  </div>
                )}
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    <FiUpload size={12} /> Choose File
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { if (e.target.files?.[0]) setBrandingLogo(e.target.files[0]); }} />
                  </label>
                  {(siteCtx?.logo || brandingLogo) && (
                    <button onClick={async () => {
                      if (brandingLogo) { setBrandingLogo(null); return; }
                      await authFetch('/api/admin/site-settings/logo', { method: 'DELETE' });
                      siteCtx.reload();
                      setBrandingMsg('Logo removed');
                    }} style={{ marginLeft: 6, padding: '6px 10px', borderRadius: 8, border: '1px solid rgba(220,38,38,0.2)', background: 'rgba(220,38,38,0.04)', color: '#dc2626', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Remove</button>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Recommended: Square image, 128x128px or larger (PNG/SVG)</div>
            </div>

            {/* Favicon Upload */}
            <div style={{ padding: 16, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-input)' }}>
              <label style={{ ...settingsLabel, marginBottom: 8, display: 'block' }}>Favicon</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                {(siteCtx?.favicon || brandingFavicon) ? (
                  <img src={brandingFavicon ? URL.createObjectURL(brandingFavicon) : `/api/admin/branding/${siteCtx.favicon}`} alt="Favicon"
                    style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'contain', background: 'var(--bg-card)', border: '1px solid var(--border)' }} />
                ) : (
                  <div style={{ width: 32, height: 32, borderRadius: 6, background: 'var(--bg-card)', border: '1px dashed var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FiImage size={14} color="var(--text-muted)" />
                  </div>
                )}
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    <FiUpload size={12} /> Choose File
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { if (e.target.files?.[0]) setBrandingFavicon(e.target.files[0]); }} />
                  </label>
                  {(siteCtx?.favicon || brandingFavicon) && (
                    <button onClick={async () => {
                      if (brandingFavicon) { setBrandingFavicon(null); return; }
                      await authFetch('/api/admin/site-settings/favicon', { method: 'DELETE' });
                      siteCtx.reload();
                      setBrandingMsg('Favicon removed');
                    }} style={{ marginLeft: 6, padding: '6px 10px', borderRadius: 8, border: '1px solid rgba(220,38,38,0.2)', background: 'rgba(220,38,38,0.04)', color: '#dc2626', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Remove</button>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Recommended: 32x32px or 64x64px (PNG/ICO/SVG)</div>
            </div>
          </div>

          {brandingMsg && (
            <div style={{
              padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13,
              background: brandingMsg.includes('Error') ? 'rgba(220,38,38,0.06)' : 'rgba(16,185,129,0.06)',
              border: `1px solid ${brandingMsg.includes('Error') ? 'rgba(220,38,38,0.2)' : 'rgba(16,185,129,0.2)'}`,
              color: brandingMsg.includes('Error') ? '#dc2626' : '#059669'
            }}>{brandingMsg}</div>
          )}

          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
            onClick={async () => {
              setBrandingSaving(true); setBrandingMsg('');
              try {
                const fd = new FormData();
                fd.append('site_name', brandingForm.site_name);
                fd.append('default_theme', brandingForm.default_theme);
                fd.append('subscription_plan_name', brandingForm.subscription_plan_name);
                fd.append('otp_feature_enabled', brandingForm.otp_feature_enabled ? '1' : '0');
                if (brandingLogo) fd.append('logo', brandingLogo);
                if (brandingFavicon) fd.append('favicon', brandingFavicon);
                const token = localStorage.getItem('token');
                const res = await fetch('/api/admin/site-settings', {
                  method: 'PUT',
                  headers: { 'Authorization': `Bearer ${token}` },
                  body: fd
                });
                if (res.ok) {
                  setBrandingMsg('Site branding saved successfully');
                  setBrandingLogo(null);
                  setBrandingFavicon(null);
                  setHeaderPlanName(brandingForm.subscription_plan_name || '');
                  siteCtx.reload();
                } else {
                  const d = await res.json();
                  setBrandingMsg(d.error || 'Error saving branding');
                }
              } catch (e) { setBrandingMsg('Error saving branding'); }
              setBrandingSaving(false);
            }}
            disabled={brandingSaving}
            style={{
              padding: '10px 24px', borderRadius: 10, border: 'none', background: '#2563eb',
              color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600,
              opacity: brandingSaving ? 0.7 : 1
            }}
          >{brandingSaving ? 'Saving...' : 'Save Branding'}</motion.button>
          </div>}
        </div>
      )}

      {/* Settings Tab - Email
          SMTP is now managed exclusively through environment variables on
          the server (SMTP_HOST, SMTP_PORT, SMTP_SECURE, SMTP_USER, SMTP_PASS,
          SMTP_FROM). The previous per-tenant form was a footgun — it wrote
          to a shared settings.json that overrode .env across all tenants.
          We keep only a read-only notice and a test-email control. */}
      {tab === 'settings' && (
        <div style={{ background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)', marginTop: 16, overflow: 'hidden' }}>
          <div onClick={() => toggleSection('email')} style={{ padding: '16px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', userSelect: 'none' }}>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-primary)' }}>
              <FiMail size={18} /> Email (SMTP)
            </h2>
            {settingsOpen.email ? <FiChevronDown size={18} color="var(--text-secondary)" /> : <FiChevronRight size={18} color="var(--text-secondary)" />}
          </div>
          {settingsOpen.email && <div style={{ padding: '0 20px 20px' }}>
            <div style={{ padding: '12px 14px', borderRadius: 10, background: 'rgba(37,99,235,0.06)', border: '1px solid rgba(37,99,235,0.2)', color: 'var(--text-primary)', fontSize: 13, lineHeight: 1.5, marginBottom: 16 }}>
              SMTP is managed via server environment variables (<code>SMTP_HOST</code>, <code>SMTP_PORT</code>, <code>SMTP_SECURE</code>, <code>SMTP_USER</code>, <code>SMTP_PASS</code>, <code>SMTP_FROM</code>) and shared across all tenants. To change them, edit <code>.env</code> on the server and restart the app.
            </div>
            {emailMsg && (
              <div style={{
                padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13,
                background: emailMsg.includes('Error') || emailMsg.includes('fail') ? 'rgba(220,38,38,0.06)' : 'rgba(16,185,129,0.06)',
                border: `1px solid ${emailMsg.includes('Error') || emailMsg.includes('fail') ? 'rgba(220,38,38,0.2)' : 'rgba(16,185,129,0.2)'}`,
                color: emailMsg.includes('Error') || emailMsg.includes('fail') ? '#dc2626' : '#059669'
              }}>{emailMsg}</div>
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input type="email" placeholder="Recipient email for test…" value={testEmailTo} onChange={e => setTestEmailTo(e.target.value)}
                style={{
                  padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border)',
                  fontSize: 13, outline: 'none', width: 260, background: 'var(--bg-input)',
                  color: 'var(--text-primary)'
                }}
              />
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                onClick={async () => {
                  if (!testEmailTo) { setEmailMsg('Enter an email address to send test to'); return; }
                  setEmailMsg('Sending test email…');
                  try {
                    const res = await authFetch('/api/admin/settings/email/test', { method: 'POST', body: JSON.stringify({ to: testEmailTo }) });
                    const d = await res.json();
                    setEmailMsg(res.ok ? `Test email sent to ${testEmailTo}!` : (d.error || 'Failed to send test email'));
                  } catch (e) { setEmailMsg('Error: could not send test email'); }
                }}
                style={{
                  padding: '10px 20px', borderRadius: 10, border: 'none',
                  background: '#2563eb', color: '#fff', cursor: 'pointer',
                  fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap'
                }}
              >Send Test Email</motion.button>
            </div>
          </div>}
        </div>
      )}
      {tab === 'settings' && (
        <div style={{ background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)', marginTop: 16, overflow: 'hidden' }}>
          <div onClick={() => toggleSection('schedule')} style={{ padding: '16px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', userSelect: 'none' }}>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-primary)' }}>
              <FiMail size={18} /> Email Schedule
            </h2>
            {settingsOpen.schedule ? <FiChevronDown size={18} color="var(--text-secondary)" /> : <FiChevronRight size={18} color="var(--text-secondary)" />}
          </div>
          {settingsOpen.schedule && <div style={{ padding: '0 20px 20px' }}>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
            Configure when automated emails are sent. All times are in server local time.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, marginBottom: 16 }}>
            <div>
              <label style={settingsLabel}>Daily Summary Hour</label>
              <select value={scheduleSettings.daily_hour} onChange={e => setScheduleSettings(s => ({ ...s, daily_hour: parseInt(e.target.value) }))}
                style={settingsInput}>
                {[...Array(24)].map((_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
              </select>
            </div>
            <div>
              <label style={settingsLabel}>Weekly Summary Day</label>
              <select value={scheduleSettings.weekly_day} onChange={e => setScheduleSettings(s => ({ ...s, weekly_day: parseInt(e.target.value) }))}
                style={settingsInput}>
                {['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'].map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
            </div>
            <div>
              <label style={settingsLabel}>Weekly Summary Hour</label>
              <select value={scheduleSettings.weekly_hour} onChange={e => setScheduleSettings(s => ({ ...s, weekly_hour: parseInt(e.target.value) }))}
                style={settingsInput}>
                {[...Array(24)].map((_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
              </select>
            </div>
            <div>
              <label style={settingsLabel}>Adherence Report Day</label>
              <select value={scheduleSettings.adherence_day} onChange={e => setScheduleSettings(s => ({ ...s, adherence_day: parseInt(e.target.value) }))}
                style={settingsInput}>
                {['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'].map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
            </div>
            <div>
              <label style={settingsLabel}>Adherence Report Hour</label>
              <select value={scheduleSettings.adherence_hour} onChange={e => setScheduleSettings(s => ({ ...s, adherence_hour: parseInt(e.target.value) }))}
                style={settingsInput}>
                {[...Array(24)].map((_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
              </select>
            </div>
          </div>
          {scheduleMsg && (
            <div style={{
              padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13,
              background: scheduleMsg.includes('Error') || scheduleMsg.includes('fail') ? 'rgba(220,38,38,0.06)' : 'rgba(16,185,129,0.06)',
              border: `1px solid ${scheduleMsg.includes('Error') || scheduleMsg.includes('fail') ? 'rgba(220,38,38,0.2)' : 'rgba(16,185,129,0.2)'}`,
              color: scheduleMsg.includes('Error') || scheduleMsg.includes('fail') ? '#dc2626' : '#059669'
            }}>{scheduleMsg}</div>
          )}
          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
            onClick={() => {
              setConfirmDialog({ title: 'Save Email Schedule', rows: [{ label: 'Action', value: 'Save email schedule settings' }], confirmLabel: 'Save', onConfirm: async () => {
                setConfirmDialog(null); setScheduleSaving(true); setScheduleMsg('');
                try {
                  const res = await authFetch('/api/admin/settings/email-schedule', { method: 'PUT', body: JSON.stringify(scheduleSettings) });
                  if (res.ok) setScheduleMsg('Email schedule saved successfully');
                  else { const d = await res.json(); setScheduleMsg(d.error || 'Error saving settings'); }
                } catch (e) { setScheduleMsg('Error saving settings'); }
                setScheduleSaving(false);
              } });
            }}
            disabled={scheduleSaving}
            style={{
              padding: '10px 24px', borderRadius: 10, border: 'none', background: '#2563eb',
              color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600,
              opacity: scheduleSaving ? 0.7 : 1
            }}
          >{scheduleSaving ? 'Saving...' : 'Save Schedule'}</motion.button>
          </div>}
        </div>
      )}

      {tab === 'settings' && (
        <div style={{ background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)', marginTop: 16, overflow: 'hidden' }}>
          <div onClick={() => toggleSection('adherence')} style={{ padding: '16px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', userSelect: 'none' }}>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-primary)' }}>
              <FiSettings size={18} /> Adherence Target
            </h2>
            {settingsOpen.adherence ? <FiChevronDown size={18} color="var(--text-secondary)" /> : <FiChevronRight size={18} color="var(--text-secondary)" />}
          </div>
          {settingsOpen.adherence && <div style={{ padding: '0 20px 20px' }}>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
            Set the target adherence percentage shown on metrics graphs
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <input type="number" min="0" max="100" value={adherenceTarget}
              onChange={e => setAdherenceTarget(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
              style={{ padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', width: 100, textAlign: 'center', fontSize: 18, fontWeight: 700, outline: 'none' }}
            />
            <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-secondary)' }}>%</span>
            <motion.button type="button" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
              onClick={async () => {
                setSavingTarget(true);
                try {
                  await authFetch('/api/admin/site-settings', { method: 'PUT', body: JSON.stringify({ adherence_target: adherenceTarget }) });
                  showToast('Adherence target updated');
                } catch (e) { showToast('Failed to save target', 'error'); }
                setSavingTarget(false);
              }} disabled={savingTarget}
              style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: savingTarget ? 0.7 : 1 }}>
              {savingTarget ? 'Saving...' : 'Save Target'}
            </motion.button>
          </div>
          </div>}
        </div>
      )}
      {tab === 'settings' && (
        <div style={{ background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)', marginTop: 16, overflow: 'hidden' }}>
          <div onClick={() => toggleSection('maintenance')} style={{ padding: '16px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', userSelect: 'none' }}>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-primary)' }}>
              <FiAlertTriangle size={18} color={maintenanceStatus.enabled ? '#2563eb' : undefined} /> Maintenance Mode
            </h2>
            {settingsOpen.maintenance ? <FiChevronDown size={18} color="var(--text-secondary)" /> : <FiChevronRight size={18} color="var(--text-secondary)" />}
          </div>
          {settingsOpen.maintenance && <div style={{ padding: '0 20px 20px' }}>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
            When enabled, all non-admin users will be locked out and shown a maintenance message. Admins can continue using the site.
          </p>

          {maintenanceStatus.enabled && (
            <div style={{
              padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13, fontWeight: 600,
              background: 'rgba(37,99,235,0.08)', border: '1px solid rgba(37,99,235,0.25)', color: '#2563eb',
              display: 'flex', alignItems: 'center', gap: 8
            }}>
              <FiAlertTriangle size={14} /> Maintenance mode is currently active
            </div>
          )}

          {!maintenanceStatus.enabled && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
              <div>
                <label style={settingsLabel}>Maintenance Message (shown to users)</label>
                <input value={maintenanceStatus.message} onChange={e => setMaintenanceStatus(s => ({ ...s, message: e.target.value }))}
                  placeholder="We're performing scheduled maintenance. Please check back soon."
                  style={settingsInput} />
              </div>
              <div>
                <label style={settingsLabel}>Estimated Duration (hours)</label>
                <select value={maintenanceStatus.estimated_hours} onChange={e => setMaintenanceStatus(s => ({ ...s, estimated_hours: e.target.value }))}
                  style={{ ...settingsInput, maxWidth: 200 }}>
                  <option value="">No estimate</option>
                  {[0.5, 1, 2, 3, 4, 6, 8, 12, 24].map(h => (
                    <option key={h} value={h}>{h < 1 ? '30 minutes' : h === 1 ? '1 hour' : `${h} hours`}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
            onClick={() => {
              const enabling = !maintenanceStatus.enabled;
              const rows = enabling
                ? [
                    { label: 'Action', value: 'Enable Maintenance Mode' },
                    { label: 'Message', value: maintenanceStatus.message || 'We\'re performing scheduled maintenance. Please check back soon.' },
                    ...(maintenanceStatus.estimated_hours ? [{ label: 'Estimated Duration', value: `${maintenanceStatus.estimated_hours} hour${maintenanceStatus.estimated_hours == 1 ? '' : 's'}` }] : []),
                    { label: 'Impact', value: 'All non-admin users will be locked out immediately' },
                  ]
                : [
                    { label: 'Action', value: 'Disable Maintenance Mode' },
                    { label: 'Impact', value: 'All users will regain access immediately' },
                  ];
              setConfirmDialog({
                title: enabling ? 'Enable Maintenance Mode?' : 'Disable Maintenance Mode?',
                subtitle: enabling ? 'This will lock out all non-admin users.' : 'This will restore access for all users.',
                rows,
                confirmLabel: enabling ? 'Enable Maintenance' : 'Disable Maintenance',
                confirmColor: 'var(--accent)',
                onConfirm: async () => {
                  setMaintenanceLoading(true);
                  try {
                    const res = await authFetch('/api/admin/maintenance', {
                      method: 'POST',
                      body: JSON.stringify({
                        enabled: enabling,
                        message: maintenanceStatus.message || 'We\'re performing scheduled maintenance. Please check back soon.',
                        estimated_hours: maintenanceStatus.estimated_hours ? parseFloat(maintenanceStatus.estimated_hours) : null,
                      }),
                    });
                    if (res.ok) {
                      setMaintenanceStatus(s => ({ ...s, enabled: enabling }));
                      showToast(enabling ? 'Maintenance mode enabled' : 'Maintenance mode disabled');
                    } else {
                      const d = await res.json();
                      showToast(d.error || 'Failed to update maintenance mode', 'error');
                    }
                  } catch (e) {
                    showToast('Error updating maintenance mode', 'error');
                  }
                  setMaintenanceLoading(false);
                  setConfirmDialog(null);
                },
              });
            }}
            disabled={maintenanceLoading}
            style={{
              padding: '10px 24px', borderRadius: 10, border: 'none',
              background: 'var(--accent)',
              color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600,
              opacity: maintenanceLoading ? 0.7 : 1,
              display: 'flex', alignItems: 'center', gap: 8
            }}
          >
            {maintenanceStatus.enabled ? <><FiToggleRight size={16} /> Disable Maintenance Mode</> : <><FiToggleLeft size={16} /> Enable Maintenance Mode</>}
          </motion.button>
          </div>}
        </div>
      )}
      {/* Payroll Settings */}
      {tab === 'settings' && (
        <div style={{ background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)', marginTop: 16, overflow: 'hidden' }}>
          <div onClick={() => {
            toggleSection('payroll');
            if (!paySettingsLoaded) {
              authFetch('/api/payslips/settings').then(r => r.ok ? r.json() : {}).then(d => { setPaySettings(ps => ({ ...ps, ...d })); setPaySettingsLoaded(true); }).catch(() => {});
            }
          }} style={{ padding: '16px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', userSelect: 'none' }}>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-primary)' }}>
              <FiDollarSign size={18} /> Payroll Settings
            </h2>
            {settingsOpen.payroll ? <FiChevronDown size={18} color="var(--text-secondary)" /> : <FiChevronRight size={18} color="var(--text-secondary)" />}
          </div>
          {settingsOpen.payroll && <div style={{ padding: '0 20px 20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 16 }}>
              <div>
                <label style={settingsLabel}>Working Week Ends</label>
                <select value={paySettings.week_end_day} onChange={e => setPaySettings(s => ({ ...s, week_end_day: e.target.value }))} style={settingsInput}>
                  {['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label style={settingsLabel}>Pay Day</label>
                <select value={paySettings.pay_day} onChange={e => setPaySettings(s => ({ ...s, pay_day: e.target.value }))} style={settingsInput}>
                  {['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label style={settingsLabel}>Pay Cycle</label>
                <select value={paySettings.pay_cycle} onChange={e => setPaySettings(s => ({ ...s, pay_cycle: e.target.value }))} style={settingsInput}>
                  <option value="weekly">Weekly</option>
                  <option value="fortnightly">Fortnightly</option>
                </select>
              </div>
            </div>
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              disabled={savingPaySettings}
              onClick={async () => {
                setSavingPaySettings(true);
                try {
                  await authFetch('/api/payslips/settings', { method: 'PUT', body: JSON.stringify(paySettings) });
                  showToast('Payroll settings saved');
                } catch { showToast('Failed to save', 'error'); }
                setSavingPaySettings(false);
              }}
              style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: savingPaySettings ? 0.7 : 1 }}>
              {savingPaySettings ? 'Saving...' : 'Save Payroll Settings'}
            </motion.button>
          </div>}
        </div>
      )}


      {/* Asset Create Wizard — used for new assets only; edit still uses
          the single-screen modal below. */}
      <AnimatePresence>
        {showAssetWizard && (
          <AssetCreateWizard
            flNodes={flNodes}
            costCodes={costCodes}
            existingNames={adminAssets.map(a => (a.name || '').toLowerCase()).filter(Boolean)}
            onCancel={() => setShowAssetWizard(false)}
            onCreate={async (formData) => {
              try {
                const res = await authFetch('/api/admin/assets', { method: 'POST', body: JSON.stringify(formData) });
                const data = await res.json().catch(() => ({}));
                if (!res.ok) return { ok: false, error: data.error || 'Failed to create asset' };
                // Refresh the admin list in the background; the wizard
                // stays mounted so it can render the confirmation step.
                loadAssetTab();
                return { ok: true, asset: data };
              } catch (e) {
                return { ok: false, error: 'Network error — please try again' };
              }
            }}
          />
        )}
      </AnimatePresence>

      {/* Asset Modal (edit only) */}
      <AnimatePresence>
        {showAssetModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 200 }}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              style={{ background: 'var(--bg-card)', borderRadius: 16, width: '100%', maxWidth: 460, maxHeight: '90vh', display: 'flex', flexDirection: 'column', border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{editAsset ? 'Edit Asset' : 'Create Asset'}</h3>
                <button onClick={() => setShowAssetModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><FiX size={18} /></button>
              </div>
              <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto' }}>
                <div>
                  <label style={settingsLabel}>Asset ID</label>
                  <input
                    value={assetForm.tag || ''}
                    readOnly
                    placeholder={editAsset ? '' : 'Auto-assigned on save (e.g. 000000001)'}
                    style={{ ...settingsInput, fontSize: 15, fontWeight: 700, color: 'var(--text-secondary)', background: 'var(--bg-input)', cursor: 'not-allowed' }}
                  />
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                    {editAsset ? 'Asset IDs are permanent and cannot be changed.' : 'A 9-digit Asset ID is assigned automatically when you save.'}
                  </div>
                </div>
                <div>
                  <label style={settingsLabel}>Name *</label>
                  <input value={assetForm.name} onChange={e => setAssetForm(f => ({ ...f, name: titleCase(e.target.value) }))} placeholder="Asset name" style={settingsInput} />
                  {assetForm.name && !editAsset && adminAssets.some(a => a.name && a.name.toLowerCase() === assetForm.name.trim().toLowerCase()) && (
                    <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2, fontWeight: 600 }}>Asset name already exists</div>
                  )}
                </div>
                <div>
                  <label style={settingsLabel}>Functional Location <span style={{ color: '#dc2626' }}>*</span></label>
                  <select value={assetForm.functional_location_id} onChange={e => setAssetForm(f => ({ ...f, functional_location_id: e.target.value }))} required style={settingsInput}>
                    <option value="">Select a Functional Location…</option>
                    {(() => {
                      // Render FLs with breadcrumb path (parent → child) so the picker
                      // is unambiguous. Walks parent_id up to the root, max 8 hops.
                      const pathFor = node => {
                        const parts = [];
                        let cur = node;
                        for (let i = 0; i < 8 && cur; i++) {
                          parts.unshift(`${cur.code} ${cur.name}`);
                          cur = cur.parent_id ? flNodes.find(n => n.id === cur.parent_id) : null;
                        }
                        return parts.join(' / ');
                      };
                      const sorted = [...flNodes].sort((a, b) => pathFor(a).localeCompare(pathFor(b)));
                      return sorted.map(fl => <option key={fl.id} value={fl.id}>{pathFor(fl)}</option>);
                    })()}
                  </select>
                </div>
                <div>
                  <label style={settingsLabel}>Status</label>
                  <select value={assetForm.status_id || '3'} onChange={e => setAssetForm(f => ({ ...f, status_id: e.target.value }))} style={settingsInput}>
                    <option value="3">Active</option>
                    <option value="4">Redundant</option>
                  </select>
                </div>
                <div>
                  <label style={settingsLabel}>Supplier</label>
                  <input value={assetForm.supplier_name || ''} onChange={e => setAssetForm(f => ({ ...f, supplier_name: titleCase(e.target.value) }))} placeholder="Optional — e.g. Acme Industrial" style={settingsInput} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={settingsLabel}>Serial Number</label>
                    <input value={assetForm.serial_number} onChange={e => setAssetForm(f => ({ ...f, serial_number: e.target.value }))} placeholder="Optional" style={settingsInput} />
                  </div>
                  <div>
                    <label style={settingsLabel}>Capacity</label>
                    <input value={assetForm.capacity} onChange={e => setAssetForm(f => ({ ...f, capacity: e.target.value }))} placeholder="e.g. 500GB, 16GB RAM" style={settingsInput} />
                  </div>
                </div>
                <div>
                  <label style={settingsLabel}>Cost Centre <span style={{ color: '#dc2626' }}>*</span></label>
                  <select value={assetForm.cost_code_id} onChange={e => setAssetForm(f => ({ ...f, cost_code_id: e.target.value }))} required style={settingsInput}>
                    <option value="">Select cost centre...</option>
                    {costCodes.map(cc => <option key={cc.id} value={cc.id}>{cc.code} — {cc.name || cc.description}</option>)}
                  </select>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={settingsLabel}>Purchase Date</label>
                    <input type="date" value={assetForm.purchase_date} onChange={e => setAssetForm(f => ({ ...f, purchase_date: e.target.value }))} style={settingsInput} />
                  </div>
                  <div>
                    <label style={settingsLabel}>Warranty End</label>
                    <input type="date" value={assetForm.warranty_end} onChange={e => setAssetForm(f => ({ ...f, warranty_end: e.target.value }))} style={settingsInput} />
                  </div>
                </div>
                <div>
                  <label style={settingsLabel}>Notes</label>
                  <textarea value={assetForm.notes} onChange={e => {
                    const val = e.target.value;
                    const sc = val.charAt(0).toUpperCase() + val.slice(1);
                    setAssetForm(f => ({ ...f, notes: sc.replace(/([.!?]\s+)([a-z])/g, (m, p, c) => p + c.toUpperCase()) }));
                  }} rows={3} placeholder="Optional"
                    style={{ ...settingsInput, resize: 'vertical' }} />
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                  <button onClick={() => setShowAssetModal(false)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                  <button onClick={saveAsset} disabled={!assetForm.name || !assetForm.cost_code_id || !assetForm.functional_location_id || (!editAsset && adminAssets.some(a => a.name && a.name.toLowerCase() === assetForm.name.trim().toLowerCase()))}
                    style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: !assetForm.name || !assetForm.cost_code_id || !assetForm.functional_location_id ? '#94a3b8' : '#2563eb', color: '#fff', cursor: !assetForm.name || !assetForm.cost_code_id || !assetForm.functional_location_id ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>
                    {editAsset ? 'Update' : 'Create'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Custom Field Modal */}
      <AnimatePresence>
        {showCfModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 200 }}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              style={{ background: 'var(--bg-card)', borderRadius: 16, width: '100%', maxWidth: 400, border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 20, borderBottom: '1px solid var(--border)' }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{editCf ? 'Edit Custom Field' : 'Add Custom Field'}</h3>
                <button onClick={() => setShowCfModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><FiX size={18} /></button>
              </div>
              <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={settingsLabel}>Field Name *</label>
                  <input value={cfForm.name} onChange={e => setCfForm(f => ({ ...f, name: titleCase(e.target.value) }))} placeholder="e.g. RAM, CPU, Storage" style={settingsInput} />
                </div>
                <div>
                  <label style={settingsLabel}>Field Type</label>
                  <select value={cfForm.field_type} onChange={e => setCfForm(f => ({ ...f, field_type: e.target.value }))} style={settingsInput}>
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="date">Date</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                  <button onClick={() => setShowCfModal(false)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                  <button onClick={saveCustomField} disabled={!cfForm.name}
                    style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: !cfForm.name ? '#94a3b8' : '#2563eb', color: '#fff', cursor: !cfForm.name ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>
                    {editCf ? 'Update' : 'Create'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>


    </div>
  );
}

const settingsLabel = {
  display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)',
  marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.3px'
};

const settingsInput = {
  width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)',
  fontSize: 13, outline: 'none', boxSizing: 'border-box', background: 'var(--bg-card)',
  fontFamily: 'inherit', color: 'var(--text-primary)'
};
